import type { CardLoadout, CardSlot } from '@kenocyber/shared';
import {
  assertSafeInteger,
  cardWorksIn,
  encodeHealthMilli,
  type JewelSocketProfile,
  PVP_HEALTH_MILLI_PER_UNIT,
  PVP_TEAMS,
  type PvpEndReason,
  type PvpParticipantId,
  type PvpResult,
  type PvpRuleset,
  type PvpSeat,
  type PvpTeam,
  type PvpTeamSlot,
  parseJewelSocketProfile,
  pvpParseTeam,
  type SimulationSeed,
  simulationSeed,
} from '@kenocyber/shared';
import type { CardAbilityState } from '../cards/abilities';
import {
  activateCardAbility,
  ageCardAbility,
  CATOLE_BLAST_EFFECT,
  cardAbilityCanActivate,
  cardAbilityCanRelease,
  cardAbilityChargeEnded,
  cardAbilityIsCharging,
  cardAbilityIsFiring,
  cardBeginCooldown,
  FIREBALL_BLAST_EFFECT,
  initialCardAbilities,
  releaseCardCharge,
} from '../cards/abilities';
import {
  CARD_CHARGE_FULL_BPS,
  type CardBalance,
  cardBalance,
  cardBeamWidthFor,
  cardIsOffensive,
  cardScaleByCharge,
  cardSphereHitRadius,
  cardSphereRadiusFor,
} from '../cards/balance';
import {
  ageCardEffects,
  applyCardTimer,
  CARD_EFFECT_SLOTS,
  type CardEffectState,
  type CardTimer,
  cardAttackSpeedBps,
  cardBreakCloak,
  cardDamageOutMilli,
  cardHealingInMilli,
  cardIsCloaked,
  cardIsFrozen,
  cardIsHittable,
  cardIsSelectable,
  cardMoveSpeedBps,
  clearCardEffects,
  initialCardEffects,
} from '../cards/effects';
import {
  type AtomicBombSelection,
  atomicBombFromWire,
  atomicBombToWire,
  PVP_VOLLEY_BOMB_RNG_LABEL,
  selectVolleyBomb,
} from '../damage/atomic-bomb';
import { DAMAGE_MILLI_PER_UNIT } from '../damage/base';
import {
  bleedingDetonationMilli,
  PVP_BLEEDING_RNG_LABEL,
  selectBleedingApplication,
} from '../damage/bleeding';
import { hostileDamageMultiplierBps, reduceHostileDamageMilli } from '../damage/damage-reduction';
import { resolveJewelEffects } from '../damage/jewel-effects';
import { lifestealEarnedMilli } from '../damage/lifesteal';
import {
  chooseMissileTargetIndex,
  type MissileSelection,
  PVP_MISSILE_TARGET_RNG_LABEL,
} from '../damage/missile';
import {
  ATOMIC_BLAST_EFFECT,
  BLEED_DETONATION_EFFECT,
  damageOriginMayTriggerJewel,
  jewelTriggersFor,
  LIFESTEAL_HEAL_EFFECT,
  MISSILE_EFFECT,
  type PvpDamageOrigin,
  REFLECTION_EFFECT,
  VITAL_HEAL_EFFECT,
} from '../damage/origin';
import {
  PVP_POISON_RNG_LABEL,
  poisonIsSpent,
  poisonPendingTicks,
  poisonTickDamageMilli,
  selectPoisonApplication,
} from '../damage/poison';
import {
  type JewelProcSelection,
  jewelProcFromWire,
  jewelProcToWire,
  type OutgoingJewelProcId,
  PROC_CHANCE_DENOMINATOR,
  PVP_OUTGOING_PROC_RNG_LABEL,
  procIntervals,
  selectOutgoingProc,
} from '../damage/proc-lottery';
import {
  HOSTILE_PAYLOAD_KINDS,
  type HostilePayloadKind,
  PVP_REFLECTION_RNG_LABEL,
  type ReflectionLineage,
  reflectionAuthorIsEligible,
  reflectionMayIntercept,
  selectReflection,
} from '../damage/reflection';
import {
  PVP_SAW_RNG_LABEL,
  SAW_INITIAL_PHASE,
  SAW_ORBIT_PHASE_UNITS,
  SAW_VECTOR_SCALE,
  sawAdvancePhase,
  sawBladeCount,
  sawBladeRadius,
  sawBladeStep,
  sawContactDamageMilli,
  sawContactDue,
  sawDirection,
  sawLevelOf,
  sawOrbitRadius,
  sawSpeedNumerator,
} from '../damage/saw';
import {
  applyVitalHealMilli,
  PVP_VITAL_HEART_RNG_LABEL,
  selectVitalHeal,
  selectVitalHealTarget,
  type VitalHealCandidate,
} from '../damage/vital-heart';
import {
  selectVolleyMissile,
  type VolleyProcId,
  volleyProcInterval,
} from '../damage/volley-lottery';
import type { SimulationRngState } from '../simulation-rng';
import {
  createSimulationRng,
  deriveSimulationSeed,
  restoreSimulationRng,
  type SimulationRng,
} from '../simulation-rng.js';
import {
  PVP_ARENA_HEIGHT_MU,
  PVP_ARENA_WIDTH_MU,
  PVP_BOMB_BLAST_RADIUS_MU,
  PVP_BOMB_BODY_RADIUS_MU,
  PVP_BOMB_PROXIMITY_RADIUS_MU,
  PVP_BOMB_SPEED_BPS,
  PVP_CARD_HOMING_BPS,
  PVP_CARD_PROJECTILE_LIFETIME_TICKS,
  PVP_CARD_PROJECTILE_SPEED_BPS,
  PVP_CARD_UNLIMITED_REACH_BPS,
  PVP_CATOLE_BLAST_RADIUS_MU,
  PVP_CATOLE_KNOCKBACK_MU,
  PVP_DRONE_KAMIKAZE_BPS,
  PVP_DRONE_KAMIKAZE_REACH_BPS,
  PVP_FIREBALL_CHARGE_MS,
  PVP_FIREBALL_PROXIMITY_RADIUS_MU,
  PVP_FIREBALL_REACH_BPS,
  PVP_FIREBALL_SPEED_BPS,
  PVP_FREEZE_ATTACK_BLOCK_BPS,
  PVP_FREEZE_DURATION_STEPS,
  PVP_ICE_BOLT_SPEED_BPS,
  PVP_MAX_HEALTH,
  PVP_MAX_PROJECTILES,
  PVP_MISSILE_BODY_RADIUS_MU,
  PVP_MISSILE_HIT_RADIUS_MU,
  PVP_MISSILE_LIFETIME_TICKS,
  PVP_MISSILE_SPEED_MU_PER_TICK,
  PVP_OBSTACLES,
  PVP_POISON_TICK_INTERVAL_TICKS,
  PVP_PROJECTILE_LIFETIME_TICKS,
  PVP_PROJECTILE_RADIUS_MU,
  PVP_PROJECTILE_SPEED_MU_PER_TICK,
  PVP_PROJECTILE_SUBSTEPS,
  PVP_RUNE_DAMAGE_BPS,
  PVP_RUNE_FIRST_SPAWN_TICKS,
  PVP_RUNE_KINDS,
  PVP_RUNE_SPEED_BPS,
  PVP_SLOW_DURATION_STEPS,
  PVP_SOLAR_LANCE_REACH_BPS,
  PVP_SOLAR_LANCE_WIDTH_BPS,
  PVP_SUMMON_MUZZLE_MU,
  PVP_TICK_MS,
  PVP_TIME_LIMIT_TICKS,
  pvpSpawnFor,
} from './balance';
import type { PvpBleedingMarkState } from './bleeding';
import {
  type PvpCardVisualState,
  pvpCardBodyDetonates,
  pvpCardVisualStateOf,
  pvpPublicCardFlags,
} from './card-frames';
import {
  PVP_DEFAULT_COMBAT_PROFILE,
  PVP_MILLI_TICKS_PER_TICK,
  type PvpCombatProfile,
  pvpAssertCombatProfile,
  pvpCopyCombatProfile,
  pvpEffectiveSpeedBps,
  pvpFireIntervalMilliTicks,
  pvpHullRadiusMu,
  pvpShipBaseDamageMilli,
  pvpShotDamageMilli,
} from './combat-profile';
import { pvpKnockbackFrom, pvpKnockbackStep } from './knockback';
import {
  PVP_DUEL_RULESET,
  type PvpEvent,
  PvpLegacyDuelError,
  type PvpProjectileState,
  type PvpShipState,
  type PvpSnapshot,
  pvpDuelSeatToParticipantId,
  pvpDuelSeatToTeam,
  pvpParticipantEventToDuelEvent,
  pvpParticipantIdToDuelSeat,
  pvpRequireDuelRuleset,
  pvpShipToDuelShip,
  pvpTeamResultToDuelResult,
} from './legacy-duel';
import {
  PVP_MAX_MISSILES,
  type PvpMissileState,
  type PvpSweepHit,
  pvpMissileAimPoint,
  pvpMissileSteer,
  pvpSweepBounds,
  pvpSweepBox,
  pvpSweepCircle,
  pvpSweepEarlier,
  pvpSweepPoint,
} from './missile';
import { type PvpHullBlocker, pvpAdvanceShip, pvpTickTravelMu } from './movement';
import type { PvpPoisonStackState } from './poison';
import { pvpRollDurationMs, pvpRollHits } from './rolls';
import {
  PVP_CARD_COMMAND_MEMORY,
  type PvpAbandonment,
  type PvpParticipantEvent,
  type PvpParticipantProjectileState,
  type PvpParticipantSeat,
  type PvpParticipantShipState,
  type PvpSawContactState,
  PvpShipRoster,
  type PvpTeamResult,
  pvpCanonicalSeats,
} from './roster';
import {
  type PvpRuneState,
  pvpAgeEffects,
  pvpBoosterPush,
  pvpClaimRune,
  pvpClearEffects,
  pvpGrowRunes,
  pvpInitialRunes,
  pvpNoEffects,
  pvpRuneKindAt,
  pvpValidateTeleport,
} from './runes.js';
import {
  PVP_SNAPSHOT_V3_RULESETS,
  PVP_SNAPSHOT_V3_VERSION,
  type PvpCombatProfileV3,
  type PvpParticipantV3,
  type PvpSnapshotV3,
  pvpDecodeSnapshotV3,
  pvpTeamResultFromV3,
  pvpTeamResultToV3,
  pvpUpgradeSnapshotV4,
  pvpUpgradeSnapshotV5,
  pvpUpgradeSnapshotV6,
  pvpUpgradeSnapshotV7,
  pvpUpgradeSnapshotV8,
  pvpUpgradeSnapshotV9,
  pvpUpgradeSnapshotV10,
  pvpUpgradeSnapshotV11,
  pvpUpgradeSnapshotV12,
  pvpUpgradeSnapshotV13,
  pvpUpgradeSnapshotV14,
  pvpUpgradeSnapshotV15,
  pvpUpgradeSnapshotV16,
} from './snapshot-v3';
import {
  PVP_DRONE_HEALTH_BPS,
  PVP_LEVIATHAN_ENTRY_BPS,
  PVP_LEVIATHAN_IDLE_MS,
  PVP_MAX_SUMMONS,
  type PvpSummonKind,
  type PvpSummonState,
  pvpAgeSummon,
  pvpLeviathanBodyRadiusMu,
  pvpLeviathanMuzzles,
} from './summons';
import { pvpSuperCannonMuzzles, shipHasSuperCannons } from './super-cannons';
import {
  clamp,
  distanceSquared,
  divTrunc,
  type IntVector,
  isqrt,
  movementDirection,
  normalise,
  scaleDirection,
  VECTOR_SCALE,
} from './vectors';

/**
 * The authoritative match.
 *
 * ## What this module is NOT allowed to be
 *
 * It does not import `HuntSimulation` and never will - not a helper, not a
 * type, not a constant. The two share `@kenocyber/shared` primitives and
 * nothing else. A duel that reached into the hunt would be one refactor away
 * from writing to a hunt session, and the boundary is easier to keep than to
 * restore.
 *
 * There is no pet here, no drop, no XP, no Gold, no map, no boss and no
 * economic randomness. There is also no `Math.random`: this simulation is a
 * pure function of its snapshot and the commands applied to it, which is what
 * makes "the server is right" a testable claim rather than a promise.
 *
 * ## Ships are addressed by identity, never by position
 *
 * This class used to be a PAIR by construction - `ships.a`, `ships.b`,
 * `otherSeat(projectile.seat)`, a two-element snapshot tuple. Every one of
 * those decided something by POSITION, and with three ships a side position is
 * the first thing that stops meaning anything: a roster reorders, a ship dies
 * and stays in the collection, a lobby fills slots out of order.
 *
 * So the ships live in a {@link PvpShipRoster} keyed by `participantId`, the
 * roster iterates in canonical order (team, then slot) whatever order it was
 * built in, and every tie a rule has to break - two hulls equidistant from a
 * pad, two enemies equidistant from a shot - is broken by that order rather
 * than by an array index. Nothing here reads an account: who is driving a ship
 * is a fact about the lobby, and a re-simulation must not need it.
 *
 * ## The 1x1 is unchanged, and that is a claim under test
 *
 * A duel is a two-participant match - `a1` and `b1` - and every number it
 * produces is identical to the two-seat version: same movement, same shots,
 * same damage, same runes, same deaths, same snapshot. `duel-invariance.test.ts`
 * holds the recorded values. The seat-shaped accessors on this class are
 * ADAPTERS at the boundary (see `legacy-duel.ts`), and they refuse rather than
 * lie when the match has no seats.
 *
 * ## Everything that decides the future is in the snapshot
 *
 * Positions, aim, health, cooldowns, projectiles, the id counter, the tick and
 * the result. Nothing visual. A room that is torn down and rebuilt from its
 * snapshot resumes the identical duel - which is what makes a reconnection a
 * reconnection rather than a restart.
 *
 * The persisted snapshot is still the TWO-SEAT one, and a `team_3v3` refuses to
 * produce it (`snapshot_not_supported_for_ruleset`) rather than write two of six
 * ships into a format that has room for two. That refusal is this round's named,
 * temporary limitation; the next one adds a snapshot the six fit in.
 */

/**
 * What a client may say. Ruleset-neutral: the same five fields, whether there
 * are two ships in the room or six.
 */
export interface PvpCommandIntent {
  readonly seq: number;
  /** Where the player clicked. Optional: most frames carry no request at all. */
  readonly teleportTo?: { readonly x: number; readonly y: number } | null;
  /**
   * **A ordem de DETONAR**, para este frame apenas (gate V28G.N).
   *
   * Uma INTENÇÃO e nunca um resultado. Não diz que alvo, não diz quantas marcas
   * e não diz quanto dano - não há campo onde isso caiba, e é por isso que não
   * há nada aqui para forjar. A autoridade valida o casco, a cadeira, a sessão e
   * o estado, e decide sozinha o que rebenta e por quanto.
   *
   * Latch e não hold, como o teleporte: uma ordem vale UM tique. Um cliente que
   * a mantivesse verdadeira em todos os frames detonaria uma vez e depois pediria
   * detonações sem marcas, que são `no-op` seguros.
   */
  readonly detonateBleed?: boolean;
  /**
   * **A ORDEM de ligar uma carta**, para este frame apenas (gate V28H.J).
   *
   * Uma INTENÇÃO e nunca um resultado, como a detonação. Não diz que carta, não
   * diz duração e não diz arrefecimento: diz o ESPAÇO, e a identidade da ordem
   * para uma repetição ser lida em vez de ligar outra vez.
   *
   * Não há campo para nomear outro participante, e é a defesa inteira: quem dá
   * a ordem é a cadeira em cuja ligação ela chegou.
   */
  readonly activateCard?: { readonly slot: CardSlot; readonly commandId: string } | null;
  /** A ordem de SOLTAR uma carga (gate V28H.N). Mesma forma, outro verbo. */
  readonly releaseCard?: { readonly slot: CardSlot; readonly commandId: string } | null;
  /**
   * **A ordem de PILOTAR o que este casco está a tocar** (gate V28H.X).
   *
   * O embarque no Leviatã acontecia sozinho, ao encostar, e isso fazia da
   * estrutura uma armadilha: quem passasse ao lado ficava lá dentro treze
   * segundos sem ter pedido nada. Agora há um botão, e um botão é uma ordem.
   *
   * Não nomeia a estrutura, pela mesma razão que a ordem de uma carta não nomeia
   * um alvo: a sala escolhe a que este casco está a tocar, e não há campo onde
   * pedir outra.
   */
  readonly boardSummon?: { readonly commandId: string } | null;
  readonly mx: number;
  readonly my: number;
  readonly ax: number;
  readonly ay: number;
  readonly firing: boolean;
}

/**
 * A hull at its spawn, full, cold and holding nothing.
 *
 * The profile is passed IN rather than read from a constant, which is the whole
 * of this gate at its smallest: a ship's ceiling is now a property of the ship.
 * Every caller passes {@link PVP_DEFAULT_COMBAT_PROFILE} today, so every ship
 * starts on exactly the health it always started on.
 */
function spawnShip(
  seat: PvpParticipantSeat,
  profile: PvpCombatProfile = PVP_DEFAULT_COMBAT_PROFILE,
): PvpParticipantShipState {
  const spawn = pvpSpawnFor(seat.team, seat.slot);
  /*
   * **A vida máxima chega COMPOSTA, e nada aqui a compõe** (gate V28G.S).
   *
   * O `maxHealth` do perfil é o teto FINAL do casco - a vida máxima autoritativa
   * da conta, já com os pontos, os artefactos, o equipamento e o Coração do Titã
   * dentro dela. Ela foi resolvida na emissão do bilhete, que é o último sítio
   * com uma base de dados aberta, e viaja assinada.
   *
   * O V28G.R compunha-a aqui, a partir das joias do perfil, porque a base era a
   * constante normalizada de 1 000 e a joia era a única fonte que existia. Com a
   * conta a fornecer o número já final, essa linha passaria a ser uma SEGUNDA
   * aplicação do Coração do Titã: 10 000 -> 16 000 na autoridade e 16 000 ->
   * 25 600 aqui. Foi removida, e com ela a própria função que a fazia - não há
   * em `pvp/` nenhuma aritmética que multiplique vida por percentagem.
   *
   * Um casco nasce cheio no teto que lhe deram.
   */
  pvpAssertCombatProfile(profile, `pvp spawn profile for ${seat.participantId}`);
  return {
    participantId: seat.participantId,
    team: seat.team,
    slot: seat.slot,
    profile: pvpCopyCombatProfile(profile),
    x: spawn.x,
    y: spawn.y,
    aimX: spawn.aimX,
    aimY: spawn.aimY,
    healthMilli: profile.maxHealthMilli,
    cooldownMilliTicks: 0,
    moveX: 0,
    moveY: 0,
    firing: false,
    connected: false,
    appliedSeq: 0,
    effects: pvpNoEffects(),
    teleportTo: null,
    /* E nenhuma ordem em mãos: um casco acabado de nascer não mandou nada. */
    detonateBleed: false,
    /* E nada ganho: um casco acabado de nascer não tem resto de cura nenhum. */
    lifestealCarryBps: 0,
    /* E ninguém o empurrou: um casco no seu ponto de partida obedece ao piloto. */
    knockback: null,
    /*
     * A órbita começa em zero, para toda a gente (gate V28G.P).
     *
     * Determinística e independente da ordem do roster: nada aqui lê o assento,
     * o id nem a ordem em que a sala construiu os cascos, que seriam três
     * maneiras de duas máquinas discordarem sobre onde uma serra está.
     */
    sawPhase: SAW_INITIAL_PHASE,
    /* E nada por pagar: um casco acabado de nascer nunca cortou nada. */
    sawCarryMilli: 0n,
    /*
     * E nenhuma carta: um casco nasce sem loadout nenhum (gate V28H.J).
     *
     * As três entram por `freezeParticipantCards` quando o piloto se senta, pela
     * mesma regra das joias e da vida máxima - uma vez, e nunca outra vez.
     */
    cards: [],
    /* Nenhuma ordem aplicada, e nenhuma em mãos. */
    cardCommands: [],
    pendingCard: null,
    pendingBoard: null,
    pendingCardRelease: null,
    /* E nada a correr: as oito ranhuras nascem vazias (gate V28H.L). */
    cardEffects: initialCardEffects(),
  };
}

export class PvpSimulation {
  private currentTick: number;
  private readonly ruleset: PvpRuleset;
  private readonly roster: PvpShipRoster;
  private shots: PvpParticipantProjectileState[];
  /**
   * As invocações a existir na arena (gate V28H.L).
   *
   * Ao lado dos projéteis e nunca dentro de um casco: uma invocação tem posição,
   * vida e relógio seus, e pode ser atingida. Um casco que a carregasse teria de
   * responder "quem levou este tiro" com "eu ou a minha invocação".
   */
  private summons: PvpSummonState[];
  private nextSummonId: number;
  private nextProjectileId: number;
  /** The pads. Owned here because what is on them decides the match. */
  private readonly runes: PvpRuneState[];
  /** Draws which rune a pad grows, and nothing else. */
  private readonly rng: SimulationRng;
  /** A semente da sala. Só para derivar fluxos; nunca sorteia por si. */
  private readonly seed: SimulationSeed;
  /**
   * O fluxo de procs de SAÍDA, um por participante.
   *
   * ## Porque por participante, e não por joia
   *
   * Porque há uma loteria única por disparo. Um fluxo por joia poria vários
   * geradores atrás de uma decisão só, e a ordem em que fossem consultados
   * passaria a importar - sem que exista uma ordem correta.
   *
   * ## Porque separado do fluxo das runas
   *
   * Porque partilhá-lo faria cada disparo deslocar a sequência delas: um duelo
   * com o Ciclone deixaria de ter as mesmas runas que um sem, e a diferença não
   * seria da joia, seria de quantas balas foram disparadas antes.
   * `jewel-proc-rng.test.ts` é a prova.
   *
   * O domínio é o índice CANÓNICO da cadeira, nunca a posição no roster: uma
   * sala montada por outra ordem tem de dar a cada cadeira exatamente a mesma
   * sequência.
   */
  private readonly procRng = new Map<PvpParticipantId, SimulationRng>();
  /**
   * O fluxo da rolagem POR SALVA do Núcleo Atômico, um por participante.
   *
   * ## Porque não é o fluxo dos procs
   *
   * Porque partilhá-lo faria cada salva consumir dois valores a mais do fluxo do
   * Ciclone, e toda a sequência de procs a partir do primeiro disparo mudava. Um
   * duelo gravado antes deste gate deixaria de ser o mesmo duelo, e a diferença
   * não seria da Bomba - seria de quantos números alguém tirou do saco antes.
   *
   * É a mesma razão pela qual o fluxo dos procs já é separado do das runas, e é
   * exatamente a mesma prova: `duel-invariance.test.ts`.
   *
   * O domínio é o índice CANÓNICO da cadeira, como o outro.
   */
  private readonly bombRng = new Map<PvpParticipantId, SimulationRng>();
  /**
   * DEV/E2E ONLY: quantas salvas de cada cadeira ainda são forçadas a trazer
   * bomba. Ver {@link PvpSimulation.devForceVolleyBombs}.
   *
   * **Fora da instantânea, e deliberadamente.** Uma afordância de ferramenta não
   * decide o futuro de uma partida no sentido em que `PvpSnapshotV3` usa a
   * palavra: ela não muda um número, não muda o estado de um gerador, e a salva
   * seguinte de uma sala restaurada é decidida pela rolagem real. Persistir isto
   * seria pô-lo a atravessar para disco e a sobreviver a uma reconstrução.
   */
  private readonly devBombCredits = new Map<PvpParticipantId, number>();
  /**
   * **Os mísseis em voo, e porque são uma lista à parte dos tiros.**
   *
   * Porque um míssil não é um projétil desta sala em nenhum sentido útil: ele
   * persegue, troca de alvo, vive nove segundos em vez de três, voa a outra
   * velocidade e - o que decide - **nasce ao lado da salva sem ocupar um tiro**.
   * A Bomba pôde viver na lista dos projéteis porque ela É um projétil,
   * transformado; este não é nenhum.
   *
   * E tem um teto próprio com uma política própria: ao alcançá-lo o contrato
   * manda **preservar os novos e largar o mais antigo**, ao contrário do teto dos
   * projéteis, que recusa o novo. Uma lista partilhada teria de escolher uma das
   * duas.
   */
  private missiles: PvpMissileState[] = [];
  /**
   * O fluxo de SELEÇÃO DE ALVO de cada participante.
   *
   * Terceiro fluxo por cadeira, e separado dos outros dois pela mesma razão que
   * eles são separados um do outro: partilhá-lo faria uma escolha de alvo
   * deslocar a sequência de procs ou a da rolagem por salva, e um duelo com o
   * míssil deixaria de ter os mesmos procs que um sem ele.
   *
   * É consumido CONDICIONALMENTE - só quando um míssil nasce ou precisa de trocar
   * de alvo - e isso é seguro precisamente porque mais ninguém o lê: não há nada
   * a jusante para deslocar, e a instantânea guarda o estado do gerador.
   */
  private readonly missileRng = new Map<PvpParticipantId, SimulationRng>();
  /**
   * DEV/E2E ONLY: quantas salvas de cada cadeira ainda são forçadas a lançar um
   * míssil. Ver {@link PvpSimulation.devForceVolleyMissiles}.
   *
   * **Fora da instantânea**, pela mesma razão exata que os créditos da bomba
   * estão: uma afordância de ferramenta não decide o futuro de uma partida no
   * sentido em que `PvpSnapshotV3` usa a palavra.
   */
  private readonly devMissileCredits = new Map<PvpParticipantId, number>();
  /**
   * **Os stacks de veneno ativos, em ordem de criação** (gate V28G.M).
   *
   * Uma lista da SALA e não um campo do casco, porque um stack tem dois donos: o
   * atacante, que o aplicou e continua a ser o autor mesmo depois de morrer, e o
   * alvo, que o carrega. Guardá-lo no alvo obrigaria cada casco a carregar a
   * autoria do que lhe fizeram; guardá-lo no atacante fá-lo-ia desaparecer com
   * ele, que é exatamente o que o §"stacks já aplicados continuam" proíbe.
   *
   * A ORDEM é a de criação, e ela é o desempate: dois stacks que vencem no mesmo
   * tique cobram pela ordem em que nasceram, em qualquer máquina, sem que nada
   * tenha de ordenar coisa nenhuma. Os ids são monotónicos, portanto a lista já
   * está ordenada por construção e continua ordenada depois de qualquer remoção.
   */
  private poison: PvpPoisonStackState[] = [];
  /**
   * A identidade estável do próximo stack.
   *
   * Um contador próprio e não o dos projéteis: um stack sobrevive ao tiro que o
   * aplicou, e partilhar o contador faria dois objetos de vidas diferentes
   * competir pelo mesmo espaço de nomes. Atravessa a instantânea, ou uma sala
   * reconstruída daria a um stack novo o id de um que ainda está a cobrar.
   */
  private nextPoisonId: number;
  /**
   * O fluxo do VENENO, um por participante (gate V28G.M).
   *
   * Quarto fluxo por cadeira, separado dos outros três pela razão de sempre:
   * partilhá-lo faria cada acerto direto deslocar a sequência dos procs, da
   * rolagem por salva ou da escolha de alvo, e um duelo com o Orbe da Víbora
   * deixaria de ter os mesmos procs que um sem ele.
   *
   * É consumido CONDICIONALMENTE - só num acerto direto de canhão num casco que
   * lhe sobreviveu - e isso é seguro precisamente porque mais ninguém o lê: não
   * há nada a jusante para deslocar, e a instantânea guarda o estado do gerador.
   */
  private readonly poisonRng = new Map<PvpParticipantId, SimulationRng>();
  /**
   * DEV/E2E ONLY: quantos acertos diretos de cada cadeira ainda são forçados a
   * envenenar. Ver {@link PvpSimulation.devForcePoison}.
   *
   * **Fora da instantânea**, pela mesma razão exata que os créditos da bomba e
   * os do míssil estão: uma afordância de ferramenta não decide o futuro de uma
   * partida no sentido em que `PvpSnapshotV3` usa a palavra.
   */
  private readonly devPoisonCredits = new Map<PvpParticipantId, number>();
  /**
   * **As marcas de sangramento ativas, em ordem de criação** (gate V28G.N).
   *
   * Uma lista da SALA e não um campo do casco, pela mesma razão que os stacks de
   * veneno: uma marca tem dois donos - o atacante, que a possui e é o único que
   * a pode detonar, e o alvo, que a carrega.
   *
   * A ORDEM é a de criação, e ela é o desempate: uma detonação soma os milli
   * congelados pela ordem em que nasceram, em qualquer máquina, sem que nada
   * tenha de ordenar coisa nenhuma. Os ids são monotónicos, portanto a lista já
   * está ordenada por construção e continua ordenada depois de qualquer remoção.
   *
   * Não há relógio nenhum a percorrer isto: uma marca não expira e não cobra
   * parcela. O que a consome é uma ordem do autor, e mais nada.
   */
  private bleeding: PvpBleedingMarkState[] = [];
  /**
   * A identidade estável da próxima marca.
   *
   * Um contador próprio e não o dos projéteis nem o dos stacks de veneno: os
   * três descrevem coisas de vidas diferentes, e partilhar um espaço de nomes
   * fá-las-ia competir por ele. Atravessa a instantânea, ou uma sala
   * reconstruída daria a uma marca nova o id de uma que ainda existe.
   */
  private nextBleedingId: number;
  /**
   * O fluxo do SANGRAMENTO, um por participante (gate V28G.N).
   *
   * Quinto fluxo por cadeira, separado dos outros quatro pela razão de sempre:
   * partilhá-lo faria cada acerto direto deslocar a sequência dos procs, da
   * rolagem por salva, da escolha de alvo ou do veneno, e um duelo com a Garra
   * Hemorrágica deixaria de ter os mesmos procs que um sem ela.
   *
   * É consumido CONDICIONALMENTE - só num acerto direto de canhão num casco que
   * lhe sobreviveu - e isso é seguro precisamente porque mais ninguém o lê: não
   * há nada a jusante para deslocar, e a instantânea guarda o estado do gerador.
   */
  private readonly bleedingRng = new Map<PvpParticipantId, SimulationRng>();
  /**
   * DEV/E2E ONLY: quantos acertos diretos de cada cadeira ainda são forçados a
   * marcar. Ver {@link PvpSimulation.devForceBleeding}.
   *
   * **Fora da instantânea**, pela mesma razão exata que os créditos da bomba, os
   * do míssil e os do veneno estão: uma afordância de ferramenta não decide o
   * futuro de uma partida no sentido em que `PvpSnapshotV3` usa a palavra.
   */
  private readonly devBleedingCredits = new Map<PvpParticipantId, number>();
  /* ---------------------------------------------------------------------- */
  /*  A Égide Espinhosa (gate V28G.O)                                       */
  /* ---------------------------------------------------------------------- */
  /**
   * **O fluxo DEFENSIVO de cada cadeira**, um por participante.
   *
   * O primeiro fluxo desta simulação cuja cadência é ditada pelo ADVERSÁRIO: ele
   * avança uma vez por instância hostil elegível que chega a este casco. É
   * exatamente por isso que ele tem de ser separado - partilhá-lo faria a
   * sequência de runas, procs, veneno e sangramento de um jogador depender de
   * quantas vezes ele foi atingido.
   *
   * Criado à primeira utilização e depois estável, como os outros quatro.
   */
  private readonly reflectRng = new Map<PvpParticipantId, SimulationRng>();
  /**
   * **DEV/E2E ONLY.** Quantas interceções cada cadeira tem forçadas, POR TIPO.
   *
   * Fora da instantânea, pela mesma razão exata que os créditos da bomba, do
   * míssil, do veneno e do sangramento estão: uma afordância de ferramenta não
   * decide o futuro de uma partida no sentido em que `PvpSnapshotV3` usa a
   * palavra.
   *
   * ## Porque é um orçamento POR TIPO, e não um contador só
   *
   * Porque um contador único é gasto por quem chegar primeiro, e quem chega
   * primeiro é sempre o mesmo. Uma avaliação manual com a Bomba armada viu isso
   * ao vivo: a Bomba ocupa o projétil de cada salva, um projétil-bomba nunca
   * chega ao corte, ao veneno nem ao sangramento, e o avaliador ficou com um ecrã
   * de bombas e mais nada.
   *
   * Um orçamento por tipo dá a cada demonstração o seu: os primeiros N tiros
   * comuns são intercetados, e depois deles os tiros passam a acertar - que é
   * exatamente o que faz nascer o corte, o stack e a marca que as demonstrações
   * seguintes precisam de ver a ser virados.
   */
  private readonly devReflectCredits = new Map<PvpParticipantId, Map<HostilePayloadKind, number>>();
  /**
   * **DEV/E2E ONLY.** Quantos cortes de Ciclone cada cadeira tem forçados.
   *
   * A sexta afordância, e a última que faltava para uma avaliação manual poder
   * escolher o payload seguinte: os outros cinco procs já tinham a sua, e o
   * Ciclone não - a chance real dele ao teto é de 13 % por projétil, que numa
   * demonstração é uma moeda ao ar.
   *
   * Substitui **uma comparação**, como todas as outras: a rolagem continua a ser
   * feita e os dois valores continuam a sair do fluxo, portanto ligá-la não
   * desloca uma runa, um proc nem um tique. **Não acrescenta a joia.**
   */
  private readonly devCycloneCredits = new Map<PvpParticipantId, number>();
  /**
   * **Os relógios de contacto das serras, um por par serra→alvo** (gate V28G.P).
   *
   * ## Porque uma lista da SALA e não um campo do casco
   *
   * Porque um par tem dois donos e nenhum deles o contém. Guardá-lo no atacante
   * obrigaria cada casco a levar um mapa endereçado por participante - que é
   * exatamente a forma que o veneno e o sangramento já recusaram, e pela mesma
   * razão: uma morte tem de poder apagar os pares dos DOIS lados numa passagem
   * só, e um mapa por casco obriga a percorrer todos os cascos para o fazer.
   *
   * Uma lista simples, e não um `Map` de chaves compostas: ela tem no máximo
   * `6 naves x 4 lâminas x 5 inimigos = 120` entradas, a procura linear é mais
   * barata do que construir uma string de chave por tique, e uma lista tem uma
   * ordem canónica - que é o que faz a instantânea ter uma representação só.
   *
   * Vazia em toda a partida sem esta joia, e é isso que faz uma sala sem serras
   * produzir exatamente o estado que produzia antes deste gate.
   */
  private sawContacts: PvpSawContactState[] = [];
  /**
   * O fluxo das serras, por cadeira, criado à primeira utilização.
   *
   * Vazio em toda a partida sem esta joia, e é isso que faz a entropia de uma
   * sala sem serras ser byte a byte a mesma que era antes deste gate.
   */
  private readonly sawRng = new Map<PvpParticipantId, SimulationRng>();
  /**
   * **O fluxo do Coração Vital, por cadeira** (gate V28G.U).
   *
   * Criado à primeira utilização e depois estável, como os outros sete. Vazio em
   * toda a partida sem esta joia? Não: ele é consumido SEMPRE que uma salva sai,
   * com joia ou sem ela, exatamente como o da Bomba - consumir condicionalmente
   * faria a sequência de um casco depender do que ele traz encaixado.
   *
   * Um fluxo próprio, e é o contrato do gate que o exige por escrito: a cura não
   * pode deslocar RNG de runas, ofensivos, Bomba, Míssil, Veneno, Sangramento ou
   * qualquer outro sistema.
   */
  private readonly vitalRng = new Map<PvpParticipantId, SimulationRng>();
  /**
   * DEV/E2E ONLY: quantas salvas desta cadeira curam à força.
   *
   * A gémea de {@link PvpSimulation.devForceVolleyBombs}, com as mesmas
   * garantias. Ver {@link PvpSimulation.devForceVitalHeals}.
   */
  private readonly devVitalCredits = new Map<PvpParticipantId, number>();
  /**
   * **Os mísseis que uma reflexão criou NESTE tique**, à espera de entrar.
   *
   * ## Porque uma lista de espera e não um `push` direto
   *
   * Porque `advanceMissiles` percorre `this.missiles` e reconstrói a lista dos
   * sobreviventes. Um `push` durante o laço seria visitado pelo próprio laço -
   * um `for...of` sobre um array vê o que lhe foi acrescentado - e o míssil
   * refletido daria um passo no tique em que nasceu, antes de o refletor sequer
   * o ter mostrado. Deixá-lo para o fim é a diferença entre nascer e já ter
   * andado.
   *
   * Nunca sobrevive a um tique, portanto não está na instantânea: é drenada no
   * mesmo passo que a encheu.
   */
  private pendingMissiles: PvpMissileState[] = [];
  /** As bombas que uma reflexão criou neste tique, pela mesma razão exata. */
  private pendingShots: PvpParticipantProjectileState[] = [];
  private outcome: PvpTeamResult | null;
  private events: PvpParticipantEvent[] = [];

  private constructor(init: {
    readonly ruleset: PvpRuleset;
    readonly tick: number;
    readonly ships: readonly PvpParticipantShipState[];
    readonly projectiles: readonly PvpParticipantProjectileState[];
    readonly nextProjectileId: number;
    /** As invocações a existir, ou nenhuma (gate V28H.L). */
    readonly summons?: readonly PvpSummonState[];
    readonly result: PvpTeamResult | null;
    readonly runes: readonly PvpRuneState[];
    readonly rng: SimulationRng;
    /** A semente da sala, para derivar os fluxos de proc por cadeira. */
    readonly seed: SimulationSeed;
    /** Os fluxos de proc a retomar, quando isto vem de um snapshot. */
    readonly procRng?: Readonly<Record<string, SimulationRngState>>;
    /** Os fluxos da rolagem por salva, pela mesma razão (gate V28G.J). */
    readonly bombRng?: Readonly<Record<string, SimulationRngState>>;
    /** Os mísseis em voo, quando isto vem de um snapshot (gate V28G.L). */
    readonly missiles?: readonly PvpMissileState[];
    /** Os fluxos de seleção de alvo, pela mesma razão que os outros dois. */
    readonly missileRng?: Readonly<Record<string, SimulationRngState>>;
    /** Os stacks de veneno ativos, quando isto vem de um snapshot (V28G.M). */
    readonly poison?: readonly PvpPoisonStackState[];
    /** A identidade do próximo stack. Um por sala, monotónica. */
    readonly nextPoisonId?: number;
    /** Os fluxos do veneno, pela mesma razão que os outros três. */
    readonly poisonRng?: Readonly<Record<string, SimulationRngState>>;
    /** As marcas de sangramento, quando isto vem de um snapshot (V28G.N). */
    readonly bleeding?: readonly PvpBleedingMarkState[];
    /** A identidade da próxima marca. Uma por sala, monotónica. */
    readonly nextBleedingId?: number;
    /** Os fluxos do sangramento, pela mesma razão que os outros quatro. */
    readonly bleedingRng?: Readonly<Record<string, SimulationRngState>>;
    /** Os fluxos DEFENSIVOS da reflexão, pela mesma razão (gate V28G.O). */
    readonly reflectRng?: Readonly<Record<string, SimulationRngState>>;
    /** Os relógios de contacto das serras, quando isto vem de um snapshot (V28G.P). */
    readonly saws?: readonly PvpSawContactState[];
    /** Os fluxos das serras, pela mesma razão que os outros seis. */
    readonly sawRng?: Readonly<Record<string, SimulationRngState>>;
    /** Os fluxos do Coração Vital, pela mesma razão que os outros sete. */
    readonly vitalRng?: Readonly<Record<string, SimulationRngState>>;
  }) {
    this.ruleset = init.ruleset;
    this.currentTick = init.tick;
    this.roster = PvpShipRoster.of(init.ruleset, init.ships);
    this.shots = init.projectiles.map((projectile) => ({ ...projectile }));
    /*
     * Reconstruídas e nunca adoptadas, como tudo o resto: uma simulação a
     * correr não partilha uma referência com o documento de onde saiu.
     */
    this.summons = (init.summons ?? []).map((summon) => ({ ...summon }));
    /*
     * O contador continua ACIMA do maior id que veio: dois ids iguais numa
     * partida fariam a cena desenhar uma entidade em cima de outra.
     */
    this.nextSummonId = this.summons.reduce((next, summon) => Math.max(next, summon.id + 1), 1);
    this.nextProjectileId = init.nextProjectileId;
    this.outcome = init.result;
    this.runes = init.runes.map((rune) => ({ ...rune }));
    this.rng = init.rng;
    this.seed = init.seed;
    /*
     * Os fluxos RETOMAM onde estavam. Recriá-los da semente repetiria a
     * sequência desde o princípio, e o disparo seguinte a uma reconexão daria
     * um proc que a partida já tinha gasto.
     */
    for (const [participantId, state] of Object.entries(init.procRng ?? {})) {
      this.procRng.set(participantId as PvpParticipantId, restoreSimulationRng(state));
    }
    for (const [participantId, state] of Object.entries(init.bombRng ?? {})) {
      this.bombRng.set(participantId as PvpParticipantId, restoreSimulationRng(state));
    }
    /* Os mísseis são COPIADOS, como os tiros: nada é partilhado com a origem. */
    this.missiles = (init.missiles ?? []).map((missile) => ({ ...missile }));
    for (const [participantId, state] of Object.entries(init.missileRng ?? {})) {
      this.missileRng.set(participantId as PvpParticipantId, restoreSimulationRng(state));
    }
    /* Os stacks são COPIADOS, como os tiros e os mísseis: nada é partilhado. */
    this.poison = (init.poison ?? []).map((stack) => ({ ...stack }));
    this.nextPoisonId = init.nextPoisonId ?? 1;
    for (const [participantId, state] of Object.entries(init.poisonRng ?? {})) {
      this.poisonRng.set(participantId as PvpParticipantId, restoreSimulationRng(state));
    }
    /* As marcas são COPIADAS, como tudo o resto: nada é partilhado com a origem. */
    this.bleeding = (init.bleeding ?? []).map((mark) => ({ ...mark }));
    this.nextBleedingId = init.nextBleedingId ?? 1;
    for (const [participantId, state] of Object.entries(init.bleedingRng ?? {})) {
      this.bleedingRng.set(participantId as PvpParticipantId, restoreSimulationRng(state));
    }
    /*
     * E o fluxo defensivo, pela mesma razão que todos os outros retomam: um
     * gerador recriado do zero repetiria a sequência desde o princípio, e o
     * golpe seguinte a uma reconexão seria decidido por uma rolagem já gasta.
     */
    for (const [participantId, state] of Object.entries(init.reflectRng ?? {})) {
      this.reflectRng.set(participantId as PvpParticipantId, restoreSimulationRng(state));
    }
    /*
     * Os relógios de contacto das serras, COPIADOS como tudo o resto (V28G.P).
     *
     * Sem eles, uma sala reaberta daria a todas as lâminas um contacto imediato
     * - `sawPairDue` responde "sim" a um par que nunca se tocou - e o primeiro
     * tique depois de uma reconexão pagaria um intervalo inteiro de dano de uma
     * vez. É a mesma razão pela qual o veneno guarda `ticksDone`.
     */
    this.sawContacts = (init.saws ?? []).map((contact) => ({ ...contact }));
    for (const [participantId, state] of Object.entries(init.sawRng ?? {})) {
      this.sawRng.set(participantId as PvpParticipantId, restoreSimulationRng(state));
    }
    for (const [participantId, state] of Object.entries(init.vitalRng ?? {})) {
      this.vitalRng.set(participantId as PvpParticipantId, restoreSimulationRng(state));
    }
  }

  /**
   * A fresh match.
   *
   * The seed is passed IN rather than generated here: a room derives it from
   * its own match id, so two rooms are different and one room is the same every
   * time it is rebuilt. Nothing about it is secret - a simulation seed is
   * public by design, and there is nothing here worth hiding, because a duel
   * pays nothing.
   *
   * The ruleset defaults to the shipped duel, so every existing caller means
   * exactly what it meant before.
   */
  static create(seed: SimulationSeed, ruleset: PvpRuleset = PVP_DUEL_RULESET): PvpSimulation {
    return PvpSimulation.createWithRoster(seed, ruleset, pvpCanonicalSeats(ruleset));
  }

  /**
   * A fresh match from a roster given in any order.
   *
   * The order is discarded: {@link PvpShipRoster} canonicalises, so a lobby that
   * filled its slots out of order, a reconnection that rebuilt a list, and a
   * test that shuffled deliberately all produce the same match. That is the
   * property this overload exists to make provable rather than assumed.
   */
  static createWithRoster(
    seed: SimulationSeed,
    ruleset: PvpRuleset,
    seats: readonly PvpParticipantSeat[],
    /**
     * What each chair's hull is, or nothing for the shipped one.
     *
     * ## Why the seam exists before anything can use it
     *
     * Because "a profile that is not the default can be represented" has to be
     * PROVABLE now, or the first jewel gate would be introducing the ability
     * and the effect in one change. Nothing in `apps/` passes this: the only
     * callers are `create` (which omits it) and the tests that assert a
     * non-default hull behaves as its numbers say.
     *
     * It takes a seat rather than a map so a caller cannot supply a profile for
     * a chair that is not in the match - the roster would refuse it later, but
     * refusing it by construction is cheaper than refusing it by validation.
     */
    profileFor?: (seat: PvpParticipantSeat) => PvpCombatProfile,
  ): PvpSimulation {
    return new PvpSimulation({
      ruleset,
      tick: 0,
      ships: seats.map((seat) => spawnShip(seat, profileFor?.(seat))),
      projectiles: [],
      nextProjectileId: 1,
      result: null,
      runes: pvpInitialRunes(PVP_RUNE_FIRST_SPAWN_TICKS),
      rng: createSimulationRng(deriveSimulationSeed(seed, 'pvp-rune', 0)),
      seed,
    });
  }

  /**
   * **The v2 legacy decoder.** Rebuilds a DUEL from its historic snapshot.
   *
   * ## How a caller knows it wants this one
   *
   * Not by looking at the snapshot. The historic shape carries no `version`
   * field and never did, so it is identified by its CONTAINER: it lives inside
   * a `PvpRoomPersistedState` stamped `v: PVP_ROOM_STATE_VERSION`, and
   * `PvpRoom.deserialize` refuses any other stamp before reaching this. That is
   * the whole identification scheme - there is no sniffing, no "does it have a
   * `version` key", and no heuristic anywhere else in the codebase.
   *
   * The team format is a separate, self-describing decoder:
   * {@link PvpSimulation.restoreV3}. The two never inspect each other's input.
   *
   * A malformed snapshot must fail loudly. Accepting one would not produce an
   * error - it would produce a DIFFERENT duel, resumed under the losing
   * player's nose.
   */
  static restore(snapshot: PvpSnapshot): PvpSimulation {
    /*
     * A VERSIONED snapshot is not this format, and saying so is the point.
     *
     * The historic shape has no `version`, no `ruleset` and no `participants`.
     * Anything carrying one of those was written by the v3 encoder or by
     * something splicing the two together, and reading it here would get as far
     * as `ships.length` before failing with a property access - an error that
     * says nothing about what was actually wrong. This refuses by name instead.
     */
    const candidate = snapshot as unknown as Record<string, unknown>;
    for (const key of ['version', 'ruleset', 'participants']) {
      if (key in candidate) {
        throw new RangeError(
          `a pvp snapshot carrying "${key}" is not the historic duel shape; use restoreV3`,
        );
      }
    }
    if (!Array.isArray(snapshot.ships)) {
      throw new RangeError('a pvp duel snapshot must carry its ships');
    }
    if (!Array.isArray(snapshot.projectiles) || !Array.isArray(snapshot.runes)) {
      throw new RangeError('a pvp duel snapshot must carry its projectiles and its pads');
    }
    assertSafeInteger(snapshot.tick, 'pvp snapshot tick');
    if (snapshot.tick < 0) {
      throw new RangeError('pvp snapshot tick must not be negative');
    }
    assertSafeInteger(snapshot.nextProjectileId, 'pvp snapshot nextProjectileId');
    if (snapshot.ships.length !== 2) {
      throw new RangeError('a pvp match has exactly two seats');
    }
    const seats = new Set(snapshot.ships.map((ship) => ship.seat));
    if (!seats.has('a') || !seats.has('b')) {
      throw new RangeError('a pvp snapshot must carry seat a and seat b');
    }
    for (const ship of snapshot.ships) {
      assertSafeInteger(ship.x, 'pvp ship x');
      assertSafeInteger(ship.y, 'pvp ship y');
      assertSafeInteger(ship.health, 'pvp ship health');
      /*
       * Against the PROFILE's ceiling, not a global constant.
       *
       * The historic format carries no profile and never can - see
       * `snapshot()`, which refuses to write one that is not the default - so
       * the ceiling a v2 record is checked against is the default's, which is
       * the only ceiling any v2 record could ever have been written under.
       *
       * O documento v2 guarda PONTOS INTEIROS e sempre guardou: ele é anterior
       * ao milli e nunca poderá conter outra coisa. A travessia para milli
       * acontece na adoção, multiplicando por mil exatamente (gate V28G.S.1).
       */
      if (ship.health < 0 || ship.health > PVP_MAX_HEALTH) {
        throw new RangeError('pvp ship health is outside its bounds');
      }
      assertSafeInteger(ship.cooldown, 'pvp ship cooldown');
      if (ship.cooldown < 0) {
        throw new RangeError('pvp ship cooldown must not be negative');
      }
      assertSafeInteger(ship.appliedSeq, 'pvp ship appliedSeq');
    }
    if (snapshot.projectiles.length > PVP_MAX_PROJECTILES) {
      throw new RangeError('pvp snapshot carries more projectiles than a match may hold');
    }
    return new PvpSimulation({
      ruleset: PVP_DUEL_RULESET,
      tick: snapshot.tick,
      ships: snapshot.ships.map((ship) => restoreDuelShip(ship)),
      projectiles: snapshot.projectiles.map((projectile) => ({
        id: projectile.id,
        ownerParticipantId: pvpDuelSeatToParticipantId(projectile.seat),
        ownerTeam: pvpDuelSeatToTeam(projectile.seat),
        /*
         * O formato v2 guarda dano INTEIRO em pontos. Multiplicar por mil é a
         * conversão exata para milli, e é exata porque v2 nasceu antes dos
         * corações: nenhum tiro que ele descreve tem fração para perder.
         */
        damageMilli: BigInt(projectile.damage) * BigInt(DAMAGE_MILLI_PER_UNIT),
        /*
         * Um tiro de um formato ANTERIOR ao gate V28G.F nunca teve proc, e
         * inventar-lhe um agora seria dar a um projétil em voo um efeito que a
         * partida que o disparou não conhecia. `null` é o upgrade correto.
         */
        jewelProc: null,
        /* E nenhuma bomba, pela mesma razão exata (gate V28G.J). */
        atomicBomb: null,
        x: projectile.x,
        y: projectile.y,
        vx: projectile.vx,
        vy: projectile.vy,
        life: projectile.life,
        /*
         * E nenhuma linhagem refletida, pela mesma razão exata (gate V28G.O).
         *
         * Um documento v2 correu num build onde a Égide Espinhosa não fazia
         * nada, portanto nenhum projétil dele pode ter nascido de uma reflexão.
         * `false` é o FACTO, e não uma lacuna.
         */
        reflected: false,
        /* Sem explosão de carta e sem perseguição: um tiro comum (gate V28H.L). */
        cardBlast: null,
        homingTargetId: null,
      })),
      nextProjectileId: snapshot.nextProjectileId,
      result: snapshot.result === null ? null : restoreDuelResult(snapshot.result),
      runes: snapshot.runes,
      rng: restoreSimulationRng(snapshot.rng),
      seed: PvpSimulation.seedFromLegacyRng(snapshot.rng),
      /* Um documento v2 nunca teve procs: os fluxos nascem limpos. */
    });
  }

  /**
   * **The v3 decoder.** Rebuilds a TEAM match from a self-describing snapshot.
   *
   * Takes `unknown` on purpose: this is what comes back out of storage and off a
   * wire, and a decoder typed to its own output has already assumed the thing it
   * exists to check. Everything is validated by {@link pvpDecodeSnapshotV3} and
   * nothing is repaired - see the refusal list there.
   *
   * Unlike the v2 decoder this one identifies itself, because the format
   * carries its own `version` and `ruleset` - and the ruleset is taken FROM the
   * document rather than assumed. It reads a duel as well as a team match now:
   * the historic two-seat shape has no field for a combat profile, so a duel
   * that is told its hulls differ has to be able to write that down.
   *
   * ## A versão 4 sobe explicitamente, e só ela
   *
   * `pvpUpgradeSnapshotV4` reescreve um documento da versão 4 como um da versão
   * 5 - contagem de corações a zero, resto de dano a zero, dano do tiro
   * convertido de pontos para milli - e devolve `null` para tudo o resto. Não é
   * tolerância: é uma reescrita total e testável que corre ANTES do
   * descodificador, e o descodificador continua a exigir a versão exata e o
   * conjunto exato de chaves. Uma sala em voo quando isto embarcar continua a
   * ser a mesma sala, tique a tique.
   */
  static restoreV3(value: unknown): PvpSimulation {
    /*
     * A cadeia é tentada em TODOS os seus degraus, e não só no primeiro.
     *
     * Cada upgrade recusa uma versão que não é a sua, e cada um encadeia para o
     * seguinte - portanto entrar no degrau certo é o que faz um documento subir
     * até aqui. Entrar só pelo 4 deixava um documento da versão 5, 6 ou 7 cair
     * no `?? value` e ser recusado pelo descodificador com uma mensagem sobre a
     * versão, escondendo que o upgrade existia e nunca tinha sido chamado.
     */
    const upgraded =
      pvpUpgradeSnapshotV4(value) ??
      pvpUpgradeSnapshotV5(value) ??
      pvpUpgradeSnapshotV6(value) ??
      pvpUpgradeSnapshotV7(value) ??
      pvpUpgradeSnapshotV8(value) ??
      pvpUpgradeSnapshotV9(value) ??
      pvpUpgradeSnapshotV10(value) ??
      pvpUpgradeSnapshotV11(value) ??
      pvpUpgradeSnapshotV12(value) ??
      pvpUpgradeSnapshotV13(value) ??
      pvpUpgradeSnapshotV14(value) ??
      pvpUpgradeSnapshotV15(value) ??
      pvpUpgradeSnapshotV16(value) ??
      value;
    const snapshot = pvpDecodeSnapshotV3(upgraded);
    return new PvpSimulation({
      ruleset: snapshot.ruleset as PvpRuleset,
      tick: snapshot.tick,
      ships: snapshot.participants.map((participant) => shipFromV3(participant)),
      projectiles: snapshot.projectiles.map((projectile) => ({
        id: projectile.id,
        ownerParticipantId: projectile.ownerParticipantId,
        ownerTeam: teamFromV3(projectile.ownerTeam),
        /* Do fio para o exato. O decodificador já provou que é um decimal. */
        damageMilli: BigInt(projectile.damageMilli),
        /*
         * O proc congelado VOLTA, e este é o defeito que o gate V28G.F deixou.
         *
         * A versão 7 já persistia o campo e esta linha punha `null` na mesma,
         * pelo que uma sala reaberta perdia o corte de todos os tiros no ar - o
         * documento dizia uma coisa e a simulação restaurada dizia outra. Nada
         * apanhava isso: as provas de reconexão liam os FLUXOS, e um fluxo
         * retomado corretamente não diz nada sobre os projéteis.
         */
        jewelProc: jewelProcFromWire(
          projectile.jewelProc === null
            ? null
            : {
                id: projectile.jewelProc.id as OutgoingJewelProcId,
                entropy: projectile.jewelProc.entropy,
                cutMilli: projectile.jewelProc.cutMilli,
              },
        ),
        /*
         * E a BOMBA volta com ela (gate V28G.J), pela mesma razão e com a mesma
         * disciplina: a magnitude vem do documento porque é a única fonte que
         * ainda diz quanto ela valia quando a salva saiu.
         */
        atomicBomb: atomicBombFromWire(projectile.atomicBomb),
        x: projectile.x,
        y: projectile.y,
        vx: projectile.vx,
        vy: projectile.vy,
        life: projectile.life,
        /* E a explosão de carta e a perseguição voltam com eles (V28H.L). */
        cardBlast:
          projectile.cardBlast === undefined || projectile.cardBlast === null
            ? null
            : {
                kind: projectile.cardBlast.kind ?? 'fireball',
                radiusMu: projectile.cardBlast.radiusMu,
                damageMilli: BigInt(projectile.cardBlast.damageMilli),
              },
        homingTargetId: (projectile.homingTargetId ?? null) as PvpParticipantId | null,
        /*
         * E a LINHAGEM volta com eles (gate V28G.O).
         *
         * Sem ela, uma bomba refletida restaurada voltaria a ser intercetável, e
         * a profundidade máxima de um deixaria de ser verdade depois de uma
         * reconexão - dois cascos com a joia fariam o pingue-pongue que a tabela
         * fechada existe para tornar impossível.
         */
        reflected: projectile.reflected,
      })),
      nextProjectileId: snapshot.nextProjectileId,
      result:
        snapshot.result === null
          ? null
          : pvpTeamResultFromV3(snapshot.result, snapshot.ruleset as PvpRuleset),
      /*
       * Os pads voltam como o descodificador os deu, e mais nada.
       *
       * Havia aqui uma normalização que transformava a runa removida num pad
       * vazio (gate V28H.H.1). Ela saiu com a mecânica: um documento que nomeie
       * a runa removida é recusado pelo descodificador, como qualquer outra
       * espécie desconhecida sempre foi.
       */
      runes: snapshot.runes.map((rune) => ({
        pad: rune.pad,
        kind: rune.kind,
        readyAt: rune.readyAt,
      })),
      /* E as invocações voltam com elas (versão 21, gate V28H.L). */
      summons: (snapshot.summons ?? []).map((summon) => ({
        id: summon.id,
        kind: summon.kind as PvpSummonKind,
        ownerParticipantId: summon.ownerParticipantId as PvpParticipantId,
        ownerTeam: teamFromV3(summon.ownerTeam),
        x: summon.x,
        y: summon.y,
        aimX: summon.aimX,
        aimY: summon.aimY,
        healthMilli: BigInt(summon.healthMilli),
        maxHealthMilli: BigInt(summon.maxHealthMilli),
        lifeMs: summon.lifeMs,
        attackAccumulatorMs: summon.attackAccumulatorMs,
        pilotParticipantId: (summon.pilotParticipantId ?? null) as PvpParticipantId | null,
        boardedRemainingMs: summon.boardedRemainingMs,
      })),
      rng: restoreSimulationRng(snapshot.rng),
      seed: simulationSeed(BigInt(snapshot.seed)),
      procRng: snapshot.procRng,
      bombRng: snapshot.bombRng,
      /*
       * Os mísseis VOLTAM inteiros (gate V28G.L).
       *
       * Posição, direção, alvo, tempo restante, identidade e o dano congelado -
       * a lista exata que o contrato manda sobreviver. Nada é reavaliado: a
       * magnitude vem do documento porque é a única fonte que ainda diz quanto
       * ela valia quando o míssil saiu, e o alvo vem de lá porque a escolha foi
       * autoritativa e já foi feita.
       */
      missiles: snapshot.missiles.map((missile) => ({
        id: missile.id,
        ownerParticipantId: missile.ownerParticipantId,
        ownerTeam: teamFromV3(missile.ownerTeam),
        damageMilli: BigInt(missile.damageMilli),
        entropy: missile.entropy,
        targetParticipantId: missile.targetParticipantId,
        x: missile.x,
        y: missile.y,
        dirX: missile.dirX,
        dirY: missile.dirY,
        life: missile.life,
        /* E a LINHAGEM, pela mesma razão exata. */
        reflected: missile.reflected,
      })),
      missileRng: snapshot.missileRng,
      /*
       * Os stacks VOLTAM inteiros (gate V28G.M).
       *
       * Identidade, autoria, alvo, total congelado, instante de aplicação e
       * parcelas já pagas - o que basta para as que faltam caírem exatamente nos
       * instantes em que iam cair. Nada é reavaliado: o total vem do documento
       * porque é a única fonte que ainda diz quanto ele valia quando o stack
       * nasceu, e o relógio continua de onde estava.
       */
      poison: snapshot.poison.map((stack) => ({
        id: stack.id,
        attackerParticipantId: stack.attackerParticipantId,
        targetParticipantId: stack.targetParticipantId,
        totalDamageMilli: BigInt(stack.totalDamageMilli),
        appliedAt: stack.appliedAt,
        ticksDone: stack.ticksDone,
        /* E a LINHAGEM: sem ela, as seis parcelas voltariam a ser elegíveis. */
        reflected: stack.reflected,
      })),
      nextPoisonId: snapshot.nextPoisonId,
      poisonRng: snapshot.poisonRng,
      /*
       * As marcas VOLTAM inteiras (gate V28G.N).
       *
       * Identidade, autoria, alvo, dano congelado e instante de aplicação - o
       * que basta para uma detonação depois da retoma cobrar exatamente o que
       * cobraria antes dela. Nada é reavaliado: o dano vem do documento porque é
       * a única fonte que ainda diz quanto ele valia quando a marca nasceu.
       */
      bleeding: snapshot.bleeding.map((mark) => ({
        id: mark.id,
        attackerParticipantId: mark.attackerParticipantId,
        targetParticipantId: mark.targetParticipantId,
        damageMilli: BigInt(mark.damageMilli),
        appliedAt: mark.appliedAt,
        /* E a LINHAGEM: sem ela, a detonação seguinte voltaria a ser elegível. */
        reflected: mark.reflected,
      })),
      nextBleedingId: snapshot.nextBleedingId,
      bleedingRng: snapshot.bleedingRng,
      /*
       * E os fluxos DEFENSIVOS retomam onde estavam (gate V28G.O).
       *
       * Recriá-los da semente repetiria a sequência desde o princípio, e a
       * instância hostil seguinte a uma reconexão seria decidida por uma rolagem
       * que a partida já tinha gasto.
       */
      reflectRng: snapshot.reflectRng,
      /*
       * Os relógios de contacto VOLTAM inteiros (gate V28G.P).
       *
       * Dono, lâmina, alvo e o instante do último contacto - o que basta para
       * uma lâmina a meio do seu intervalo continuar a meio dele. Nada é
       * reavaliado: a magnitude de um contacto sai do perfil congelado no
       * instante em que ele acontece, e não há nada entre decidir e aplicar onde
       * ela pudesse mudar de ideias.
       */
      saws: snapshot.saws.map((contact) => ({ ...contact })),
      sawRng: snapshot.sawRng,
      vitalRng: snapshot.vitalRng,
    });
  }

  /* ------------------------------------------------------------------------ */
  /*  Canonical surface: everything by participant                            */
  /* ------------------------------------------------------------------------ */

  get tick(): number {
    return this.currentTick;
  }

  /** Which shape of match this is. Fixed at construction and never widened. */
  get matchRuleset(): PvpRuleset {
    return this.ruleset;
  }

  /** How the match ended, by TEAM. The internal truth every other answer derives from. */
  get teamResult(): PvpTeamResult | null {
    return this.outcome;
  }

  /** Everyone, canonical order, alive or not. A dead ship is never removed. */
  participants(): readonly PvpParticipantShipState[] {
    return this.roster.all();
  }

  hasParticipant(participantId: PvpParticipantId): boolean {
    return this.roster.has(participantId);
  }

  /** The live ship. Mutating it mutates the match, which is what callers want. */
  shipOf(participantId: PvpParticipantId): PvpParticipantShipState {
    return this.roster.require(participantId);
  }

  /**
   * **Congela quantos Corações Primordiais este casco leva para a partida.**
   *
   * Chamado uma vez, pela sala, quando a pessoa se senta - e nunca depois. É a
   * fronteira temporal do gate V28F.B expressa como uma operação em vez de uma
   * regra: incorporar um coração a meio de uma partida não a altera, porque não
   * existe caminho por onde a contagem nova pudesse entrar.
   *
   * ## Porque recusa depois do primeiro tique
   *
   * Porque a partir daí há tiros no ar que nasceram com o dano antigo, e mudar
   * o casco por baixo deles produziria uma partida em que dois projéteis do
   * mesmo cano valem coisas diferentes por uma razão que ninguém consegue ver.
   * A recusa é estrutural; um comentário seria uma esperança.
   *
   * ## Só a contagem
   *
   * Os outros quatro campos do perfil não são tocados. O duelo continua
   * normalizado - 40 de dano-base, um teto de vida, uma velocidade e uma cadência
   * iguais para toda a gente - e os corações são a ÚNICA coisa que fura essa
   * normalização, que é exatamente o que o produto decidiu.
   */
  freezeParticipantHearts(participantId: PvpParticipantId, primordialHeartCount: bigint): void {
    if (this.currentTick > 0) {
      throw new RangeError(
        `a hull is frozen when its pilot sits down, not at tick ${this.currentTick}`,
      );
    }
    if (typeof primordialHeartCount !== 'bigint' || primordialHeartCount < 0n) {
      throw new RangeError(`a primordial heart count is a non-negative bigint`);
    }
    const ship = this.roster.require(participantId);
    const next: PvpCombatProfile = { ...ship.profile, primordialHeartCount };
    pvpAssertCombatProfile(next, `pvp hull for ${participantId}`);
    /*
     * O perfil é `readonly` na interface e substituído por inteiro aqui: uma
     * atribuição campo a campo seria um sítio onde metade de um casco pode ficar
     * escrita. O elenco é local a esta linha e não sai dela.
     */
    (ship as { profile: PvpCombatProfile }).profile = next;
  }

  /**
   * O perfil de joias deste casco, escrito quando o piloto se senta.
   *
   * ## A mesma regra da contagem de corações, e pela mesma razão
   *
   * Recusa depois do primeiro tique. A partir daí há tiros no ar que nasceram
   * sob uma configuração, e trocar o casco por baixo deles produziria uma
   * partida em que dois projéteis do mesmo cano valem coisas diferentes por um
   * motivo que ninguém consegue ver. A recusa é estrutural; um comentário seria
   * uma esperança.
   *
   * ## E o que ele NÃO muda mais (gate V28G.S)
   *
   * `maxHealth`. Até ao V28G.R o Coração do Titã era composto aqui, a partir
   * destas joias, porque a vida-base do duelo era a constante normalizada de
   * 1 000. Agora o teto chega FINAL no bilhete - a vida máxima autoritativa da
   * conta, com o Coração do Titã já lá dentro - e compô-lo outra vez aqui seria
   * a dupla aplicação que o gate proíbe por nome: 16 000 viraria 25 600.
   *
   * Quem escreve o teto é {@link freezeParticipantMaxHealth}, e nada mais. Este
   * método escreve as joias e só as joias, exatamente como o dos corações
   * escreve a contagem e só a contagem.
   *
   * ## Uma vez, e a recusa é estrutural
   *
   * Um casco que já carrega joias é RECUSADO. Uma substituição não passa por
   * aqui duas vezes: ela repõe o casco primeiro ({@link resetParticipantHull}),
   * porque quem se senta é outra pessoa.
   */
  freezeParticipantJewels(participantId: PvpParticipantId, jewels: JewelSocketProfile): void {
    if (this.currentTick > 0) {
      throw new RangeError(
        `a hull is frozen when its pilot sits down, not at tick ${this.currentTick}`,
      );
    }
    const ship = this.roster.require(participantId);
    if (ship.profile.jewels.entries.length > 0) {
      throw new RangeError(
        `${participantId} already carries jewels: a hull is composed once, never twice`,
      );
    }
    const next: PvpCombatProfile = {
      ...ship.profile,
      jewels: parseJewelSocketProfile(jewels),
    };
    pvpAssertCombatProfile(next, `pvp hull for ${participantId}`);
    (ship as { profile: PvpCombatProfile }).profile = next;
  }

  /**
   * **Congela as três cartas que este casco leva para a partida** (V28H.J).
   *
   * ## A mesma regra das joias, e pela mesma razão
   *
   * Uma vez, quando o piloto se senta, e nunca outra vez. Um bilhete de
   * reconexão traz o loadout que a conta tem NESSE instante, e deixá-lo
   * sobrepor-se seria deixar alguém trocar de cartas noutro separador e
   * carregar em F5 para levar as novas para uma partida a meio.
   *
   * Uma SUBSTITUIÇÃO muda-as, porque é outra pessoa a sentar-se: a nave volta ao
   * que nasceu e o bilhete do substituto congela o casco DELE.
   *
   * ## O que ela NÃO faz
   *
   * Não liga carta nenhuma, e não decide quais funcionam. Ela escreve os três
   * espaços; o que cada carta faz - ou não faz - é decidido em
   * {@link activateParticipantCard}, contra a mesma lista de habilidades
   * implementadas que a caça usa.
   */
  freezeParticipantCards(participantId: PvpParticipantId, cards: CardLoadout): void {
    if (this.currentTick > 0) {
      throw new RangeError(
        `a hull is frozen when its pilot sits down, not at tick ${this.currentTick}`,
      );
    }
    const ship = this.roster.require(participantId);
    if (ship.cards.length > 0) {
      throw new RangeError(
        `${participantId} already carries cards: a hull is composed once, never twice`,
      );
    }
    ship.cards = initialCardAbilities(cards);
  }

  /**
   * **A ORDEM do jogador para ligar uma carta** (gate V28H.J).
   *
   * ## Quem pode dar a ordem
   *
   * A cadeira que a mandou, e mais ninguém: o participante chega da LIGAÇÃO em
   * que o frame entrou, e não de um campo que o cliente escreveu. Não há nada
   * aqui para forjar.
   *
   * ## O que ela recusa, e sem gastar nada
   *
   * Um espaço vazio, uma carta que não funciona no duelo, uma carta sem
   * habilidade escrita, um casco morto, e uma carta que ainda arrefece. As
   * quatro primeiras são perguntas ao loadout congelado; a última é delegada em
   * `cardAbilityCanActivate`, que é a mesma fronteira que a caça atravessa.
   *
   * ## A repetição LÊ
   *
   * Uma ordem cuja identidade já foi aplicada devolve `true` sem ligar nada:
   * ela já ligou. É isso que faz um cliente que reenvia - porque não viu a carta
   * acender - receber a verdade em vez de uma recusa.
   *
   * @returns `true` quando a carta ficou activa por causa desta ordem, ou já
   * estava por causa da mesma ordem repetida.
   */
  /**
   * **Solta a carga de um espaço** (gate V28H.N).
   *
   * A mesma disciplina de {@link activateParticipantCard}: a identidade já
   * aplicada LÊ em vez de repetir, o casco é o da ligação, e uma ordem que não
   * encontra carga nenhuma é recusada em silêncio em vez de disparar do nada.
   *
   * Não toca no arrefecimento: ele conta desde a activação, e soltar cedo não
   * devolve nada - é o preço de uma carga curta.
   */
  /**
   * **Quanto tempo esta carta carrega NESTA arena** (gate V28I.C).
   *
   * Quinze segundos para a Esfera Ígnea, o catálogo para as outras. A caça tem o
   * seu, e o catálogo guarda zero para ninguém o ler por engano.
   */
  private chargeMsOf(card: CardAbilityState): number {
    if (card.definitionId === 'card_charged_fireball') {
      return PVP_FIREBALL_CHARGE_MS;
    }
    return cardBalance(card.definitionId)?.chargeMs ?? 0;
  }

  private releaseParticipantCard(
    ship: PvpParticipantShipState,
    order: { readonly slot: CardSlot; readonly commandId: string },
  ): boolean {
    if (ship.cardCommands.includes(order.commandId)) {
      return true;
    }
    const card = ship.cards.find((entry) => entry.slot === order.slot);
    if (!card || ship.healthMilli <= 0n || !cardAbilityCanRelease(card)) {
      return false;
    }
    if (!releaseCardCharge(card, this.chargeMsOf(card))) {
      return false;
    }
    /* O que a carga produzia acontece AGORA, pelo caminho do fim natural. */
    this.releaseChargedCard(ship, card);
    ship.cardCommands = [order.commandId, ...ship.cardCommands].slice(0, PVP_CARD_COMMAND_MEMORY);
    return true;
  }

  /**
   * **Encerra o Manto Espectral deste casco** (gate V28H.V).
   *
   * Uma porta só para as duas maneiras de o perder: um tiro de canhão e uma
   * ordem ofensiva aceite. Duas cópias desta função seriam dois sítios onde o
   * HUD se pode esquecer de acompanhar o efeito.
   *
   * Ela apaga as DUAS coisas, e é a correcção que a auditoria trouxe:
   * `cardBreakCloak` zera o temporizador do EFEITO - que é o estado
   * autoritativo, o que decide quem se vê - e a carta ficava a correr, portanto
   * a barra dizia "ATIVA 7s" com a nave já visível.
   *
   * O arrefecimento não é tocado: ele conta desde a ordem, e revelar-se cedo não
   * devolve nada - é o preço de ter atacado.
   */
  private breakCloak(ship: PvpParticipantShipState): void {
    if (!cardBreakCloak(ship.cardEffects)) {
      return;
    }
    const cloak = ship.cards.find((entry) => entry.definitionId === 'card_cloak');
    if (cloak !== undefined) {
      cloak.activeRemainingMs = 0;
    }
  }

  private activateParticipantCard(
    ship: PvpParticipantShipState,
    order: { readonly slot: CardSlot; readonly commandId: string },
  ): boolean {
    if (ship.cardCommands.includes(order.commandId)) {
      return true;
    }
    const card = ship.cards.find((entry) => entry.slot === order.slot);
    if (!card) {
      return false;
    }
    if (!cardWorksIn(card.definitionId, 'pvp')) {
      return false;
    }
    /*
     * Um alvo? No duelo há sempre adversário enquanto a partida corre, e a
     * pergunta que a caça faz - "há mob no campo?" - não tem equivalente aqui.
     * Um casco morto é a recusa que substitui essa, e está logo abaixo.
     */
    if (ship.healthMilli <= 0n) {
      return false;
    }
    if (!cardAbilityCanActivate(card, { hasTarget: true })) {
      return false;
    }
    /*
     * **O efeito é PREPARADO antes de a carta ligar** (gate V28H.L).
     *
     * O contrato dos Super Canhões fixou-o e vale para as dezasseis: "sem alvo
     * válido a ordem é recusada e não gasta arrefecimento". Uma carta que
     * ligasse primeiro e falhasse depois queimaria o arrefecimento por nada, e
     * é exactamente o que acontece a quem carrega no botão com o adversário
     * fora de alcance.
     */
    const plan = this.planCardEffect(ship, card);
    if (plan === null) {
      return false;
    }
    activateCardAbility(card, this.chargeMsOf(card));
    /*
     * **E uma ordem OFENSIVA aceite encerra o Manto** (gate V28H.V).
     *
     * Aqui e em nenhum outro sítio. Este é o ponto por onde passam as dezasseis
     * cartas depois de todas as recusas - espaço vazio, modo errado, casco
     * morto, arrefecimento a correr, plano impossível - portanto uma ordem
     * recusada nunca chega cá, que é exactamente o que a decisão pede.
     *
     * E é na ORDEM, e não no dano: a Lança Solar e a Esfera Ígnea revelam quem
     * as usa no início da carga, que é o instante em que o adversário passa a
     * ter direito a ver o que vem aí.
     */
    if (cardIsOffensive(card.definitionId)) {
      this.breakCloak(ship);
    }
    plan();
    /*
     * A identidade fica registada DEPOIS de a carta ligar, e a lista tem teto:
     * a mais recente à cabeça, e o que passar de oito cai. Uma lista sem teto
     * seria um cliente a fazer a sala crescer para sempre.
     */
    ship.cardCommands = [order.commandId, ...ship.cardCommands].slice(0, PVP_CARD_COMMAND_MEMORY);
    return true;
  }

  /**
   * **O que esta carta vai fazer, ou `null` se não puder fazer nada** (V28H.L).
   *
   * ## Porque devolve uma FUNÇÃO e não um efeito já aplicado
   *
   * Porque a pergunta "isto pode acontecer?" tem de ser respondida **antes** de
   * a carta ligar, e a resposta "sim" tem de valer para o instante em que ela
   * liga. Devolver um plano resolve as duas: quem chama pergunta primeiro,
   * liga a seguir, e executa por fim - sem um estado intermédio em que a carta
   * está ligada e o efeito ainda não existe.
   *
   * ## A escolha do alvo é do SERVIDOR, e é determinista
   *
   * A ordem que chega do cliente leva o espaço e mais nada - a ADR 0064 fez
   * disso a razão de ninguém poder ligar a carta de outra pessoa, e um campo de
   * alvo desfaria essa garantia de uma penada. Portanto o alvo é derivado:
   *
   * **o inimigo VIVO e ALVEJÁVEL mais próximo, dentro do alcance da carta**, e
   * um empate resolve-se pela ordem canónica dos participantes.
   *
   * Sem sorteio nenhum. Um sorteio faria a mesma janela re-simulada escolher
   * outra pessoa, e a re-simulação é o que torna a sala autoritativa.
   */
  private planCardEffect(
    ship: PvpParticipantShipState,
    card: CardAbilityState,
  ): (() => void) | null {
    const balance = cardBalance(card.definitionId);
    if (balance === null) {
      return null;
    }
    switch (balance.effect) {
      case 'self_buff':
        return this.planSelfBuff(ship, balance);
      case 'target_debuff':
        return this.planTargetDebuff(ship, balance);
      case 'area_debuff':
        return this.planAreaDebuff(ship, balance);
      case 'dash':
        return this.planDash(ship, balance);
      case 'charged_beam':
      case 'charged_projectile':
        /*
         * As duas que CARREGAM não fazem nada no instante da ordem: o que a
         * ordem faz é começar a carga, e `activateCardAbility` já a começou. O
         * efeito sai quando ela acaba, em `releaseChargedCard`.
         */
        return () => {};
      case 'guided_blast':
        return this.planGuidedBlast(ship, balance);
      case 'summon':
        return this.planSummon(ship, card, balance);
      case 'temporary_cannons':
      case 'orbit_blade':
        /*
         * Nenhuma das duas tem efeito a aplicar AQUI: elas são lidas do estado
         * da carta - `activeRemainingMs` - por quem as executa a cada tique,
         * `fireSuperCannons` e `tickBladeStorm`. Um plano vazio é a maneira de
         * dizer "podes ligar", que é exactamente o que os Super Canhões sempre
         * fizeram.
         *
         * O acumulador é posto a zero por `activateCardAbility`, portanto a
         * primeira lâmina do Turbilhão cai um intervalo depois da activação e
         * não no instante dela - a habilidade nasce montada, e o primeiro golpe
         * dela é um golpe e não um efeito colateral de ligar.
         */
        return () => {};
      default:
        /*
         * As que ainda não têm forma escrita neste ficheiro. Elas não estão em
         * `IMPLEMENTED_CARD_ABILITIES`, portanto `cardAbilityCanActivate` já as
         * recusou antes de chegarmos aqui - este ramo existe para o compilador
         * ter o `switch` completo, e para uma carta que entre na lista sem ramo
         * ser recusada em vez de ligar e não fazer nada.
         */
        return null;
    }
  }

  /**
   * **O Catolé: uma bomba colossal, teleguiada a alguém** (gate V28H.L).
   *
   * Sem carga - ele sai no instante da ordem - e COM alvo, que é o que
   * "teleguiada" quer dizer. O alvo é escolhido pelo servidor pela mesma regra
   * de todas as cartas com alvo, e é escolhido **uma vez**: um projétil que
   * trocasse de alvo a meio do voo seria um projétil que ninguém consegue
   * evitar.
   *
   * Sem ninguém a quem o mandar, a ordem é recusada e não gasta arrefecimento -
   * sessenta segundos é caro de mais para se perder num campo vazio.
   */
  private planGuidedBlast(
    ship: PvpParticipantShipState,
    balance: CardBalance,
  ): (() => void) | null {
    /*
     * Sem limite de alcance na ESCOLHA: o Catolé persegue, portanto o que
     * importa é haver alguém, e não estar perto. `PVP_ARENA_WIDTH_MU` em bps do
     * casco é maior do que a arena inteira, que é a maneira de dizer "toda a
     * gente" sem escrever um caso especial.
     */
    const target = this.nearestCardTarget(ship, PVP_CARD_UNLIMITED_REACH_BPS);
    if (target === null) {
      return null;
    }
    return () => {
      this.launchCardBlast(ship, balance, target);
    };
  }

  /**
   * **Uma entidade nasce no mundo** (gate V28H.L).
   *
   * O Drone e o Leviatã. A recusa é o TECTO da sala - doze invocações ao mesmo
   * tempo - e não uma condição de alvo: as duas cartas fazem sentido num campo
   * vazio, e recusá-las por não haver ninguém seria inventar uma regra.
   */
  private planSummon(
    ship: PvpParticipantShipState,
    card: CardAbilityState,
    balance: CardBalance,
  ): (() => void) | null {
    if (this.summons.length >= PVP_MAX_SUMMONS) {
      return null;
    }
    const kind: PvpSummonKind = card.definitionId === 'card_leviathan' ? 'leviathan' : 'drone';
    return () => {
      const health =
        kind === 'drone'
          ? (ship.profile.maxHealthMilli * BigInt(PVP_DRONE_HEALTH_BPS)) / 10_000n
          : ship.profile.maxHealthMilli;
      this.summons.push({
        id: this.nextSummonId,
        kind,
        ownerParticipantId: ship.participantId,
        ownerTeam: ship.team,
        /*
         * Nasce à FRENTE de quem a invocou, e não em cima dele: um drone que
         * nascesse no centro do casco ficaria preso a empurrá-lo no primeiro
         * tique, e um Leviatã embarcaria o dono antes de ele decidir.
         */
        x: ship.x + divTrunc(ship.aimX * pvpHullRadiusMu(ship.profile) * 3, VECTOR_SCALE),
        y: ship.y + divTrunc(ship.aimY * pvpHullRadiusMu(ship.profile) * 3, VECTOR_SCALE),
        aimX: ship.aimX,
        aimY: ship.aimY,
        healthMilli: health,
        maxHealthMilli: health,
        lifeMs: kind === 'leviathan' ? PVP_LEVIATHAN_IDLE_MS : balance.durationMs,
        attackAccumulatorMs: 0,
        pilotParticipantId: null,
        boardedRemainingMs: 0,
      });
      this.nextSummonId += 1;
    };
  }

  /** As invocações a existir, para o frame e para as provas. */
  summonList(): readonly PvpSummonState[] {
    return this.summons;
  }

  /**
   * **O que as cartas deste casco estão a DESENHAR** (gate V28H.L).
   *
   * Uma lista de letras, e não uma bandeira por carta: as visíveis já são três
   * e vão ser mais, e um campo por cada uma seria um bump de protocolo por
   * habilidade escrita.
   *
   * O que sai daqui é público - um círculo a girar, uma serra a cortar e um
   * feixe a arder são coisas que quem está do outro lado tem de ver. E desde o
   * gate V28H.O sai também **quanto** da carga já foi cumprido, que é a
   * excepção deliberada: a Lança prende quem a carrega, e o contrato aprovado
   * diz que o adversário vê a nave presa e tem tempo para sair da linha.
   */
  cardVisualStateOf(participantId: PvpParticipantId): PvpCardVisualState {
    const ship = this.roster.get(participantId);
    if (ship === undefined) {
      return { letters: [], chargePercent: null, beamPercent: null };
    }
    /*
     * Delegada, e não escrita aqui.
     *
     * Esta decisão esteve duplicada - uma cópia aqui, outra em `team-wire.ts` -
     * e as duas eram idênticas por acidente. É assim que duas metades da mesma
     * sala passam a discordar sobre o que está a acontecer.
     */
    return pvpCardVisualStateOf(ship.cards, ship.cardEffects);
  }

  /**
   * **Um tique de todas as invocações** (gate V28H.L).
   *
   * A ordem é: envelhecer, largar as que acabaram, e só depois agir. Uma
   * entidade que agisse no tique em que morre disparava do além.
   *
   * **Uma invocação morre com quem a invocou.** Um dono morto, desligado ou
   * ausente leva-a com ele no mesmo tique - a regra mais simples que é sempre
   * verdadeira, e a única que não deixa uma entidade órfã numa instantânea.
   */
  private tickSummons(): void {
    const survivors: PvpSummonState[] = [];
    for (const summon of this.summons) {
      const owner = this.roster.get(summon.ownerParticipantId);
      const orphaned = owner === undefined || owner.healthMilli <= 0n || !owner.connected;
      if (orphaned || this.outcome !== null) {
        this.endSummon(summon, owner ?? null);
        continue;
      }
      if (pvpAgeSummon(summon, PVP_TICK_MS)) {
        this.endSummon(summon, owner);
        continue;
      }
      this.actSummon(summon, owner);
      survivors.push(summon);
    }
    this.summons = survivors;
  }

  /**
   * O que uma invocação faz quando sai da arena.
   *
   * O Drone **explode**: o kamikaze é o fim dele, aconteça ele por tempo, por
   * vida ou pela morte do dono. É dano de CARTA - origem `card` - e por isso
   * não activa joia nenhuma, que é literalmente o contrato do catálogo: "a
   * colisão kamikaze não ativa joias".
   *
   * O Leviatã **larga o piloto** onde estava, e mais nada.
   */
  private endSummon(summon: PvpSummonState, owner: PvpParticipantShipState | null): void {
    if (summon.pilotParticipantId !== null) {
      const pilot = this.roster.get(summon.pilotParticipantId);
      if (pilot !== undefined) {
        pilot.x = summon.x;
        pilot.y = summon.y;
      }
      summon.pilotParticipantId = null;
    }
    if (summon.kind !== 'drone' || owner === null) {
      return;
    }
    const damageMilli =
      (pvpShotDamageMilli(owner.profile, 10_000) * BigInt(PVP_DRONE_KAMIKAZE_BPS)) / 10_000n;
    const radius = Math.trunc(
      (pvpHullRadiusMu(owner.profile) * PVP_DRONE_KAMIKAZE_REACH_BPS) / 10_000,
    );
    const hit: PvpParticipantId[] = [];
    for (const victim of this.roster.enemiesOf(summon.ownerTeam)) {
      if (victim.healthMilli <= 0n) {
        continue;
      }
      const reach = radius + pvpHullRadiusMu(victim.profile);
      if (distanceSquared(summon.x, summon.y, victim.x, victim.y) > reach * reach) {
        continue;
      }
      if (this.applyDamage(victim, damageMilli, 'card', summon.x, summon.y, owner, null) > 0n) {
        hit.push(victim.participantId);
      }
    }
    this.events.push({
      kind: 'blast',
      effectId: ATOMIC_BLAST_EFFECT,
      participantId: owner.participantId,
      team: owner.team,
      /* Negativo: o id de uma INVOCAÇÃO, e nunca o de um projétil que existiu. */
      projectileId: -summon.id,
      tick: this.currentTick,
      x: summon.x,
      y: summon.y,
      radius,
      hit,
    });
  }

  /**
   * O que uma invocação faz enquanto existe.
   *
   * O **Drone** caça: anda para o inimigo escolhível mais próximo e dispara ao
   * seu ritmo. Os disparos PRIMÁRIOS dele nascem com origem `temporary_cannon`
   * e activam as joias de quem o invocou - é o contrato do catálogo (gate
   * V28H.B), e é a metade positiva da mesma frase cuja metade negativa proíbe o
   * kamikaze de activar seja o que for.
   *
   * O **Leviatã** espera. Ele não persegue e não dispara sozinho: sem piloto é
   * uma estrutura, e com piloto é o piloto que decide.
   */
  private actSummon(summon: PvpSummonState, owner: PvpParticipantShipState): void {
    if (summon.kind === 'leviathan') {
      this.tickLeviathan(summon);
      return;
    }
    const balance = cardBalance('card_attack_drone');
    if (balance === null) {
      return;
    }
    const target = this.nearestSummonTarget(summon);
    if (target !== null) {
      const heading = normalise(target.x - summon.x, target.y - summon.y);
      if (heading !== null) {
        summon.aimX = heading.x;
        summon.aimY = heading.y;
        /* Anda ao ritmo de um casco, e pára nas paredes pelo mesmo recorte. */
        const step = pvpTickTravelMu(10_000);
        summon.x = clamp(
          summon.x + divTrunc(heading.x * step, VECTOR_SCALE),
          0,
          PVP_ARENA_WIDTH_MU,
        );
        summon.y = clamp(
          summon.y + divTrunc(heading.y * step, VECTOR_SCALE),
          0,
          PVP_ARENA_HEIGHT_MU,
        );
      }
    }
    summon.attackAccumulatorMs += PVP_TICK_MS;
    if (summon.attackAccumulatorMs < balance.hitIntervalMs || target === null) {
      return;
    }
    summon.attackAccumulatorMs -= balance.hitIntervalMs;
    this.fireFromSummon(summon, owner, balance.damageBps, 1);
  }

  /**
   * **O Leviatã Aéreo, por um tique** (gate V28H.L).
   *
   * ## Sem piloto: uma estrutura à espera
   *
   * Ele não persegue e não dispara. Qualquer nave que **encoste** entra - "a
   * entrada física da nave" é uma decisão aprovada, e encostar é a metade dela
   * que a simulação consegue afirmar; as entradas laterais e as setas animadas
   * são desenho e vivem na cena.
   *
   * **Qualquer nave**, e não só quem o invocou: "se outro jogador entrar, valem
   * as joias desse piloto" é literal no contrato do catálogo, e uma estrutura
   * que só o dono pudesse pilotar não teria essa frase.
   *
   * ## Com piloto: treze segundos, e o piloto decide
   *
   * **APROVADO: destruição automática 13 s após a entrada.** A partir da
   * entrada é `boardedRemainingMs` que manda - "aconteça o que acontecer"
   * inclui o tempo que ele ainda tinha por gastar.
   *
   * O casco do piloto viaja COM ele e fica inalvejável: ele está lá dentro. Os
   * seis canhões disparam ao ritmo da carta, com o dano e as joias do PILOTO -
   * outra vez o contrato, e outra vez literal.
   */
  private tickLeviathan(summon: PvpSummonState): void {
    const balance = cardBalance('card_leviathan');
    if (balance === null) {
      return;
    }
    if (summon.pilotParticipantId === null) {
      this.boardLeviathan(summon);
      return;
    }
    const pilot = this.roster.get(summon.pilotParticipantId);
    if (pilot === undefined || pilot.healthMilli <= 0n || !pilot.connected) {
      /*
       * Um piloto que morre lá dentro larga os comandos, e o Leviatã continua
       * os treze segundos até ao fim - "aconteça o que acontecer". Ele não
       * volta a aceitar ninguém: a entrada aconteceu, e o relógio dela corre.
       */
      summon.pilotParticipantId = null;
      return;
    }
    /*
     * **O piloto conduz.** As teclas dele movem a estrutura, e o casco vai
     * junto: ele está lá dentro, e desenhar as duas coisas em sítios diferentes
     * seria desenhar uma mentira.
     */
    const step = pvpTickTravelMu(this.moveSpeedOf(pilot));
    const direction = movementDirection(Math.sign(pilot.moveX), Math.sign(pilot.moveY));
    summon.x = clamp(summon.x + divTrunc(direction.x * step, VECTOR_SCALE), 0, PVP_ARENA_WIDTH_MU);
    summon.y = clamp(summon.y + divTrunc(direction.y * step, VECTOR_SCALE), 0, PVP_ARENA_HEIGHT_MU);
    summon.aimX = pilot.aimX;
    summon.aimY = pilot.aimY;
    pilot.x = summon.x;
    pilot.y = summon.y;

    summon.attackAccumulatorMs += PVP_TICK_MS;
    const interval = this.leviathanFireIntervalMs(pilot, balance.hitIntervalMs);
    if (summon.attackAccumulatorMs < interval) {
      return;
    }
    summon.attackAccumulatorMs -= interval;
    if (!pilot.firing) {
      return;
    }
    /* Os SEIS canhões, com o dano e as joias de quem pilota. */
    this.fireFromSummon(summon, pilot, balance.damageBps, balance.count);
  }

  /**
   * **A que ritmo os seis canhões disparam** (gate V28H.X).
   *
   * Oito salvas por segundo - o número da carta - **ou a cadência do PILOTO, se
   * ela for mais rápida**. É literal na decisão do dono, e a razão é clara: a
   * estrutura é uma arma nas mãos de alguém, e quem treinou a própria nave para
   * disparar depressa não a deve perder ao entrar nela.
   *
   * A cadência do piloto sai da MESMA função que os canhões dele usam -
   * `fireIntervalOf` - portanto já traz as joias e o Curto-Circuito com ela: um
   * piloto em curto-circuito conduz um helicóptero em curto-circuito.
   *
   * O chão é um tique: o acumulador anda de `PVP_TICK_MS` em `PVP_TICK_MS`, e um
   * intervalo menor do que isso não seria uma cadência mais alta - seria um
   * número que a simulação não consegue distinguir do anterior.
   */
  private leviathanFireIntervalMs(ship: PvpParticipantShipState, cardMs: number): number {
    /* De mili-tiques para milissegundos: mil mili-tiques são um tique. */
    const pilotMs = Math.trunc(
      (this.fireIntervalOf(ship) * PVP_TICK_MS) / PVP_MILLI_TICKS_PER_TICK,
    );
    return Math.max(PVP_TICK_MS, Math.min(cardMs, pilotMs));
  }

  /**
   * **Quem PEDIU para pilotar, e está a tocar, entra** (gate V28H.X).
   *
   * ## O que mudou, e porquê
   *
   * Isto embarcava quem encostasse, sozinho. Fazia da estrutura uma armadilha:
   * passar ao lado do Leviatã de outra pessoa prendia lá dentro treze segundos
   * sem ter pedido nada, e a nave desaparecia do sítio onde o jogador a julgava.
   *
   * Agora é preciso as DUAS coisas - estar a tocar, e ter carregado no botão -
   * e a ordem é consumida na tentativa, com ou sem sucesso: um pedido feito
   * longe da estrutura não fica guardado à espera de a apanhar mais tarde.
   *
   * Determinista na mesma: se dois cascos pedirem no mesmo tique, entra o
   * primeiro da ordem canónica, e nunca o que calhou ser iterado primeiro.
   */
  private boardLeviathan(summon: PvpSummonState): void {
    for (const ship of this.roster.all()) {
      if (ship.healthMilli <= 0n || !ship.connected || ship.pendingBoard === null) {
        continue;
      }
      /* Gasta o pedido AQUI: ele vale este tique, tenha ele pegado ou não. */
      ship.pendingBoard = null;
      const reach = Math.trunc((pvpHullRadiusMu(ship.profile) * PVP_LEVIATHAN_ENTRY_BPS) / 10_000);
      if (distanceSquared(summon.x, summon.y, ship.x, ship.y) > reach * reach) {
        continue;
      }
      summon.pilotParticipantId = ship.participantId;
      /* **APROVADO: treze segundos a contar da entrada.** */
      summon.boardedRemainingMs = cardBalance('card_leviathan')?.durationMs ?? 0;
      return;
    }
  }

  /**
   * Um alvo para uma invocação: o inimigo vivo e ESCOLHÍVEL mais próximo.
   *
   * A mesma regra das cartas com alvo, e a mesma razão: um drone escolhe, e o
   * Bastião e o Manto tiram quem os tem da lista de quem se pode escolher.
   */
  private nearestSummonTarget(summon: PvpSummonState): PvpParticipantShipState | null {
    let best: PvpParticipantShipState | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const enemy of this.roster.enemiesOf(summon.ownerTeam)) {
      if (enemy.healthMilli <= 0n || !cardIsSelectable(enemy.cardEffects)) {
        continue;
      }
      const distance = distanceSquared(summon.x, summon.y, enemy.x, enemy.y);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = enemy;
      }
    }
    return best;
  }

  /**
   * Uma invocação dispara, com o dano e as JOIAS de quem a invocou.
   *
   * Origem `temporary_cannon`: "só os ataques primários dessas entidades podem
   * iniciar a loteria de joias" (gate V28H.B). O que estes tiros gerarem nasce
   * `jewel`, que não origina nada, e a recursão fecha em um.
   */
  private fireFromSummon(
    summon: PvpSummonState,
    owner: PvpParticipantShipState,
    damageBps: number,
    barrels: number,
  ): void {
    const damageMilli =
      (pvpShotDamageMilli(
        owner.profile,
        owner.effects.doubleDamageTicks > 0 ? PVP_RUNE_DAMAGE_BPS : 10_000,
      ) *
        BigInt(damageBps)) /
      10_000n;
    if (damageMilli <= 0n) {
      return;
    }
    const velocity = scaleDirection(
      { x: summon.aimX, y: summon.aimY },
      PVP_PROJECTILE_SPEED_MU_PER_TICK,
    );
    /*
     * **Uma boca por cano** (gate V28H.X), e não seis tiros do mesmo ponto.
     *
     * "Seis canhões" é literal no contrato do Leviatã, e a sala cumpria o número
     * sem cumprir os canhões: seis projécteis exactamente sobrepostos leem-se
     * como um, e o que se via era uma estrutura enorme a cuspir um fio de tiros.
     *
     * A geometria é a mesma que a cena desenha - `pvpLeviathanMuzzles` - e o
     * Drone continua a ter uma boca só, à frente do corpo, que é o que ele
     * sempre teve.
     */
    const muzzles =
      summon.kind === 'leviathan'
        ? pvpLeviathanMuzzles(summon, pvpLeviathanBodyRadiusMu(pvpHullRadiusMu(owner.profile)))
        : [
            {
              x: summon.x + divTrunc(summon.aimX * PVP_SUMMON_MUZZLE_MU, VECTOR_SCALE),
              y: summon.y + divTrunc(summon.aimY * PVP_SUMMON_MUZZLE_MU, VECTOR_SCALE),
            },
          ];
    for (let barrel = 0; barrel < barrels; barrel += 1) {
      if (this.shots.length >= PVP_MAX_PROJECTILES) {
        return;
      }
      /*
       * Se houver menos bocas do que canos - um Drone com mais de um - elas
       * repetem-se em vez de faltarem. Nunca acontece hoje, e uma lista vazia
       * aqui seria um tiro sem sítio de onde sair.
       */
      const muzzle = muzzles[barrel % muzzles.length] ?? {
        x: summon.x,
        y: summon.y,
      };
      this.shots.push({
        id: this.nextProjectileId,
        ownerParticipantId: owner.participantId,
        ownerTeam: summon.ownerTeam,
        /* De QUAL estrutura ele saiu: a cena desenha as duas de outra maneira. */
        fromSummon: summon.kind,
        damageMilli,
        /* A rolagem de joia do DONO, uma por projétil, como nos Super Canhões. */
        jewelProc: this.rollOutgoingProc(owner, 'temporary_cannon'),
        atomicBomb: null,
        cardBlast: null,
        homingTargetId: null,
        /* Nasce na boca do cano, e afastado do corpo da invocação. */
        x: muzzle.x,
        y: muzzle.y,
        vx: velocity.x,
        vy: velocity.y,
        life: PVP_PROJECTILE_LIFETIME_TICKS,
        reflected: false,
      });
      this.nextProjectileId += 1;
    }
  }

  /** Um temporizador sobre quem lançou. Nunca falha: o próprio casco está ali. */
  private planSelfBuff(ship: PvpParticipantShipState, balance: CardBalance): () => void {
    return () => {
      const slot =
        balance.buff === 'move_speed'
          ? ship.cardEffects.haste
          : balance.buff === 'untargetable'
            ? ship.cardEffects.untargetable
            : ship.cardEffects.cloak;
      applyCardTimer(slot, balance.durationMs, balance.magnitudeBps);
    };
  }

  /**
   * Um temporizador sobre UM inimigo, escolhido pelo servidor.
   *
   * `null` quando não há ninguém a quem aplicar - e é aí que a recusa poupa o
   * arrefecimento.
   */
  private planTargetDebuff(
    ship: PvpParticipantShipState,
    balance: CardBalance,
  ): (() => void) | null {
    /*
     * **Quem LANÇA não tem alcance de escolha** (gate V28H.W).
     *
     * A mesma constante e a mesma razão do Catolé: um dardo que persegue só
     * precisa de que exista alguém, e não de que esse alguém esteja perto - o
     * alcance dele é o do projéctil, e o projéctil vive até morrer numa parede,
     * num casco ou no fim do tempo de vida.
     *
     * A Marca da Caveira continua presa ao alcance da tabela: ela TOCA, e tocar
     * tem distância. O Curto-Circuito deixou de estar (gate V28H.X) - ele
     * dispara, e passou a ser o terceiro corpo desta família.
     */
    const target = this.nearestCardTarget(
      ship,
      balance.bolt === null ? balance.reachBps : PVP_CARD_UNLIMITED_REACH_BPS,
    );
    if (target === null) {
      return null;
    }
    /*
     * **As duas cartas de gelo LANÇAM** (gate V28H.W), e não aplicam.
     *
     * "Lança uma esfera de gelo brilhante e teleguiada" e "lança uma estrela de
     * gelo teleguiada": lançar quer dizer que alguma coisa atravessa a arena e
     * pode falhar - o alvo pode fugir, morrer ou pôr uma parede pelo meio antes
     * de ela chegar. Aplicar no instante da ordem seria a carta a acertar
     * sempre, que é outra carta.
     *
     * O Curto-Circuito e a Marca da Caveira continuam instantâneos: eles não
     * têm `bolt` na tabela, e nada aqui os obriga a mudar.
     */
    if (balance.bolt !== null) {
      const bolt = balance.bolt;
      return () => {
        this.launchDebuffBolt(ship, bolt, target);
      };
    }
    return () => {
      applyCardTimer(this.debuffSlotOf(target, balance), balance.durationMs, balance.magnitudeBps);
    };
  }

  /**
   * **Um dardo de gelo, teleguiado ao alvo que a sala escolheu** (V28H.W).
   *
   * Ele reaproveita o projéctil de carta - o mesmo que a Esfera Ígnea e o Catolé
   * usam - e não uma lista nova, porque é literalmente a mesma coisa: um corpo
   * que a sala move, que colide com o que colide, e que morre quando chega. O
   * que muda é o que ele faz ao chegar, e isso está em `detonateCardBlast`.
   *
   * Não leva dano nem raio: `damageMilli` e `radiusMu` a zero dizem-no. E não
   * leva a duração sorteada, e é deliberado - ela é decidida no IMPACTO, a
   * partir do id deste projéctil, portanto não precisa de viajar na instantânea
   * nem de ser recalculada de outra maneira na re-simulação.
   */
  private launchDebuffBolt(
    ship: PvpParticipantShipState,
    kind: 'ice_sphere' | 'ice_star' | 'shock_bolt',
    target: PvpParticipantShipState,
  ): void {
    /*
     * **Ele sai na direcção do ALVO, e não na da mira** (gate V28H.W).
     *
     * A carta escolhe o alvo - é a sala que o escolhe, e o dono nunca aponta
     * para ele - portanto sair pela mira era mandar o dardo para um sítio que
     * ninguém pediu. Medido na bancada: com o rato apontado para cima, o dardo
     * saía para cima, curvava devagar e morria numa pilastra antes de chegar a
     * meio caminho. Do outro lado não aparecia nada, e do lado de quem lançou
     * aparecia uma coisa a ir-se embora.
     *
     * A perseguição continua a existir e continua a ser dela que depende o
     * acerto: o alvo move-se depois disto. O que muda é o primeiro tique.
     */
    /*
     * NORMALIZADO, e não o delta cru: `scaleDirection` recebe um vector já em
     * unidades de `VECTOR_SCALE` e multiplica-o pela distância deste tique. O
     * delta entre dois cascos vale milhões, e passá-lo aqui deu ao dardo
     * milhares de vezes a velocidade pedida - medido: quatro milhões de mu por
     * tique, a arena atravessada em três, e o dardo a rebentar no limite do mapa
     * antes de o adversário o ver nascer.
     */
    const aim = normalise(target.x - ship.x, target.y - ship.y) ?? {
      x: ship.aimX,
      y: ship.aimY,
    };
    const velocity = scaleDirection(
      aim,
      Math.trunc((PVP_PROJECTILE_SPEED_MU_PER_TICK * PVP_ICE_BOLT_SPEED_BPS) / 10_000),
    );
    this.shots.push({
      id: this.nextProjectileId,
      ownerParticipantId: ship.participantId,
      ownerTeam: ship.team,
      damageMilli: 0n,
      jewelProc: null,
      atomicBomb: null,
      cardBlast: { kind, radiusMu: 0, damageMilli: 0n },
      homingTargetId: target.participantId,
      x: ship.x,
      y: ship.y,
      vx: velocity.x,
      vy: velocity.y,
      life: PVP_CARD_PROJECTILE_LIFETIME_TICKS,
      reflected: false,
    });
    this.nextProjectileId += 1;
  }

  /**
   * O mesmo temporizador sobre TODOS os inimigos vivos, de uma vez.
   *
   * Sem alcance: o Malware é declarado sobre "todos os inimigos", e um alcance
   * inventado aqui seria uma decisão que ninguém tomou. Aplica-se a quem estiver
   * vivo no instante do lançamento - uma aura que perseguisse quem chegasse
   * depois seria outra carta.
   *
   * **O Bastião não protege disto**, e é deliberado: ele impede ser ESCOLHIDO, e
   * uma carta que não escolhe ninguém não tem por onde ser bloqueada.
   */
  private planAreaDebuff(ship: PvpParticipantShipState, balance: CardBalance): (() => void) | null {
    const victims = this.roster.enemiesOf(ship.team).filter((enemy) => enemy.healthMilli > 0n);
    if (victims.length === 0) {
      return null;
    }
    return () => {
      for (const victim of victims) {
        applyCardTimer(
          this.debuffSlotOf(victim, balance),
          balance.durationMs,
          balance.magnitudeBps,
        );
      }
    };
  }

  /**
   * Um deslocamento na direcção da mira.
   *
   * Reaproveita o estado de EMPURRÃO, e é uma escolha e não uma economia: um
   * empurrão já é "uma distância total repartida pelos tiques, entregue ao mesmo
   * `pvpAdvanceShip` que o impulsionador usa", e é literalmente o que um arranque
   * é. As três propriedades que ele traz são as que se querem - as paredes e os
   * cascos travam-no, a velocidade não o altera, e bater não devolve o controlo
   * mais cedo.
   *
   * `null` quando o casco não tem direcção nenhuma, o que não acontece num duelo
   * a decorrer mas é a resposta certa se acontecer.
   */
  private planDash(ship: PvpParticipantShipState, balance: CardBalance): (() => void) | null {
    /*
     * **Para onde o jogador está a ANDAR** (gate V28H.V), e não para onde mira.
     *
     * Era a mira, e o dono viu o resultado: "não sei se ele está a avançar para
     * a direcção que a nave está virada ou se está a ir na direcção que o canhão
     * está virado". Eram as duas coisas ao mesmo tempo, porque neste jogo o
     * canhão segue o rato e o casco segue o movimento - e a carta seguia o rato.
     *
     * "A direcção é baseada na movimentação em WASD", e é a única das três que
     * o jogador escolhe com as mãos enquanto avança.
     *
     * `movementDirection` e não uma normalização qualquer: é a MESMA função que
     * o passo a pé usa, na mesma tabela de inteiros, com o mesmo tratamento das
     * diagonais. Uma segunda expressão aqui daria um avanço num ângulo em que
     * ninguém anda.
     */
    const held = movementDirection(ship.moveX, ship.moveY);
    /*
     * **Parado não avança**, e a ordem é recusada em vez de inventar um rumo.
     *
     * Recusada quer dizer recusada por inteiro: quem chama não liga a carta e
     * não cobra arrefecimento nenhum. A alternativa - avançar na última direcção
     * conhecida - seria a carta a decidir por um jogador que não decidiu.
     */
    if (held.x === 0 && held.y === 0) {
      return null;
    }
    const direction = normalise(held.x, held.y);
    if (direction === null) {
      return null;
    }
    const distance = Math.trunc((pvpHullRadiusMu(ship.profile) * balance.reachBps) / 10_000);
    const ticks = Math.max(1, Math.round(balance.durationMs / PVP_TICK_MS));
    return () => {
      ship.knockback = {
        dirX: direction.x,
        dirY: direction.y,
        remainingMu: distance,
        ticks,
      };
    };
  }

  /**
   * **A velocidade de movimento deste casco, num sítio só** (gate V28H.L).
   *
   * Ela era lida em três pontos - o passo do jogador, o empurrão e a validação
   * do teletransporte - e as três liam a mesma expressão inline. Duas cartas
   * passaram a mexer nela (a Sobremarcha, a Lentidão) e o Congelamento passou a
   * anulá-la, portanto três cópias eram três sítios para uma delas ficar para
   * trás. É o mesmo argumento que levou `pvpAdvanceShip` a ser partilhada com o
   * cliente: uma segunda cópia de uma expressão é como uma previsão passa a
   * integrar a uma velocidade que a sala não usa.
   *
   * A ordem é perfil, runa, cartas - e as cartas por último de propósito: o
   * bónus e a penalidade delas são sobre a velocidade EM VIGOR, e não sobre a
   * de catálogo.
   */
  private moveSpeedOf(ship: PvpParticipantShipState): number {
    const withRune = pvpEffectiveSpeedBps(
      ship.profile,
      ship.effects.speedTicks > 0 ? PVP_RUNE_SPEED_BPS : null,
    );
    const withCards = cardMoveSpeedBps(ship.cardEffects, withRune);
    /*
     * **A carga da Lança Solar PRENDE o movimento** (ADR 0065).
     *
     * A quarta das cinco restrições, e a que torna a excepção ao laser justa:
     * "enquanto carrega, a nave fica sem movimento e continua a poder mirar".
     * Um laser que atinge no instante em que existe só é justo se existir
     * depois de um aviso, e a imobilidade É o aviso - o adversário vê a nave
     * presa e tem o tempo da carga para sair da linha.
     *
     * Mirar continua a valer, e é por isso que só a VELOCIDADE vai a zero: uma
     * carta que fixasse a direcção no início seria uma carta que só acerta em
     * quem está parado.
     *
     * Zero e não uma redução: a descrição aprovada diz "não se mover".
     */
    return this.isChargeLocked(ship) ? 0 : withCards;
  }

  /** Alguma carta a carregar está a prender o movimento deste casco? */
  private isChargeLocked(ship: PvpParticipantShipState): boolean {
    return ship.cards.some(
      (card) =>
        cardAbilityIsCharging(card) &&
        (cardBalance(card.definitionId)?.chargeLocksMovement ?? false),
    );
  }

  /**
   * **O intervalo entre duas salvas deste casco** (gate V28H.L).
   *
   * O perfil e as joias dizem qual é; o Curto-Circuito reduz a CADÊNCIA, e uma
   * cadência menor é um intervalo maior - portanto a redução entra como uma
   * divisão e não como uma subtracção. A 40% de redução o intervalo vai a
   * 10 000/6 000 do que era, ou seja aumenta dois terços.
   *
   * Inteiro, com uma truncagem única, e a favor do jogador: truncar o intervalo
   * para baixo dá-lhe a fracção de tique em vez de lha tirar.
   */
  private fireIntervalOf(ship: PvpParticipantShipState): number {
    const base = pvpFireIntervalMilliTicks(ship.profile);
    const speedBps = cardAttackSpeedBps(ship.cardEffects, 10_000);
    if (speedBps >= 10_000 || speedBps <= 0) {
      /*
       * `<= 0` é o casco congelado, e ele nem chega aqui - `fire` recusa antes.
       * Devolver a base em vez de dividir por zero é a resposta segura para o
       * dia em que alguém chame isto noutro sítio.
       */
      return base;
    }
    return Math.trunc((base * 10_000) / speedBps);
  }

  /** A ranhura que esta carta ocupa no casco que a recebe. */
  private debuffSlotOf(target: PvpParticipantShipState, balance: CardBalance): CardTimer {
    switch (balance.debuff) {
      case 'move_speed':
        return target.cardEffects.slow;
      case 'attack_speed':
        return target.cardEffects.attackBreak;
      case 'damage_dealt':
        return target.cardEffects.damageBreak;
      case 'healing_received':
        return target.cardEffects.malware;
      default:
        return target.cardEffects.frozen;
    }
  }

  /**
   * **O inimigo vivo e alvejável mais próximo, dentro do alcance.**
   *
   * A regra de selecção das cartas com alvo, escrita uma vez. Determinista de
   * ponta a ponta: `enemiesOf` devolve a ordem canónica, a distância compara-se
   * ao quadrado em inteiros - sem raiz, sem vírgula - e um empate fica com o
   * primeiro da ordem canónica, que é sempre o mesmo.
   *
   * **Um casco inalvejável ou invisível não é escolhido**, e é aqui que o
   * Bastião e o Manto ganham o seu sentido: eles não param um projétil no ar,
   * param a ESCOLHA.
   */
  private nearestCardTarget(
    ship: PvpParticipantShipState,
    reachBps: number,
  ): PvpParticipantShipState | null {
    const reach = Math.trunc((pvpHullRadiusMu(ship.profile) * reachBps) / 10_000);
    const limit = reach * reach;
    let best: PvpParticipantShipState | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const enemy of this.roster.enemiesOf(ship.team)) {
      if (
        enemy.healthMilli <= 0n ||
        !cardIsSelectable(enemy.cardEffects) ||
        this.isPiloting(enemy.participantId)
      ) {
        continue;
      }
      const dx = enemy.x - ship.x;
      const dy = enemy.y - ship.y;
      const distance = dx * dx + dy * dy;
      if (distance > limit) {
        continue;
      }
      /* Estritamente menor: um empate fica com o primeiro da ordem canónica. */
      if (distance < bestDistance) {
        bestDistance = distance;
        best = enemy;
      }
    }
    return best;
  }

  /**
   * **Os relógios das cartas de um casco, por um tique** (gate V28H.J).
   *
   * 50 ms, que é o tique do duelo - e não os 16 ms da caça. A aritmética é a
   * mesma função (`ageCardAbility`); o que muda é o passo, porque o que muda é
   * o relógio da sala.
   *
   * Um casco MORTO larga tudo o que estava a correr, e é uma regra do gate:
   * "morte, abandono ou fim da partida encerram imediatamente habilidades
   * activas". O arrefecimento continua a correr - ele é do espaço, e não do
   * casco - mas a habilidade acaba no instante da morte.
   */
  private ageCards(ship: PvpParticipantShipState): CardAbilityState[] {
    const dead = ship.healthMilli <= 0n || !ship.connected;
    /*
     * As cargas que TERMINARAM neste tique (gate V28H.L).
     *
     * Um instante e não um estado: a esfera sai uma vez, e o feixe acende uma
     * vez. `chargeRemainingMs === 0` sozinho é verdade para sempre a partir daí,
     * portanto quem age tem de saber qual foi o tique.
     */
    const charged: CardAbilityState[] = [];
    for (const card of ship.cards) {
      if (dead && card.activeRemainingMs > 0) {
        card.activeRemainingMs = 0;
        card.chargeRemainingMs = 0;
        card.attackAccumulatorMs = 0;
      }
      const before = card.chargeRemainingMs;
      ageCardAbility(card, PVP_TICK_MS);
      /*
       * Um casco morto não lança o que estava a carregar: a linha acima pôs a
       * carga a zero SEM passar por aqui, e é deliberado - "morte, abandono ou
       * fim da partida encerram imediatamente habilidades activas".
       */
      if (!dead && cardAbilityChargeEnded(before, card.chargeRemainingMs)) {
        charged.push(card);
      }
    }
    /*
     * E as oito ranhuras (gate V28H.L), pelo mesmo passo e pela mesma regra: um
     * casco morto ou desligado larga tudo o que estava a correr sobre ele.
     *
     * Larga o que RECEBEU e não só o que lançou, e é deliberado: uma Lentidão
     * que sobrevivesse à morte estaria a correr sobre um destroço, e uma sala
     * que o ressuscitasse devolvia-lhe uma penalidade que a partida já tinha
     * esquecido - exactamente o argumento que o empurrão já usa.
     */
    if (dead) {
      clearCardEffects(ship.cardEffects);
      return charged;
    }
    ageCardEffects(ship.cardEffects, PVP_TICK_MS);
    return charged;
  }

  /**
   * **Os dois factos PÚBLICOS que as cartas de temporizador produzem**
   * (gate V28H.L).
   *
   * Congelado e inalvejável, e mais nada. Os dois mudam o que acontece a quem
   * está do outro lado - uma nave que parou de responder, uns tiros que
   * atravessam - portanto os dois lados têm de os ver, pela mesma razão que os
   * canhões temporários são públicos.
   *
   * Quanto FALTA de cada um não sai daqui: isso é informação de decisão e viaja
   * na metade privada da cadeira, com os arrefecimentos.
   */
  publicCardFlagsOf(participantId: PvpParticipantId): ReturnType<typeof pvpPublicCardFlags> {
    const ship = this.roster.get(participantId);
    /*
     * **Uma leitura só, partilhada com o 3x3** (gate V28H.X): `pvpPublicCardFlags`.
     *
     * Aqui estavam as seis expressões escritas à mão, e no fio da equipa estavam
     * as mesmas outra vez. Dois estados novos partiram a cópia - um dos lados
     * compilou sem eles - e é o defeito que este projecto já apanhou quatro
     * vezes na mesma fronteira.
     *
     * Um casco que já não existe não tem estado nenhum, e um objecto vazio é a
     * resposta honesta: quem morreu não está congelado.
     */
    return ship === undefined
      ? pvpPublicCardFlags(initialCardEffects())
      : pvpPublicCardFlags(ship.cardEffects);
  }

  /**
   * **Este casco está invisível para quem NÃO é da equipa dele?**
   *
   * A pergunta que a sala faz antes de escrever um casco num frame. Um casco
   * invisível é **omitido** do frame do adversário, e não marcado nele: uma
   * bandeira "estou invisível" com uma posição ao lado seria uma posição que um
   * cliente modificado desenhava à mesma, e o Manto deixaria de ser uma carta
   * para passar a ser uma sugestão.
   *
   * Os ALIADOS continuam a vê-lo. É uma decisão provisória e é de jogo: num 3x3
   * uma equipa que perdesse de vista um dos seus perderia a formação inteira
   * por causa da carta de um deles.
   */
  isCloakedFrom(participantId: PvpParticipantId, viewerTeam: PvpTeam): boolean {
    const ship = this.roster.get(participantId);
    if (ship === undefined || ship.team === viewerTeam) {
      return false;
    }
    return cardIsCloaked(ship.cardEffects);
  }

  /**
   * **Este casco está DENTRO de uma invocação?** (gate V28H.L).
   *
   * Um piloto de Leviatã está lá dentro: ele não pode ser atingido nem
   * escolhido, e a estrutura leva os tiros por ele. É a mesma pergunta que o
   * Bastião responde, por uma razão diferente e no mesmo sítio.
   */
  private isPiloting(participantId: PvpParticipantId): boolean {
    return this.summons.some((summon) => summon.pilotParticipantId === participantId);
  }

  /** Se este casco tem os dois canhões temporários montados AGORA. */
  hasSuperCannons(participantId: PvpParticipantId): boolean {
    const ship = this.roster.get(participantId);
    return ship === undefined ? false : shipHasSuperCannons(ship);
  }

  /** Os três espaços de UMA cadeira, para o HUD dela. Nunca de outra. */
  cardSlotsOf(participantId: PvpParticipantId): readonly CardAbilityState[] {
    const ship = this.roster.get(participantId);
    return ship === undefined ? [] : ship.cards;
  }

  /**
   * **Congela a vida máxima que este casco leva para a partida** (gate V28G.S).
   *
   * ## O número chega pronto, e é essa a lei
   *
   * `maxHealth` é a vida máxima FINAL da conta de quem se senta: a vida-base da
   * nave, os pontos alocados, a plana dos artefactos e do equipamento, e a soma
   * das percentagens - o Coração do Titã incluído - aplicada uma única vez. Foi
   * resolvida na emissão do bilhete, pela mesma função que a caça atravessa, e
   * viaja dentro do payload assinado.
   *
   * Esta simulação não sabe de onde ela veio e não tem por onde a recompor: não
   * há aqui um catálogo, um nível de joia, um ponto alocado nem um artefacto. É
   * o que torna a dupla aplicação impossível em vez de improvável.
   *
   * ## Porque escreve a vida corrente também
   *
   * Porque um casco por começar está CHEIO, e o teto acabou de mudar. Não é
   * cura: antes do primeiro tique a vida de uma nave é sempre o seu máximo -
   * `spawnShip` põe-a lá - e isto repõe a mesma afirmação depois de o teto ser
   * conhecido. Entrar no duelo com a vida cheia é a regra de produto: os dois
   * modos partilham o MÁXIMO e nunca a vida atual, e uma nave ferida na caça não
   * entra ferida no duelo.
   *
   * ## Recusa depois do primeiro tique, como as outras duas
   *
   * A partir daí há tiros no ar e uma barra desenhada contra um denominador. O
   * `snapshot-v3.ts` já diz porquê: "a ceiling a snapshot is validated against
   * has to be the ceiling the snapshot was written under".
   */
  freezeParticipantMaxHealth(participantId: PvpParticipantId, maxHealthMilli: bigint): void {
    if (this.currentTick > 0) {
      throw new RangeError(
        `a hull is frozen when its pilot sits down, not at tick ${this.currentTick}`,
      );
    }
    const ship = this.roster.require(participantId);
    const next: PvpCombatProfile = { ...ship.profile, maxHealthMilli };
    pvpAssertCombatProfile(next, `pvp hull for ${participantId}`);
    (ship as { profile: PvpCombatProfile }).profile = next;
    ship.healthMilli = next.maxHealthMilli;
  }

  /**
   * **Repõe um casco no estado em que nasceu**, para outra pessoa se sentar.
   *
   * Uma substituição é uma operação de lobby - o duelo recusa-a depois do
   * primeiro tique - e o que ela troca é a PESSOA. O casco que ficou é o da
   * pessoa que saiu: a contagem de corações dela, as joias dela, e o teto de
   * vida que essas joias compuseram. Deixá-lo ficar daria ao substituto uma nave
   * que ele não tem, e recusar o congelamento seguinte daria-lhe uma nave que
   * ninguém escolheu.
   *
   * Repõe o perfil por omissão e enche a vida. Depois disto o casco está no
   * estado exato em que `spawnShip` o deixou, e o bilhete do substituto pode
   * congelá-lo pela primeira vez.
   */
  resetParticipantHull(participantId: PvpParticipantId): void {
    if (this.currentTick > 0) {
      throw new RangeError(
        `a hull is reset before the first tick, not at tick ${this.currentTick}`,
      );
    }
    const ship = this.roster.require(participantId);
    (ship as { profile: PvpCombatProfile }).profile = pvpCopyCombatProfile(
      PVP_DEFAULT_COMBAT_PROFILE,
    );
    ship.healthMilli = PVP_DEFAULT_COMBAT_PROFILE.maxHealthMilli;
  }

  /** Every shot in flight, with its owner. */
  projectiles(): readonly PvpParticipantProjectileState[] {
    return this.shots;
  }

  /**
   * Os mísseis em voo, com o seu dono (gate V28G.L).
   *
   * Uma leitura própria e não uma lista misturada com os tiros: quem desenha
   * precisa de saber qual é qual, e deduzi-lo por um campo opcional seria a
   * dedução que a Bomba já ensinou a não fazer. A sala serve-se disto para os
   * descrever no fio.
   */
  missilesInFlight(): readonly PvpMissileState[] {
    return this.missiles;
  }

  /**
   * Os stacks de veneno ativos, em ordem de criação (gate V28G.M).
   *
   * A sala serve-se disto para os descrever no fio, e é a ÚNICA porta: uma cena
   * que contasse os seus próprios seis segundos discordaria da sala no instante
   * em que um frame chegasse tarde, e desenharia uma marca que já expirou.
   *
   * O que sai daqui é estado autoritativo e nada visual. Onde a marca fica, que
   * tamanho tem e quando pulsa são decisões do renderer, derivadas destes
   * números.
   */
  poisonStacks(): readonly PvpPoisonStackState[] {
    return this.poison;
  }

  /**
   * As marcas de sangramento ativas, em ordem de criação (gate V28G.N).
   *
   * A sala serve-se disto para as descrever no fio, e é a ÚNICA porta. O que sai
   * daqui é estado autoritativo e nada visual: quantas existem, de quem são e
   * sobre quem estão. Onde o corte fica, que tamanho tem e de que cor é são
   * decisões do renderer, derivadas destes números.
   *
   * A autoria SAI, ao contrário da do veneno, e a razão é mecânica antes de ser
   * visual: é ela que decide quem pode detonar, portanto é ela que decide o que
   * o botão de um jogador conta. Uma cena que a adivinhasse contaria as marcas
   * de um aliado.
   */
  bleedingMarks(): readonly PvpBleedingMarkState[] {
    return this.bleeding;
  }

  /**
   * Quantas marcas este casco tem espalhadas, somando todos os alvos.
   *
   * O número que o botão `DETONAR ×N` mostra, respondido pela AUTORIDADE e nunca
   * contado pelo cliente a partir de uma lista que ele pode ter recebido
   * incompleta. Zero quando não há nenhuma, que é exatamente quando o comando é
   * um `no-op` e o botão está desativado.
   */
  bleedingMarkCountOf(participantId: PvpParticipantId): number {
    let total = 0;
    for (const mark of this.bleeding) {
      if (mark.attackerParticipantId === participantId) {
        total += 1;
      }
    }
    return total;
  }

  /** The pads, for the room to describe to its clients. */
  runeList(): readonly PvpRuneState[] {
    return this.runes;
  }

  /**
   * Everything that decides this TEAM match's future, as plain data.
   *
   * ## What it shares with the running simulation: nothing
   *
   * Every object is rebuilt. The historic duel snapshot passed `effects`,
   * `teleportTo` by reference - `{ ...ship }` is a shallow copy -
   * so a room restored from one while the original still lived would age both
   * their timers twice. That behaviour is preserved for v2 because changing it
   * would change every duel ever recorded; v3 is a new format, so it is written
   * the way the old one should have been, and `snapshot-v3.test.ts` mutates each
   * side to prove the other does not move.
   *
   * ## Both rulesets, and why the duel joined
   *
   * It used to refuse a duel: a 1x1 was stored in the historic two-seat shape,
   * and emitting v3 from one would have been a format change nothing had asked
   * for. Protocol version 3 asked for it. A duel is now TOLD that its two hulls
   * may differ, and the historic shape has no field to write that down - so a
   * duel that persisted in it would lose the difference on the first eviction.
   *
   * The document names its own ruleset, which is how a decoder tells the two
   * apart without guessing, and `pvpCanonicalSeats` gives it the right number
   * of chairs. Nothing else about the format changed, and no version-4 document
   * written before this reads differently.
   */
  snapshotV3(): PvpSnapshotV3 {
    if (!PVP_SNAPSHOT_V3_RULESETS.includes(this.ruleset)) {
      throw new PvpLegacyDuelError(
        'snapshot_not_supported_for_ruleset',
        `version ${PVP_SNAPSHOT_V3_VERSION} describes ${PVP_SNAPSHOT_V3_RULESETS.join(' or ')}, not ${this.ruleset}`,
      );
    }
    return {
      version: PVP_SNAPSHOT_V3_VERSION,
      ruleset: this.ruleset,
      tick: this.currentTick,
      // Canonical order by construction: the roster iterates team then slot
      // whatever order it was built in, which is what makes two rooms with
      // identical futures serialise to identical bytes.
      participants: this.roster.all().map((ship) => shipToV3(ship)),
      projectiles: this.shots.map((projectile) => ({
        id: projectile.id,
        ownerParticipantId: projectile.ownerParticipantId,
        ownerTeam: projectile.ownerTeam,
        /* String decimal, nunca um número de JSON: quarenta dígitos não cabem. */
        damageMilli: projectile.damageMilli.toString(10),
        /* O proc congelado atravessa a serialização exatamente como o dano. */
        jewelProc: jewelProcToWire(projectile.jewelProc),
        /* E a bomba também: um projétil em voo atravessa uma reconexão inteiro. */
        atomicBomb: atomicBombToWire(projectile.atomicBomb),
        x: projectile.x,
        y: projectile.y,
        vx: projectile.vx,
        vy: projectile.vy,
        life: projectile.life,
        /* A LINHAGEM: sem ela, uma bomba refletida volta a ser intercetável. */
        reflected: projectile.reflected,
        /* Sem explosão de carta e sem perseguição: um tiro comum (gate V28H.L). */
        cardBlast: null,
        homingTargetId: null,
      })),
      nextProjectileId: this.nextProjectileId,
      runes: this.runes.map((rune) => ({
        pad: rune.pad,
        kind: rune.kind,
        readyAt: rune.readyAt,
      })),
      rng: this.rng.snapshot(),
      seed: this.seed.toString(10),
      procRng: Object.fromEntries(
        [...this.procRng.entries()].map(([participantId, stream]) => [
          participantId,
          stream.snapshot(),
        ]),
      ),
      /* O fluxo por salva retoma onde estava, como o dos procs (gate V28G.J). */
      bombRng: Object.fromEntries(
        [...this.bombRng.entries()].map(([participantId, stream]) => [
          participantId,
          stream.snapshot(),
        ]),
      ),
      /*
       * Os mísseis em voo, inteiros (gate V28G.L).
       *
       * Reconstruídos campo a campo e nunca adotados: um documento que
       * partilhasse a referência com a sala viva envelheceria com ela, que é o
       * defeito que a versão 3 existe para não repetir.
       */
      missiles: this.missiles.map((missile) => ({
        id: missile.id,
        ownerParticipantId: missile.ownerParticipantId,
        ownerTeam: missile.ownerTeam,
        /* String decimal, nunca um número de JSON: quarenta dígitos não cabem. */
        damageMilli: missile.damageMilli.toString(10),
        entropy: missile.entropy,
        targetParticipantId: missile.targetParticipantId,
        x: missile.x,
        y: missile.y,
        dirX: missile.dirX,
        dirY: missile.dirY,
        life: missile.life,
        /* A LINHAGEM: sem ela, um míssil refletido volta a ser intercetável. */
        reflected: missile.reflected,
      })),
      /* E os fluxos de alvo retomam onde estavam, como os outros dois. */
      missileRng: Object.fromEntries(
        [...this.missileRng.entries()].map(([participantId, stream]) => [
          participantId,
          stream.snapshot(),
        ]),
      ),
      /*
       * Os stacks de veneno, inteiros (gate V28G.M).
       *
       * Reconstruídos campo a campo e nunca adotados, como os mísseis. O total
       * atravessa como STRING decimal pela regra de sempre - ele deriva do
       * dano-base autoritativo, que não tem tecto - e `appliedAt` atravessa como
       * o TIQUE em que a aplicação aconteceu, que é o relógio deste modo.
       */
      poison: this.poison.map((stack) => ({
        id: stack.id,
        attackerParticipantId: stack.attackerParticipantId,
        targetParticipantId: stack.targetParticipantId,
        totalDamageMilli: stack.totalDamageMilli.toString(10),
        appliedAt: stack.appliedAt,
        ticksDone: stack.ticksDone,
        /* A LINHAGEM: sem ela, as seis parcelas voltam a ser elegíveis. */
        reflected: stack.reflected,
      })),
      nextPoisonId: this.nextPoisonId,
      /* E os fluxos do veneno retomam onde estavam, como os outros três. */
      poisonRng: Object.fromEntries(
        [...this.poisonRng.entries()].map(([participantId, stream]) => [
          participantId,
          stream.snapshot(),
        ]),
      ),
      /*
       * As marcas de sangramento, inteiras (gate V28G.N).
       *
       * Reconstruídas campo a campo e nunca adotadas, como os mísseis e os
       * stacks. O dano atravessa como STRING decimal pela regra de sempre - ele
       * deriva do dano-base autoritativo, que não tem tecto - e `appliedAt`
       * atravessa como o TIQUE em que a aplicação aconteceu.
       */
      bleeding: this.bleeding.map((mark) => ({
        id: mark.id,
        attackerParticipantId: mark.attackerParticipantId,
        targetParticipantId: mark.targetParticipantId,
        damageMilli: mark.damageMilli.toString(10),
        appliedAt: mark.appliedAt,
        /* A LINHAGEM: sem ela, a detonação seguinte volta a ser elegível. */
        reflected: mark.reflected,
      })),
      nextBleedingId: this.nextBleedingId,
      /* E os fluxos do sangramento retomam onde estavam, como os outros quatro. */
      bleedingRng: Object.fromEntries(
        [...this.bleedingRng.entries()].map(([participantId, stream]) => [
          participantId,
          stream.snapshot(),
        ]),
      ),
      /*
       * E os fluxos DEFENSIVOS retomam onde estavam, como os outros cinco
       * (gate V28G.O). Este é o primeiro cuja cadência é ditada pelo
       * ADVERSÁRIO, portanto perdê-lo desloca a defesa de um jogador em função
       * de quantas vezes o outro disparou.
       */
      /*
       * **As invocações** (versão 21, gate V28H.L).
       *
       * Elas têm posição, vida e relógio seus e podem ser atingidas, portanto
       * uma sala reaberta sem elas perdia entidades vivas - e um Leviatã com
       * piloto perdia o piloto lá dentro.
       */
      summons: this.summons.map((summon) => ({
        id: summon.id,
        kind: summon.kind,
        ownerParticipantId: summon.ownerParticipantId,
        ownerTeam: summon.ownerTeam,
        x: summon.x,
        y: summon.y,
        aimX: summon.aimX,
        aimY: summon.aimY,
        /* String decimal, como toda a vida: quarenta dígitos não cabem num número. */
        healthMilli: summon.healthMilli.toString(10),
        maxHealthMilli: summon.maxHealthMilli.toString(10),
        lifeMs: summon.lifeMs,
        attackAccumulatorMs: summon.attackAccumulatorMs,
        pilotParticipantId: summon.pilotParticipantId,
        boardedRemainingMs: summon.boardedRemainingMs,
      })),
      reflectRng: Object.fromEntries(
        [...this.reflectRng.entries()].map(([participantId, stream]) => [
          participantId,
          stream.snapshot(),
        ]),
      ),
      /*
       * Os relógios de contacto das serras, em ORDEM CANÓNICA (gate V28G.P).
       *
       * Ordenados por `(dono, lâmina, alvo)` e não pela ordem em que os
       * contactos aconteceram: a lista viva cresce por primeiro contacto, que
       * depende de onde as naves andaram, e duas re-simulações da mesma partida
       * podiam guardar a mesma informação por outra ordem. Uma sala tem uma
       * representação só, ou o digest compara duas verdades iguais e diz que
       * discordam.
       */
      saws: [...this.sawContacts]
        .sort(
          (left, right) =>
            (left.ownerParticipantId < right.ownerParticipantId
              ? -1
              : left.ownerParticipantId > right.ownerParticipantId
                ? 1
                : 0) ||
            left.bladeIndex - right.bladeIndex ||
            (left.targetParticipantId < right.targetParticipantId
              ? -1
              : left.targetParticipantId > right.targetParticipantId
                ? 1
                : 0),
        )
        .map((contact) => ({
          ownerParticipantId: contact.ownerParticipantId,
          bladeIndex: contact.bladeIndex,
          targetParticipantId: contact.targetParticipantId,
          lastContactTick: contact.lastContactTick,
        })),
      /* E os fluxos das serras retomam onde estavam, como os outros seis. */
      sawRng: Object.fromEntries(
        [...this.sawRng.entries()].map(([participantId, stream]) => [
          participantId,
          stream.snapshot(),
        ]),
      ),
      /* E os do Coração Vital, pela mesma razão exata (gate V28G.U). */
      vitalRng: Object.fromEntries(
        [...this.vitalRng.entries()].map(([participantId, stream]) => [
          participantId,
          stream.snapshot(),
        ]),
      ),
      result: this.outcome === null ? null : pvpTeamResultToV3(this.outcome),
    };
  }

  /** Takes the presentation events since the last drain. Never in the snapshot. */
  drainParticipantEvents(): readonly PvpParticipantEvent[] {
    const drained = this.events;
    this.events = [];
    return drained;
  }

  /**
   * Applies one participant's intent, or refuses it.
   *
   * Returns `true` only when the command moved that participant's applied
   * sequence forward. A repeat, a replay and anything out of order all return
   * `false` and change NOTHING - which is what makes a retry safe and a recorded
   * packet useless. The sequence is per participant, so no one player can ever
   * advance another's, ally or not.
   *
   * A participant this match does not have is refused outright: with six ships
   * an unknown id is a client naming somebody else's ship, and answering it at
   * all would be answering a question it had no right to ask.
   */
  applyParticipantCommand(participantId: PvpParticipantId, intent: PvpCommandIntent): boolean {
    const ship = this.roster.get(participantId);
    if (ship === undefined) {
      return false;
    }
    if (this.outcome !== null) {
      return false;
    }
    if (intent.seq <= ship.appliedSeq) {
      return false;
    }
    ship.appliedSeq = intent.seq;
    /*
     * A disconnected or dead ship's intent is not applied even if a frame
     * arrives. The room zeroes the intent on drop; honouring a late packet
     * afterwards would let a ship keep flying for the length of a network
     * buffer, and a wreck take a lap of the arena.
     */
    if (!ship.connected || ship.healthMilli <= 0n) {
      return false;
    }
    ship.moveX = Math.sign(intent.mx);
    ship.moveY = Math.sign(intent.my);
    ship.firing = intent.firing;
    /*
     * A teleport is LATCHED, never held.
     *
     * A player holding the key would otherwise spend a charge on the first
     * tick and then ask again on every one after it, and the room would be
     * answering a question nobody asked. The tick clears the flag whether or
     * not the move happened, so one press is one attempt.
     */
    if (intent.teleportTo) {
      ship.teleportTo = { x: intent.teleportTo.x, y: intent.teleportTo.y };
    }
    /*
     * **A ordem de DETONAR é latched pela mesma razão exata** (gate V28G.N).
     *
     * Um jogador que a mantivesse verdadeira em todos os frames rebentaria as
     * marcas uma vez e depois pediria detonações sem marcas, que são `no-op`
     * seguros. O tique apaga a bandeira tenha ou não havido marcas, portanto uma
     * pressão é uma tentativa.
     *
     * Só arma; não executa. Executar aqui faria a ordem de dois jogadores
     * depender da ordem de CHEGADA dos frames, e o §"não deixe o resultado
     * depender da chegada de clientes" existe por isso. Quem executa é o tique,
     * pela ordem do roster.
     */
    /*
     * A ORDEM de carta, armada como a detonação (gate V28H.J).
     *
     * A cadeira é a da ligação: `ship` já foi resolvido a partir dela, e não há
     * campo na intenção onde nomear outra pessoa. Uma segunda ordem no mesmo
     * frame substitui a primeira - um latch guarda uma, e a última é a que o
     * jogador quis.
     */
    if (intent.activateCard) {
      ship.pendingCard = intent.activateCard;
    }
    /* E a soltura, com o seu latch próprio: soltar não é ligar. */
    if (intent.releaseCard) {
      ship.pendingCardRelease = intent.releaseCard;
    }
    /* E a ordem de pilotar, com o dela: embarcar não é nenhuma das outras. */
    if (intent.boardSummon) {
      ship.pendingBoard = intent.boardSummon;
    }
    if (intent.detonateBleed === true) {
      ship.detonateBleed = true;
    }
    const aim = normalise(intent.ax, intent.ay);
    if (aim !== null) {
      // A zero vector keeps the previous direction. See `normalise`.
      ship.aimX = aim.x;
      ship.aimY = aim.y;
    }
    return true;
  }

  /**
   * Attaches or detaches a participant.
   *
   * Dropping zeroes the intent: a player who is not there is not holding a key
   * and not holding fire. The SHIP stays in the arena and stays shootable -
   * removing it would make disconnecting a defensive move.
   */
  setParticipantConnected(participantId: PvpParticipantId, connected: boolean): void {
    const ship = this.roster.require(participantId);
    ship.connected = connected;
    if (!connected) {
      ship.moveX = 0;
      ship.moveY = 0;
      ship.firing = false;
    }
  }

  /**
   * Takes one hull out of the fight WITHOUT deciding the match.
   *
   * ## Why elimination and the ending are two different things
   *
   * A duel only ever had one of them: with one ship a side, the ship falling
   * and the match ending were the same event, so `finishTeam` said everything
   * there was to say. With three a side they come apart - the first two
   * eliminations change who is shooting and nothing else - and a room that
   * could only express "the match is over" would have to end a 3x3 the moment
   * anybody's grace window expired.
   *
   * So this does exactly what a killing shot does and stops there: health to
   * zero, intent cleared, timers and charges dropped (a wreck that came back
   * holding a banked teleport would make dying a way to store one), and one
   * `death` event so the renderer shows the same explosion it shows for a shot.
   * Whether that ends anything is read afterwards, by whoever asked - and in
   * this build that is {@link PvpRoom}, which counts the teams still standing
   * and names `abandonment` as the reason rather than letting the next tick
   * call it a `death`.
   *
   * Returns whether anything changed. A hull that was already down is NOT
   * eliminated twice: a player who dies and then lets their grace window expire
   * is one loss, and a second event would draw a second explosion over a wreck
   * and, worse, count a second time towards the team being wiped.
   *
   * The shipped 1x1 never calls this. Its abandonment rule finishes the match
   * outright, exactly as it always has - see `duel-invariance.test.ts`.
   */
  eliminateParticipant(participantId: PvpParticipantId): boolean {
    const ship = this.roster.require(participantId);
    if (ship.healthMilli <= 0n) {
      return false;
    }
    ship.healthMilli = 0n;
    ship.moveX = 0;
    ship.moveY = 0;
    ship.firing = false;
    ship.teleportTo = null;
    /*
     * O resto vai com o casco (gate V28G.I).
     *
     * A mesma limpeza que um tiro mortal faz, e escrita aqui pela mesma razão
     * que o carregamento do teleporte já é: um destroço que voltasse a segurar
     * alguma coisa faria de morrer uma maneira de a guardar.
     */
    ship.lifestealCarryBps = 0;
    pvpClearEffects(ship.effects);
    /*
     * E o veneno vai com ele, pela mesma razão (gate V28G.M).
     *
     * Este é o SEGUNDO caminho pelo qual um casco morre - o primeiro é um golpe
     * que o leva a zero - e a regra do §"se o alvo morrer, todos os stacks
     * daquele alvo são removidos" é sobre o ALVO e não sobre como ele caiu. Um
     * casco eliminado por abandono que ficasse a carregar marcas seria um
     * destroço envenenado.
     */
    this.clearPoisonOf(participantId);
    /*
     * E o sangramento vai com ele nos DOIS sentidos (gate V28G.N).
     *
     * As marcas que ele CARREGAVA saem porque o alvo morreu, exatamente como as
     * de veneno saem. As que ele TINHA POSTO noutros saem porque o autor morreu
     * - e essa metade é própria desta joia: uma marca só rebenta por ordem do
     * autor, e uma que ninguém possa mandar rebentar é uma ameaça desenhada que
     * não existe.
     *
     * E a ordem em mãos vai com ele, como o teleporte carregado vai: um destroço
     * que guardasse uma detonação faria de morrer uma maneira de a bancar.
     */
    ship.detonateBleed = false;
    this.clearBleedingAround(participantId);
    this.events.push({
      kind: 'death',
      participantId: ship.participantId,
      team: ship.team,
      x: ship.x,
      y: ship.y,
    });
    return true;
  }

  /**
   * Ends the match from outside the simulation: an abandonment, or the room
   * itself failing.
   *
   * Idempotent. The first reason to arrive is the one recorded - a second call
   * cannot overwrite a death with an abandonment, which is what would happen if
   * a grace timer fired one tick after the killing shot.
   */
  finishTeam(
    reason: PvpEndReason,
    winner: PvpTeam | null,
    abandonment: PvpAbandonment | null = null,
  ): PvpTeamResult {
    if (this.outcome !== null) {
      return this.outcome;
    }
    this.outcome = {
      winner,
      reason,
      ticks: this.currentTick,
      /*
       * A cause belongs to an abandonment and to nothing else.
       *
       * Dropped rather than trusted when the reason disagrees: a caller passing
       * one alongside `death` has made a mistake, and carrying it would put an
       * explanation on a screen the explanation is not about. The room never
       * does this; the guard is here so a later caller cannot.
       */
      abandonment: reason === 'abandonment' ? abandonment : null,
    };
    return this.outcome;
  }

  /* ------------------------------------------------------------------------ */
  /*  The tick                                                                */
  /* ------------------------------------------------------------------------ */

  /**
   * Advances exactly one tick.
   *
   * Fixed order, and the order is part of the rules: intent is already applied,
   * ships move, guns fire, shots travel and resolve, then deaths and the clock
   * are read. Firing BEFORE the shots move is what makes a muzzle appear at the
   * hull rather than a tick ahead of it.
   *
   * Each phase runs over the WHOLE roster before the next begins - move
   * everyone, then teleport everyone, then collect, then fire - which is the
   * order a two-ship match already had, generalised. Doing one ship completely
   * before the next would make the first ship's shots travel through a world the
   * second had not moved in yet.
   */
  advance(): void {
    if (this.outcome !== null) {
      return;
    }
    this.currentTick += 1;
    /*
     * Runes grow BEFORE anyone moves, so a pad that came back this tick can be
     * taken this tick. The alternative reads as a pad flickering.
     */
    pvpGrowRunes(this.runes, this.currentTick, () =>
      pvpRuneKindAt(this.rng.nextIntInclusive(0, PVP_RUNE_KINDS.length - 1)),
    );
    const ships = this.roster.all();
    /*
     * **Onde cada casco ESTAVA, antes de qualquer um andar** (gate V28H.K).
     *
     * Os obstáculos fixos saíram da arena e as naves passaram a ser o bloqueio
     * físico. Uma nave é expulsa das POSIÇÕES DE INÍCIO DO TIQUE das outras, e
     * nunca das já actualizadas: contra o estado de início não há ordem de
     * percurso, portanto duas re-simulações da mesma janela chegam ao mesmo
     * sítio. Contra posições a mudar, quem andasse primeiro ganhava o lugar.
     *
     * Um destroço não bloqueia: a lista só leva quem está vivo.
     */
    const blockers = ships
      .filter((ship) => ship.healthMilli > 0n)
      .map((ship) => ({
        id: ship.participantId,
        x: ship.x,
        y: ship.y,
        radius: pvpHullRadiusMu(ship.profile),
      }));
    for (const ship of ships) {
      this.moveShip(
        ship,
        blockers.filter((blocker) => blocker.id !== ship.participantId),
      );
    }
    for (const ship of ships) {
      this.teleport(ship);
    }
    /*
     * **A DETONAÇÃO, antes de tudo o que dispara** (gate V28G.N).
     *
     * Nesta linha porque o §"se disparo e detonação forem recebidos no mesmo
     * tique, processe a detonação antes de novas aplicações desse tique" é uma
     * regra sobre esta ordem exata. As marcas nascem em `advanceProjectiles`,
     * que corre depois de `fire`; executar a ordem aqui garante que uma marca
     * recém-aplicada NUNCA rebenta no tique em que nasceu, por construção e não
     * por uma verificação de idade.
     *
     * Pela ordem do ROSTER e não pela de chegada dos frames: dois jogadores que
     * mandem no mesmo tique detonam pela mesma ordem em qualquer máquina.
     *
     * A bandeira é apagada tenha havido marcas ou não - uma pressão é uma
     * tentativa - e um casco morto não manda coisa nenhuma.
     */
    /*
     * **As CARTAS, antes de tudo o que dispara** (gate V28H.J).
     *
     * Envelhecer primeiro, para uma habilidade que acaba neste tique não
     * disparar mais uma vez; aplicar a ordem a seguir, para os canhões que
     * acabaram de montar dispararem já neste tique - eles são armamento
     * adicional, e uma salva do casco que saísse sem eles no tique da activação
     * leria como um atraso.
     *
     * Pela ordem do ROSTER e não pela de chegada dos frames: dois jogadores que
     * mandem no mesmo tique ligam pela mesma ordem em qualquer máquina, que é a
     * mesma razão pela qual a detonação abaixo o faz.
     */
    for (const ship of ships) {
      for (const card of this.ageCards(ship)) {
        /*
         * A carga acabou: a esfera sai, o Catolé sai, o feixe acende.
         *
         * Aqui e não no tique da ordem, porque é isso que uma carga É - e é
         * também o que a torna evitável: o adversário vê a nave presa a
         * carregar e tem 1,5 s para sair da linha.
         */
        this.releaseChargedCard(ship, card);
      }
    }
    for (const ship of ships) {
      const order = ship.pendingCard;
      if (order === null) {
        continue;
      }
      /* Consumida tenha ela pegado ou não: uma ordem vale um tique. */
      ship.pendingCard = null;
      this.activateParticipantCard(ship, order);
    }
    /*
     * **E as SOLTURAS** (gate V28H.N), depois das activações e nunca antes.
     *
     * A ordem entre as duas importa num caso real: um cliente com um quadro
     * muito curto pode ligar e soltar no mesmo frame - um toque na tecla. Nessa
     * ordem, o toque produz uma carga mínima, que é o que ele foi. Ao contrário,
     * a soltura não encontraria carga nenhuma e o toque viraria carga cheia.
     */
    for (const ship of ships) {
      const order = ship.pendingCardRelease;
      if (order === null) {
        continue;
      }
      ship.pendingCardRelease = null;
      this.releaseParticipantCard(ship, order);
    }
    for (const ship of ships) {
      if (!ship.detonateBleed) {
        continue;
      }
      ship.detonateBleed = false;
      if (ship.healthMilli <= 0n) {
        continue;
      }
      this.detonateBleeding(ship);
    }
    this.collect();
    for (const ship of ships) {
      this.fire(ship);
    }
    /*
     * **O Turbilhão, DEPOIS das salvas e pela ordem do roster** (gate V28H.L).
     *
     * Depois porque uma lâmina não é um tiro: ela não nasce de uma cadência, não
     * viaja e não pode ser evitada. Pô-la antes faria uma morte pela lâmina
     * apagar a salva do mesmo tique, e a salva é a coisa que o jogador mandou.
     *
     * Pela ordem do roster, como tudo o resto neste tique: dois Turbilhões a
     * cobrar no mesmo instante cobram pela mesma ordem em qualquer máquina.
     */
    for (const ship of ships) {
      this.tickBladeStorm(ship);
      this.tickSolarLance(ship);
    }
    /*
     * **As invocações, depois dos cascos e dos projéteis** (gate V28H.L).
     *
     * Depois dos cascos porque uma invocação persegue quem já andou neste
     * tique, e depois dos projéteis porque um drone que morra de um tiro deste
     * tique não deve chegar a disparar.
     */
    this.tickSummons();
    this.advanceProjectiles();
    /*
     * Os mísseis DEPOIS dos tiros, e a ordem é uma regra.
     *
     * Um míssil lançado neste tique já viaja neste tique, exatamente como o
     * projétil da mesma salva viaja: `advanceMissiles` percorre a lista que
     * `fire` acabou de aumentar. Tratá-lo de outra maneira faria a salva e o
     * míssil separarem-se por um passo no primeiro instante.
     *
     * Depois dos tiros porque um casco que morre de um tiro deste tique já está
     * morto quando os mísseis o procuram: o retarget encontra a morte no tique em
     * que ela aconteceu, e não um tique atrasado.
     */
    this.advanceMissiles();
    /*
     * **AS SERRAS, depois de tudo o que dispara e antes do veneno** (V28G.P).
     *
     * Nesta linha e não noutra, por três razões que apontam todas para cá.
     *
     * Depois de `moveShip` e `teleport`, porque uma órbita é desenhada à volta
     * da posição FINAL do casco neste tique - uma lâmina calculada da posição
     * antiga ficaria um passo atrás do dono dela, e a colisão deixaria de
     * corresponder ao que se vê.
     *
     * Depois dos tiros, das explosões e dos mísseis, porque um contacto cobra
     * sobre a vida que eles já reduziram: um casco que morre da salva deste
     * tique não é cortado, e `applyDamage` recusa um alvo sem vida.
     *
     * Antes do veneno, porque um contacto pode APLICAR um stack, e um stack
     * aplicado neste tique não deve cobrar a primeira parcela no mesmo instante
     * em que nasceu - `poisonPendingTicks` compara o decorrido com o intervalo,
     * portanto zero decorrido é zero parcelas, e a ordem só torna isso óbvio.
     */
    this.tickSaws();
    /*
     * **O veneno DEPOIS de tudo o que dispara, e a ordem é uma regra.**
     *
     * As parcelas deste tique cobram sobre a vida que os tiros, as explosões e
     * os mísseis deste tique já reduziram - portanto um casco que morre de um
     * tiro não paga a parcela que lhe caía no mesmo instante, e um que sobrevive
     * paga-a. Pô-las antes faria o veneno decidir a morte com uma vida que a
     * salva ainda ia tirar.
     *
     * Antes de `resolveEnd`, porque uma parcela PODE ser o golpe letal e o §"o
     * veneno pode causar o golpe letal" exige que a partida acabe no tique em
     * que isso acontece.
     */
    this.tickPoison();
    /*
     * Timers age LAST, so a rune taken this tick is worth its full duration and
     * one taken on the tick it expires is not silently worth a tick less.
     */
    for (const ship of ships) {
      pvpAgeEffects(ship.effects);
    }
    this.resolveEnd();
  }

  private moveShip(ship: PvpParticipantShipState, blockers: readonly PvpHullBlocker[] = []): void {
    if (ship.healthMilli <= 0n) {
      /*
       * A wreck holds no keys.
       *
       * In a duel this was implicit - the first death ended the match, so a dead
       * ship never saw another tick. With three a side it has to be said, or a
       * hull that died holding a direction would keep flying while its team
       * fought on.
       *
       * E não guarda empurrão nenhum: a morte encerra o estado (§6), e um casco
       * que ressuscitasse a meio de um empurrão retomaria um deslocamento que a
       * partida já tinha esquecido.
       */
      ship.knockback = null;
      return;
    }
    /*
     * **O EMPURRÃO, e o que ele substitui.**
     *
     * Enquanto durar, o casco anda o passo da explosão e mais nada:
     *
     * - `moveX`/`moveY` entram a ZERO, portanto o input do jogador é ignorado -
     *   e não é apagado: o casco continua a segurar a direção, e volta a andar
     *   nela no tique em que o empurrão acaba;
     * - o passo vai em `drift`, o mesmo parâmetro do impulsionador, portanto as
     *   paredes e as pilastras travam-no exatamente como travam um passo normal;
     * - `speedBps` fica no seu valor real e não faz diferença nenhuma, porque
     *   ele só multiplica o passo do JOGADOR - é isso que faz velocidade, runas
     *   e joias de movimento não alterarem o deslocamento;
     * - a contagem de tiques desce TENHA O CASCO ANDADO OU NÃO, portanto bater
     *   numa parede não devolve o controlo mais cedo.
     */
    const knockback = ship.knockback;
    if (knockback !== null) {
      const step = pvpKnockbackStep(knockback);
      ship.knockback = step.next;
      const pushed = pvpAdvanceShip(
        ship,
        0,
        0,
        step.drift,
        this.moveSpeedOf(ship),
        /* O raio DESTE casco: um empurrão trava numa parede onde o desenho trava. */
        pvpHullRadiusMu(ship.profile),
        /* E num casco, que desde o gate V28H.K é o que a arena tem em vez de pilares. */
        blockers,
      );
      ship.x = pushed.x;
      ship.y = pushed.y;
      return;
    }
    /*
     * Delegated, so the browser can predict with the SAME arithmetic.
     *
     * The client draws its own ship one frame after a key press rather than up
     * to 50 ms later, which means it has to guess what this method will do. A
     * guess made against a second implementation drifts within a second and is
     * corrected forever after; a guess made against this one is wrong only by
     * what the network reordered. See `pvpAdvanceShip`.
     */
    const moved = pvpAdvanceShip(
      ship,
      ship.moveX,
      ship.moveY,
      pvpBoosterPush(ship),
      /*
       * The hull's OWN speed, with the rune composed onto it rather than
       * replacing it. With the default profile this is the same pair of
       * numbers it always was - see `pvpEffectiveSpeedBps`.
       */
      this.moveSpeedOf(ship),
      /*
       * E o raio DESTE casco (gate V28G.R). Uma nave maior é travada mais cedo
       * pelas paredes e pelos pilares, que é a contrapartida geométrica da vida
       * a mais - e o cliente prevê com o mesmo número, lido do mesmo perfil.
       */
      pvpHullRadiusMu(ship.profile),
      /*
       * E os outros cascos (gate V28H.K). Uma nave tanque é a parede: o raio sai
       * da vida máxima, portanto quem tem mais vida ocupa mais espaço e bloqueia
       * mais - que é o papel que o contrato do catálogo lhe deu quando mandou os
       * obstáculos fixos embora.
       */
      blockers,
    );
    ship.x = moved.x;
    ship.y = moved.y;
  }

  /**
   * Spends a teleport charge, if the player asked and the room agrees.
   *
   * The client sends a REQUEST carrying where the player clicked, and never a
   * result. Whether that point may be reached - and therefore where the ship
   * ends up - is decided by `pvpValidateTeleport` against the arena, the
   * structures, the hull's own radius, the charge and the ship's life.
   *
   * A refused destination is refused, never adjusted: a shortened teleport
   * would be a free nudge out of the corner the player was supposed to be
   * stuck in, and nothing is spent on a move that did not happen.
   */
  private teleport(ship: PvpParticipantShipState): void {
    const asked = ship.teleportTo;
    if (asked === null) {
      return;
    }
    /*
     * Cleared BEFORE the verdict, so one request is one attempt.
     *
     * A held button, a doubled click, a replayed frame and a second tab all
     * arrive as repeated requests; each is answered once, and none of them
     * can spend a second charge, because the ship is asking for nothing by
     * the time the next tick runs.
     */
    ship.teleportTo = null;
    /*
     * **Um casco a ser empurrado não se teleporta** (gate V28G.J).
     *
     * O §6 diz que o empurrão não pode ser cancelado pelo jogador, e o teleporte
     * é a única coisa no duelo que o cancelaria por completo: ele reposiciona o
     * casco de uma vez e a explosão deixava de significar coisa nenhuma.
     *
     * O pedido é DESCARTADO e a carga não é gasta - recusar sem cobrar é o que
     * faz isto ser um bloqueio e não uma punição. É a mesma forma que o resto do
     * método já usa para uma recusa.
     *
     * O que continua: mirar e disparar. O empurrão tira o controlo da POSIÇÃO, e
     * uma nave que também deixasse de responder ao rato seria uma nave desligada
     * durante 350 ms - que o contrato não pede.
     */
    if (ship.knockback !== null) {
      return;
    }
    const verdict = pvpValidateTeleport(asked, ship, ship.effects, (x, y) =>
      this.blocked(x, y, pvpHullRadiusMu(ship.profile)),
    );
    if (!verdict.ok) {
      return;
    }
    ship.effects.teleportCharges -= 1;
    ship.x = verdict.x;
    ship.y = verdict.y;
    /*
     * The event carries the position the SERVER chose, not the one that was
     * asked for. They are equal today, and saying it this way is what stops a
     * future rule - a snap, an offset, a cap - from silently disagreeing with
     * what the clients drew. It also names the participant that moved, so a
     * six-ship room cannot animate the wrong hull.
     */
    this.events.push({
      kind: 'teleport',
      participantId: ship.participantId,
      team: ship.team,
      x: verdict.x,
      y: verdict.y,
    });
  }

  /**
   * Would a hull of THIS radius, centred here, be inside a wall?
   *
   * O raio é um argumento e não a constante (gate V28G.R): uma nave maior não
   * cabe onde uma normal cabe, e um teletransporte que a pusesse lá deixá-la-ia
   * dentro de uma parede que o desenho mostra.
   */
  private blocked(x: number, y: number, radius: number): boolean {
    for (const obstacle of PVP_OBSTACLES) {
      if (
        x > obstacle.x - radius &&
        x < obstacle.x + obstacle.w + radius &&
        y > obstacle.y - radius &&
        y < obstacle.y + obstacle.h + radius
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Hands out every pad somebody is standing on, and says who took what.
   *
   * Per PAD rather than per ship, which is the change six participants force:
   * collecting ship by ship gave the pad to whoever was iterated first, and
   * "iterated first" must stop being a rule. `pvpClaimRune` decides by the
   * distance the reach test already computes, ties by canonical order, and
   * offers the pad to the next hull when the nearest one declines it.
   */
  private collect(): void {
    const claimants = this.roster.all();
    for (const rune of this.runes) {
      const claim = pvpClaimRune(rune, claimants, this.currentTick);
      if (claim === null) {
        continue;
      }
      const ship = claimants[claim.claimant];
      if (ship === undefined) {
        continue;
      }
      this.events.push({
        kind: 'rune',
        participantId: ship.participantId,
        team: ship.team,
        rune: claim.kind,
        x: ship.x,
        y: ship.y,
      });
    }
  }

  /**
   * A rolagem de proc deste disparo, do fluxo deste participante.
   *
   * Consome SEMPRE dois valores - a rolagem e a entropia - mesmo sem joia
   * nenhuma. Consumir condicionalmente faria a sequência de um casco depender do
   * seu equipamento, e dois duelos com a mesma semente deixariam de ser o mesmo
   * duelo assim que alguém encaixasse uma joia.
   */
  private rollOutgoingProc(
    ship: PvpParticipantShipState,
    /**
     * A ORIGEM deste tiro. `cannon` por omissão, que é o casco.
     *
     * `temporary_cannon` é a dos Super Canhões (gate V28H.J), e ela existe para
     * a tabela fechada de `damage/origin.ts` decidir - em vez de estes tiros se
     * disfarçarem de casco. O que a tabela diz é que ela aciona joia como o
     * canhão aciona, e que o que a joia gerar não aciona nada.
     */
    origin: PvpDamageOrigin = 'cannon',
  ): JewelProcSelection | null {
    const stream = this.procRngFor(ship.participantId);
    const roll = stream.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    const entropy = stream.nextIntInclusive(0, 0xffff_ffff);
    /*
     * **A afordância de desenvolvimento, e ela substitui UMA comparação.**
     *
     * A rolagem acima já foi feita e os dois valores já saíram do fluxo, tal
     * como na loteria por salva - portanto a sequência desta sala é idêntica à
     * de uma sala sem a ferramenta. O que é substituído é o valor CONTRA o qual
     * a rolagem é comparada: o INÍCIO do intervalo do Ciclone, que por
     * construção cai dentro dele.
     *
     * **Não acrescenta a joia.** Um casco sem o Ciclone de Lâminas não tem
     * intervalo nenhum, `procIntervals` não o devolve, e o crédito é gasto sem
     * produzir corte - forçar uma ferramenta não é razão para acionar a joia do
     * lado.
     */
    const forcedCyclone = this.spendForcedCyclone(ship.participantId);
    const cycloneStart = forcedCyclone
      ? (procIntervals(ship.profile.jewels).find((interval) => interval.procId === 'cyclone_cut')
          ?.from ?? null)
      : null;
    return selectOutgoingProc(
      ship.profile.jewels,
      origin,
      cycloneStart ?? roll,
      entropy,
      /*
       * O dano-base AUTORITATIVO deste casco, NESTE instante, e mais nada: sem
       * `jewel_damage`, sem os Corações e sem a runa. A magnitude fica
       * congelada com a seleção, pela mesma lei que congela `damageMilli`.
       */
      BigInt(pvpShipBaseDamageMilli(ship.profile)),
    );
  }

  /**
   * A semente de um documento ANTERIOR ao gate V28G.F.
   *
   * Esses formatos não guardam a semente da sala - ela não era precisa antes de
   * existirem fluxos por cadeira. O que eles guardam é o ESTADO do gerador das
   * runas, cuja própria semente é determinística e está no documento.
   *
   * Derivar dela é reprodutível e não inventa nada: o mesmo documento antigo dá
   * sempre os mesmos fluxos. E como nenhum projétil desses formatos carrega
   * proc, a sequência começa limpa sem perder efeito nenhum.
   */
  private static seedFromLegacyRng(state: SimulationRngState): SimulationSeed {
    return simulationSeed(BigInt(state.seed));
  }

  /** O fluxo desta cadeira, criado à primeira utilização e depois estável. */
  private procRngFor(participantId: PvpParticipantId): SimulationRng {
    const existing = this.procRng.get(participantId);
    if (existing) {
      return existing;
    }
    const created = createSimulationRng(
      deriveSimulationSeed(this.seed, PVP_OUTGOING_PROC_RNG_LABEL, this.seatIndexOf(participantId)),
    );
    this.procRng.set(participantId, created);
    return created;
  }

  /**
   * A rolagem da SALVA deste disparo, do fluxo por salva deste participante.
   *
   * Num duelo uma salva é um projétil - o canhão normalizado do PvP dispara um
   * de cada vez - portanto "uma vez por salva" e "uma vez por disparo" são aqui
   * o mesmo instante. Está escrito assim mesmo assim, e não como "por tiro",
   * porque a REGRA é por salva: é a caça que tem canhões de sete bocas, e as
   * duas simulações têm de responder à mesma pergunta.
   *
   * Consome SEMPRE dois valores, com joia ou sem ela, pela razão de sempre.
   */
  private rollVolleyProcs(ship: PvpParticipantShipState): {
    readonly bomb: AtomicBombSelection | null;
    readonly missile: MissileSelection | null;
  } {
    const stream = this.bombRngFor(ship.participantId);
    const roll = stream.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    const entropy = stream.nextIntInclusive(0, 0xffff_ffff);
    /*
     * O dano-base AUTORITATIVO deste casco, e mais nada - a mesma leitura que a
     * loteria faz, pela mesma lei. A magnitude fica congelada com a seleção e
     * nunca é reavaliada em voo.
     */
    const baseMilli = BigInt(pvpShipBaseDamageMilli(ship.profile));
    const jewels = ship.profile.jewels;

    /*
     * **A afordância de desenvolvimento, e ela substitui UMA comparação.**
     *
     * A rolagem acima já foi feita e os dois valores já saíram do fluxo -
     * portanto a sequência desta sala é idêntica à de uma sala sem a ferramenta,
     * e ligá-la não desloca uma runa, um proc nem um tique. O que é substituído
     * é o valor CONTRA o qual a rolagem é comparada: o INÍCIO do intervalo do
     * proc pedido, que é por construção um valor que cai dentro dele e fora de
     * todos os outros.
     *
     * Gasta-se ao ser usada: um crédito por salva, e quando eles acabam a sala
     * volta à chance real sem que nada mais mude.
     *
     * **Não acrescenta a joia.** Um casco sem a joia pedida não tem intervalo
     * nenhum, e a salva forçada não produz coisa nenhuma - nem o proc pedido nem
     * outro qualquer, porque forçar uma ferramenta não é razão para acionar a
     * joia do lado.
     */
    const forced = this.spendForcedVolleyProc(ship.participantId);
    if (forced !== null) {
      const interval = volleyProcInterval(jewels, forced);
      if (interval === null) {
        return { bomb: null, missile: null };
      }
      return forced === 'atomic_blast'
        ? {
            bomb: selectVolleyBomb(jewels, 'cannon', interval.from, entropy, baseMilli),
            missile: null,
          }
        : {
            bomb: null,
            missile: selectVolleyMissile(jewels, 'cannon', interval.from, entropy, baseMilli),
          };
    }

    /*
     * **Uma rolagem, dois intervalos DISJUNTOS, no máximo um proc.**
     *
     * A mesma rolagem é comparada contra os dois intervalos, e eles não se
     * sobrepõem - a Bomba ocupa `[0, w)` e o míssil `[w, w + m)`, na ordem
     * canónica do registo. Não são dois sorteios independentes, e a exclusão
     * mútua é uma propriedade da aritmética em vez de uma verificação que
     * alguém tem de se lembrar de escrever.
     *
     * `selectVolleyBomb` recebe exatamente os argumentos que sempre recebeu, e é
     * por isso que uma sala sem o Arsenal Ígneo se comporta tique a tique como
     * se comportava antes deste gate.
     */
    const bomb = selectVolleyBomb(jewels, 'cannon', roll, entropy, baseMilli);
    const missile = selectVolleyMissile(jewels, 'cannon', roll, entropy, baseMilli);
    return { bomb, missile };
  }

  /**
   * Qual proc por salva esta cadeira tem forçado agora, gastando o crédito.
   *
   * A Bomba primeiro quando as duas ferramentas estão armadas ao mesmo tempo -
   * uma escolha determinística, e a única forma de duas ferramentas
   * independentes não produzirem dois procs numa salva que só permite um.
   */
  private spendForcedVolleyProc(participantId: PvpParticipantId): VolleyProcId | null {
    const bombs = this.devBombCredits.get(participantId) ?? 0;
    if (bombs > 0) {
      this.devBombCredits.set(participantId, bombs - 1);
      return 'atomic_blast';
    }
    const missiles = this.devMissileCredits.get(participantId) ?? 0;
    if (missiles > 0) {
      this.devMissileCredits.set(participantId, missiles - 1);
      return 'missile';
    }
    return null;
  }

  /** O fluxo do Coração Vital desta cadeira, criado à primeira utilização. */
  private vitalRngFor(participantId: PvpParticipantId): SimulationRng {
    const existing = this.vitalRng.get(participantId);
    if (existing) {
      return existing;
    }
    const created = createSimulationRng(
      deriveSimulationSeed(this.seed, PVP_VITAL_HEART_RNG_LABEL, this.seatIndexOf(participantId)),
    );
    this.vitalRng.set(participantId, created);
    return created;
  }

  /**
   * **A rolagem do Coração Vital, e ela é INDEPENDENTE da loteria ofensiva.**
   *
   * Um fluxo próprio, um intervalo próprio e uma rolagem própria, na unidade da
   * SALVA. O §"é um proc de suporte independente: não consome nem bloqueia a
   * loteria ofensiva" é cumprido por construção - esta rolagem não é comparada
   * contra nenhum intervalo da Bomba nem do Míssil - portanto uma salva pode
   * legitimamente lançar um míssil E curar.
   *
   * Consome UM valor por salva, sempre, com joia ou sem ela.
   *
   * ## A seleção e a cura são UMA decisão, aqui
   *
   * O §"troca de alvo entre disparo e evento não existe" é isso mesmo: não há
   * pedido guardado, não há alvo pendente e não há janela onde alguma das duas
   * coisas possa mudar. O que sai daqui é um facto fechado.
   *
   * @param projectileId o projétil que esta salva acabou de criar. É a
   *   identidade AUTORITATIVA e estável que deduplica o anúncio - num duelo uma
   *   salva é um projétil, portanto ela nomeia a salva exatamente.
   */
  private procVitalHeal(ship: PvpParticipantShipState, projectileId: number): void {
    const roll = this.vitalRngFor(ship.participantId).nextIntInclusive(
      0,
      PROC_CHANCE_DENOMINATOR - 1,
    );
    /*
     * A ferramenta de desenvolvimento substitui UMA comparação, e nada mais.
     *
     * A rolagem acima já foi feita e o valor já saiu do fluxo, portanto a
     * sequência desta sala é idêntica à de uma sala sem a ferramenta. Zero cai
     * sempre dentro do intervalo da joia, e fora dele quando ela não existe -
     * forçar não é acrescentar a joia. E não muda a magnitude: ela sai do
     * dano-base autoritativo do casco, como qualquer outra.
     */
    const credits = this.devVitalCredits.get(ship.participantId) ?? 0;
    if (credits > 0) {
      this.devVitalCredits.set(ship.participantId, credits - 1);
    }
    const selection = selectVitalHeal(
      ship.profile.jewels,
      'cannon',
      credits > 0 ? 0 : roll,
      /* O dano-base AUTORITATIVO deste casco, e mais nada. A mesma leitura. */
      BigInt(pvpShipBaseDamageMilli(ship.profile)),
    );
    if (selection === null) {
      return;
    }
    const chosen = selectVitalHealTarget(
      this.roster.team(ship.team).map((ally) => this.vitalCandidate(ship, ally)),
      this.vitalCandidate(ship, ship),
    );
    if (chosen === null) {
      /*
       * Ninguém precisa de cura. A rolagem foi consumida na mesma - é o que
       * mantém a sequência independente do estado da sala - e não há cura nem
       * evento: um facto que não aconteceu não é anunciado.
       */
      return;
    }
    const target = this.roster.require(chosen.id as PvpParticipantId);
    const application = applyVitalHealMilli({
      requestedMilli: selection.requestedMilli,
      healthMilli: target.healthMilli,
      maxHealthMilli: target.profile.maxHealthMilli,
    });
    if (application.appliedMilli <= 0n) {
      /* Sem overheal, sem escudo e sem ressurreição - e portanto sem evento. */
      return;
    }
    this.grantHealth(target, application.appliedMilli);
    /*
     * O anúncio, e só depois de a cura ter mesmo acontecido.
     *
     * Diz o efeito, quem ativou, quem foi curado, que salva o causou, quando e
     * quanto. Não diz nível, cópias, joia, chance nem parâmetro de arte.
     *
     * A posição é a de QUEM ATIVOU, que é de onde o fluxo parte - a mesma regra
     * que o corte e o sifão seguem, com o sentido invertido. O destino é o alvo,
     * e a cena sabe onde ele está porque o desenha.
     */
    this.events.push({
      kind: 'proc',
      procId: VITAL_HEAL_EFFECT,
      participantId: ship.participantId,
      team: ship.team,
      targetId: target.participantId,
      projectileId,
      tick: this.currentTick,
      /* Já é milli: a vida de um casco é milli, e isto é o que lhe foi somado. */
      healedMilli: application.appliedMilli,
      x: ship.x,
      y: ship.y,
    });
  }

  /**
   * Um casco, medido para a lei de seleção de alvo.
   *
   * A distância é AO QUADRADO e em milli-unidades inteiras: a raiz não muda
   * ordenação nenhuma e o seu último bit não é obrigatoriamente igual entre
   * motores. O desempate é `PvpShipRoster.indexOf`, que é o único permitido -
   * uma propriedade da CADEIRA, e nunca a posição num array.
   */
  private vitalCandidate(
    healer: PvpParticipantShipState,
    candidate: PvpParticipantShipState,
  ): VitalHealCandidate {
    const dx = candidate.x - healer.x;
    const dy = candidate.y - healer.y;
    return {
      id: candidate.participantId,
      order: this.roster.indexOf(candidate.participantId),
      distanceSq: dx * dx + dy * dy,
      injured:
        candidate.healthMilli > 0n && candidate.healthMilli < candidate.profile.maxHealthMilli,
    };
  }

  /**
   * **O ÚNICO sítio de toda a simulação que ACRESCENTA vida a um casco.**
   *
   * O gémeo do funil da subtração, e existe pela mesma razão exata: dois efeitos
   * com a sua própria soma seriam dois sítios que podem cada um esquecer o teto,
   * o destroço ou o sinal - e o que esquece é o que ninguém está a olhar.
   * `milli-damage-paths.test.ts` conta as ocorrências e falha se aparecer uma
   * segunda.
   *
   * ## O que ele NÃO faz, e porquê
   *
   * Não limita. O clamp da vida máxima e a recusa de curar um destroço acontecem
   * ANTES, em quem sabe qual é a lei do seu efeito - a percentagem do sifão tem
   * um resto sub-milli a guardar, e a cura do Coração Vital não tem. Limitar
   * outra vez aqui seria um segundo teto a concordar com o primeiro por
   * coincidência.
   *
   * O que ele garante é o que só um funil pode garantir: que existe exatamente
   * um sítio onde a vida sobe, e que nada sobe com um valor não positivo.
   */
  private grantHealth(ship: PvpParticipantShipState, milli: bigint): void {
    if (milli <= 0n) {
      return;
    }
    /*
     * **O Malware, e é aqui que ele morde** (gate V28H.L).
     *
     * Uma linha, no ÚNICO sítio por onde a vida de um casco sobe - que é a mesma
     * disciplina que pôs a Égide dentro de `applyDamage`. Um `if` por fonte de
     * cura seria um sítio por joia para a próxima se esquecer de reduzir.
     *
     * Reduz a cura RECEBIDA e não a ganha: o Roubo de Vida de quem ataca
     * continua a render o que rende, e o que o Malware faz é o casco marcado
     * aproveitar menos - que é literalmente "reduz a cura recebida".
     *
     * Se a redução levar a cura a zero, não sobe nada, e quem chamou vê zero -
     * sem evento, porque não houve facto nenhum a anunciar.
     */
    const received = cardHealingInMilli(ship.cardEffects, milli);
    if (received <= 0n) {
      return;
    }
    ship.healthMilli += received;
  }

  /**
   * **DEV/E2E ONLY — arma as próximas `count` salvas de cada cadeira com uma
   * cura**, se o casco tiver a joia.
   *
   * Gémea de {@link PvpSimulation.devForceVolleyBombs}, com as mesmas garantias e
   * pela mesma razão: a chance real ao topo do contrato é de 6 % por salva, e
   * esperar por ela numa avaliação manual é uma moeda ao ar.
   *
   * O que ela muda é UMA comparação. A rolagem continua a ser feita e o valor
   * continua a sair do fluxo, portanto ligar isto não desloca uma runa, um proc
   * nem um tique. Não toca na magnitude, na seleção de alvo, no clamp da vida
   * máxima, na proibição de curar um destroço nem na instantânea.
   *
   * **Não acrescenta a joia.** Um casco sem Coração Vital continua a não curar.
   *
   * Chamada apenas pelo objeto da sala, e só quando o ambiente a autoriza. Em
   * produção nada a chama, porque a variável que a autoriza nunca é definida.
   */
  devForceVitalHeals(count: number): void {
    if (!Number.isSafeInteger(count) || count <= 0) {
      return;
    }
    for (const ship of this.roster.all()) {
      this.devVitalCredits.set(ship.participantId, count);
    }
  }

  /**
   * **DEV/E2E ONLY — arma as próximas `count` salvas de cada cadeira.**
   *
   * ## O que ela muda, e o que não pode mudar
   *
   * Uma comparação, e nada mais. A rolagem continua a ser feita e os dois
   * valores continuam a sair do fluxo, portanto ligar isto não desloca uma runa,
   * um proc nem um tique - a sequência da sala é idêntica à de uma sala sem
   * ferramenta nenhuma. O que muda é o valor com que a rolagem é comparada.
   *
   * Não toca em dano, magnitude, raio, velocidade, colisão, proximidade,
   * empurrão, resultado, chance real nem instantânea. A bomba que sai daqui é
   * construída por `selectVolleyBomb` com o dano-base autoritativo do casco,
   * como qualquer outra - e é a SIMULAÇÃO que a cria, portanto o cliente desenha
   * uma bomba verdadeira em vez de um efeito encenado.
   *
   * **Não acrescenta a joia.** Um casco sem Núcleo Atômico continua a não
   * produzir bomba nenhuma.
   *
   * ## Porque um crédito e não um interruptor
   *
   * Porque o contrato pede "uma ativação força exatamente uma bomba e depois se
   * desarma". Um interruptor permanente faria TODAS as salvas serem bombas, o
   * que é outra coisa: mediria uma cadência que o jogo não tem. Um crédito
   * gasta-se, e quando acaba a sala volta à chance real sozinha.
   *
   * Chamada apenas pelo objeto da sala, e só quando o ambiente a autoriza. Em
   * produção nada a chama, porque a variável que a autoriza nunca é definida.
   */
  devForceVolleyBombs(count: number): void {
    if (!Number.isSafeInteger(count) || count <= 0) {
      return;
    }
    for (const ship of this.roster.all()) {
      this.devBombCredits.set(ship.participantId, count);
    }
  }

  /**
   * **DEV/E2E ONLY — arma as próximas `count` salvas de cada cadeira com um
   * míssil**, se o casco tiver a joia.
   *
   * Gémea de {@link PvpSimulation.devForceVolleyBombs}, com as mesmas garantias e
   * pela mesma razão: a chance real ao topo do contrato é de 10 % por salva, e
   * esperar por ela numa avaliação manual é uma moeda ao ar.
   *
   * O que ela muda é UMA comparação. A rolagem continua a ser feita e os dois
   * valores continuam a sair do fluxo, portanto ligar isto não desloca uma runa,
   * um proc nem um tique. Não toca em dano, alvo, velocidade, giro, colisão,
   * tempo de vida, explosão, chance real nem instantânea - o míssil que sai daqui
   * é construído por `selectVolleyMissile` com o dano-base autoritativo do casco,
   * como qualquer outro, e é a SIMULAÇÃO que o cria.
   *
   * **Não acrescenta a joia.** Um casco sem Arsenal Ígneo continua a não lançar
   * míssil nenhum.
   *
   * Chamada apenas pelo objeto da sala, e só quando o ambiente a autoriza. Em
   * produção nada a chama, porque a variável que a autoriza nunca é definida.
   */
  devForceVolleyMissiles(count: number): void {
    if (!Number.isSafeInteger(count) || count <= 0) {
      return;
    }
    for (const ship of this.roster.all()) {
      this.devMissileCredits.set(ship.participantId, count);
    }
  }

  /* ---------------------------------------------------------------------- */
  /*  O Orbe da Víbora (gate V28G.M)                                        */
  /* ---------------------------------------------------------------------- */

  /** O fluxo de veneno desta cadeira, criado à primeira utilização. */
  private poisonRngFor(participantId: PvpParticipantId): SimulationRng {
    const existing = this.poisonRng.get(participantId);
    if (existing) {
      return existing;
    }
    const created = createSimulationRng(
      deriveSimulationSeed(this.seed, PVP_POISON_RNG_LABEL, this.seatIndexOf(participantId)),
    );
    this.poisonRng.set(participantId, created);
    return created;
  }

  /**
   * **Um acerto direto de canhão sorteia o seu veneno**, e no máximo um stack.
   *
   * ## O que decide o sorteio, e o que não decide nada
   *
   * A rolagem sai do fluxo desta cadeira, e a comparação é contra o intervalo da
   * joia. `selectPoisonApplication` responde `null` por três razões diferentes -
   * a rolagem caiu fora, o casco não tem a joia, a origem não pode originar - e
   * nenhuma delas produz stack. O chamador não precisa de as distinguir.
   *
   * ## O alvo morto sai ANTES da rolagem
   *
   * Porque um stack num destroço é um stack que ninguém paga, e porque o §"se o
   * tiro matar, nenhum stack nasce" é uma regra sobre o que EXISTE e não sobre o
   * que se sorteia. Sair antes é também o que faz a sequência deste atacante ser
   * a mesma numa re-simulação: o servidor reproduz as mesmas mortes e portanto
   * as mesmas saídas.
   *
   * ## A magnitude é congelada AQUI
   *
   * Neste instante, do dano-base autoritativo deste casco e mais nada: sem a
   * Chama, sem os Corações, sem a runa de Dano Dobrado, sem crítico e sem outro
   * proc. As seis parcelas saem deste número e não voltam a perguntar nada a
   * ninguém - comprar um ponto, trocar de nave ou desencaixar a joia durante os
   * seis segundos não muda uma parcela.
   */
  private poisonDirectHit(
    attacker: PvpParticipantShipState,
    target: PvpParticipantShipState,
    /**
     * De ONDE veio o golpe que está a semear isto (gate V28G.P).
     *
     * `cannon` para um acerto direto de tiro, e `saw` para uma instância de
     * dano de uma serra orbital. O parâmetro existe porque a decisão passou a
     * ter duas respostas, e a segunda metade da tabela fechada é quem as separa
     * - nunca uma condição escrita aqui.
     *
     * Sem valor por omissão, pela regra de `damage/origin.ts`: um chamador novo
     * que se esqueça não herda uma classificação que ninguém escolheu para ele.
     */
    origin: PvpDamageOrigin,
  ): void {
    if (target.healthMilli <= 0n) {
      return;
    }
    const stream = this.poisonRngFor(attacker.participantId);
    const roll = stream.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    /*
     * FERRAMENTA DE DESENVOLVIMENTO, e ela não mexe em número nenhum.
     *
     * A rolagem foi feita e o valor foi consumido acima, portanto o fluxo avança
     * exatamente como avançaria sem isto. O que muda é a COMPARAÇÃO: em vez da
     * rolagem real usa-se ZERO, que cai sempre dentro do intervalo da joia. Um
     * casco sem a joia continua a não envenenar, porque forçar a rolagem não é
     * forçar a posse.
     */
    const forced = this.spendForcedPoison(attacker.participantId);
    const applied = selectPoisonApplication(
      attacker.profile.jewels,
      origin,
      forced ? 0 : roll,
      /*
       * O dano-base AUTORITATIVO deste casco, NESTE instante, e mais nada. A
       * mesma leitura que a loteria ofensiva faz no nascimento de um tiro.
       */
      BigInt(pvpShipBaseDamageMilli(attacker.profile)),
    );
    if (applied === null || applied.totalDamageMilli <= 0n) {
      return;
    }
    /*
     * **A REFLEXÃO DA APLICAÇÃO** (gate V28G.O).
     *
     * O §5 do gate: "nenhuma marca aparece no refletor; a marca aparece
     * imediatamente no atacante original; o refletor torna-se o autor;
     * magnitude e relógio da marca são os do payload original".
     *
     * As quatro coisas em duas linhas, porque a única diferença entre um stack
     * refletido e um normal é QUEM está em cada campo: o `push` abaixo é o mesmo
     * `push`, com o atacante e o alvo trocados e `reflected` a `true`. O total
     * congelado não é recalculado contra o perfil do refletor - foi decidido
     * quando o payload nasceu, e reavaliá-lo responderia à pergunta errada.
     */
    const mirrored = this.reflects(target, attacker, 'poison_application', 'original');
    if (mirrored) {
      this.announceReflection(target, attacker, 'poison_application', 'intercepted');
    }
    /*
     * **Um proc, um stack, e nunca uma renovação.**
     *
     * `push` e não uma procura por um stack do mesmo atacante: os stacks são
     * independentes por contrato, cada um com o seu relógio e a sua magnitude, e
     * um que "renovasse" o anterior seria a mecânica de `PvpEffects` aplicada a
     * uma coisa que não é um efeito de runa.
     */
    this.poison.push({
      id: this.nextPoisonId,
      attackerParticipantId: mirrored ? target.participantId : attacker.participantId,
      targetParticipantId: mirrored ? attacker.participantId : target.participantId,
      totalDamageMilli: applied.totalDamageMilli,
      appliedAt: this.currentTick,
      ticksDone: 0,
      reflected: mirrored,
    });
    this.nextPoisonId += 1;
  }

  /**
   * **As parcelas vencidas deste tique**, por ordem de criação dos stacks.
   *
   * ## Porque a lista é copiada antes de a percorrer
   *
   * Porque uma parcela pode MATAR, e uma morte remove todos os stacks daquele
   * casco de `this.poison` no meio da passagem. Percorrer a lista viva enquanto
   * ela encolhe saltaria elementos; percorrer uma cópia e perguntar a vida do
   * alvo antes de cada parcela dá a resposta certa nos dois casos - os stacks do
   * morto já não cobram, e os dos outros cobram na mesma.
   *
   * ## A origem é `jewel`, e o que isso desliga sozinho
   *
   * Tudo. `damageOriginMayTriggerJewel('jewel')` é falso, e é essa tabela que faz
   * uma parcela não acionar proc, não acionar Roubo de Vida e não acionar outro
   * veneno. A profundidade de recursão é um porque não existe aresta no grafo que
   * lhe dê duas - não porque alguém escreveu uma verificação aqui.
   *
   * ## O dano viaja no evento que já existe
   *
   * `applyDamage` emite o `hit` de sempre, com a magnitude realmente aplicada
   * depois do clamp do overkill. Não há evento novo: qual stack pulsou é
   * derivável do estado que a sala já publica - `ticksDone` sobe - e um
   * protocolo a mais para decoração derivável seria um protocolo a mais para
   * nada.
   */
  private tickPoison(): void {
    if (this.poison.length === 0) {
      return;
    }
    const now = this.currentTick;
    for (const stack of [...this.poison]) {
      const target = this.roster.get(stack.targetParticipantId);
      if (target === undefined) {
        continue;
      }
      /*
       * O ATACANTE, resolvido mesmo que esteja morto ou desligado.
       *
       * Um casco nunca sai do roster - morrer não o remove - portanto a autoria
       * continua a resolver e o crédito da morte vai a quem envenenou. É isso
       * que o §"se o aplicador morrer ou desconectar, stacks já aplicados
       * continuam" quer dizer em termos de estado.
       */
      const attacker = this.roster.get(stack.attackerParticipantId);
      if (attacker === undefined) {
        continue;
      }
      while (
        target.healthMilli > 0n &&
        poisonPendingTicks(stack, now, PVP_POISON_TICK_INTERVAL_TICKS) > 0
      ) {
        stack.ticksDone += 1;
        const tickMilli = poisonTickDamageMilli(stack.totalDamageMilli, stack.ticksDone);
        /*
         * **A REFLEXÃO DE UMA PARCELA** (gate V28G.O).
         *
         * O §5 do gate: "um tick de Veneno proveniente de uma marca
         * originalmente não refletida também é uma instância hostil elegível.
         * Se o tick for refletido, somente aquele tick é redirecionado; a marca
         * original continua o seu relógio."
         *
         * As duas metades estão nesta estrutura. `stack.ticksDone` já subiu
         * acima, portanto o relógio da marca anda **independentemente** do que
         * acontece à parcela - ela não é repetida, não é adiada e não volta ao
         * saco. E a rolagem é POR PARCELA: cada uma é uma instância hostil
         * própria, com a sua própria chance.
         *
         * Uma marca refletida não chega a sortear: a linhagem dela é
         * `reflected`, e a tabela recusa o par antes de qualquer rolagem ser
         * feita. É assim que a profundidade máxima continua a ser um.
         */
        const lineage: ReflectionLineage = stack.reflected ? 'reflected' : 'original';
        if (this.reflects(target, attacker, 'poison_tick', lineage)) {
          this.resolveReflected(target, attacker, tickMilli, 'poison_tick', null);
          continue;
        }
        this.applyDamage(
          target,
          tickMilli,
          /* A origem que fecha o grafo. Nunca `cannon`. */
          'jewel',
          target.x,
          target.y,
          attacker,
          /* Nenhum tiro trouxe isto: o projétil que a semeou já não existe. */
          null,
        );
      }
    }
    /*
     * O que sai da lista: o que já pagou as seis, e o que ficou sem alvo vivo.
     *
     * A remoção do gasto acontece no MESMO tique da sexta parcela, que é o
     * §"depois do sexto tick, o stack é removido" - e é também o que faz a marca
     * desaparecer imediatamente, porque a marca é o stack.
     */
    this.poison = this.poison.filter((stack) => {
      if (poisonIsSpent(stack)) {
        return false;
      }
      const target = this.roster.get(stack.targetParticipantId);
      return target !== undefined && target.healthMilli > 0n;
    });
  }

  /** Todos os stacks de um casco saem juntos. Chamado quando ele morre. */
  private clearPoisonOf(participantId: PvpParticipantId): void {
    if (this.poison.length === 0) {
      return;
    }
    this.poison = this.poison.filter((stack) => stack.targetParticipantId !== participantId);
  }

  /* ---------------------------------------------------------------------- */
  /*  A Garra Hemorrágica (gate V28G.N)                                     */
  /* ---------------------------------------------------------------------- */

  /** O fluxo de sangramento desta cadeira, criado à primeira utilização. */
  private bleedingRngFor(participantId: PvpParticipantId): SimulationRng {
    const existing = this.bleedingRng.get(participantId);
    if (existing) {
      return existing;
    }
    const created = createSimulationRng(
      deriveSimulationSeed(this.seed, PVP_BLEEDING_RNG_LABEL, this.seatIndexOf(participantId)),
    );
    this.bleedingRng.set(participantId, created);
    return created;
  }

  /**
   * **Um acerto direto de canhão sorteia a sua marca**, e no máximo uma.
   *
   * ## O que decide o sorteio, e o que não decide nada
   *
   * A rolagem sai do fluxo desta cadeira, e a comparação é contra o intervalo da
   * joia. `selectBleedingApplication` responde `null` por três razões diferentes
   * - a rolagem caiu fora, o casco não tem a joia, a origem não pode originar - e
   * nenhuma delas produz marca. O chamador não precisa de as distinguir.
   *
   * ## O alvo morto sai ANTES da rolagem
   *
   * Porque o §"se o tiro matar o alvo, nenhuma marca nasce" é uma regra sobre o
   * que EXISTE e não sobre o que se sorteia. Sair antes é também o que faz a
   * sequência deste atacante ser a mesma numa re-simulação: o servidor reproduz
   * as mesmas mortes e portanto as mesmas saídas.
   *
   * ## A magnitude é congelada AQUI
   *
   * Neste instante, do dano-base autoritativo deste casco e mais nada: sem a
   * Chama, sem os Corações, sem a runa de Dano Dobrado, sem crítico e sem outro
   * proc. A detonação SOMA estes números e não volta a perguntar nada a
   * ninguém - melhorar a nave enquanto a marca está acumulada não a muda.
   */
  private bleedingDirectHit(
    attacker: PvpParticipantShipState,
    target: PvpParticipantShipState,
    /**
     * De ONDE veio o golpe que está a semear isto (gate V28G.P).
     *
     * `cannon` para um acerto direto de tiro, e `saw` para uma instância de
     * dano de uma serra orbital. O parâmetro existe porque a decisão passou a
     * ter duas respostas, e a segunda metade da tabela fechada é quem as separa
     * - nunca uma condição escrita aqui.
     *
     * Sem valor por omissão, pela regra de `damage/origin.ts`: um chamador novo
     * que se esqueça não herda uma classificação que ninguém escolheu para ele.
     */
    origin: PvpDamageOrigin,
  ): void {
    if (target.healthMilli <= 0n) {
      return;
    }
    const stream = this.bleedingRngFor(attacker.participantId);
    const roll = stream.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    /*
     * FERRAMENTA DE DESENVOLVIMENTO, e ela não mexe em número nenhum.
     *
     * A rolagem foi feita e o valor foi consumido acima, portanto o fluxo avança
     * exatamente como avançaria sem isto. O que muda é a COMPARAÇÃO: em vez da
     * rolagem real usa-se ZERO, que cai sempre dentro do intervalo da joia. Um
     * casco sem a joia continua a não marcar, porque forçar a rolagem não é
     * forçar a posse.
     */
    const forced = this.spendForcedBleeding(attacker.participantId);
    const applied = selectBleedingApplication(
      attacker.profile.jewels,
      origin,
      forced ? 0 : roll,
      /*
       * O dano-base AUTORITATIVO deste casco, NESTE instante, e mais nada. A
       * mesma leitura que a loteria ofensiva faz no nascimento de um tiro.
       */
      BigInt(pvpShipBaseDamageMilli(attacker.profile)),
    );
    if (applied === null || applied.damageMilli <= 0n) {
      return;
    }
    /*
     * **A REFLEXÃO DA APLICAÇÃO** (gate V28G.O).
     *
     * O §5 do gate: "nenhuma marca aparece no refletor; a marca aparece
     * imediatamente no atacante original; o refletor torna-se seu autor; o dano
     * milli congelado permanece o original".
     *
     * E a consequência que não custa código nenhum: `DETONAR x N` conta as
     * marcas de que o jogador é AUTOR e nunca perguntou se ele tem a Garra
     * Hemorrágica. Uma marca refletida tem o refletor por autor, portanto
     * aparece no botão dele, obedece à ordem dele e só a essa - o §"ele só pode
     * detonar marcas das quais é autor" é a regra que já existia.
     */
    const mirrored = this.reflects(target, attacker, 'bleed_application', 'original');
    if (mirrored) {
      this.announceReflection(target, attacker, 'bleed_application', 'intercepted');
    }
    /*
     * **Um proc, uma marca, e nunca uma renovação.**
     *
     * `push` e não uma procura por uma marca do mesmo atacante: as marcas são
     * independentes por contrato, cada uma com a sua magnitude congelada, e uma
     * que "renovasse" a anterior seria a mecânica de `PvpEffects` aplicada a uma
     * coisa que não é um efeito de runa. É também o que faz a detonação crescer
     * linearmente: dez acertos marcados são dez parcelas somadas.
     */
    this.bleeding.push({
      id: this.nextBleedingId,
      attackerParticipantId: mirrored ? target.participantId : attacker.participantId,
      targetParticipantId: mirrored ? attacker.participantId : target.participantId,
      damageMilli: applied.damageMilli,
      appliedAt: this.currentTick,
      reflected: mirrored,
    });
    this.nextBleedingId += 1;
  }

  /**
   * **A ORDEM: todas as marcas deste autor rebentam no mesmo tique.**
   *
   * ## Um golpe por ALVO, e nunca um por marca
   *
   * As marcas deste autor são agrupadas pelo casco que as carrega, e cada casco
   * leva **uma** instância de dano igual à soma milli das marcas que ele tem
   * DESTE autor. Um golpe por marca imprimiria dez números onde o contrato pede
   * um, e faria dez clamps de overkill em vez de um - que é aritmética
   * diferente, não apresentação diferente.
   *
   * ## As de outra gente ficam
   *
   * O filtro é pelo autor, portanto as marcas de um aliado no mesmo alvo não são
   * lidas, não são somadas e não são consumidas. Em 3x3 isso é o §"um jogador
   * nunca detona as marcas de um aliado", cumprido pelo agrupamento em vez de
   * por uma verificação escrita.
   *
   * ## A ordem é DETERMINÍSTICA e não a de um `Map`
   *
   * Os alvos são percorridos pela ordem do ROSTER - a mesma em qualquer máquina,
   * derivada do ruleset - e não pela ordem de inserção de um agrupamento nem
   * pela ordem em que as marcas nasceram. A soma de cada alvo é comutativa em
   * `bigint`, portanto só a ordem dos GOLPES precisa de ser fixada, e é.
   *
   * ## A origem é `jewel`, e o que isso desliga sozinho
   *
   * Tudo. `damageOriginMayTriggerJewel('jewel')` é falso, e é essa tabela que faz
   * uma detonação não acionar proc, não acionar Roubo de Vida, não envenenar e
   * não semear outra marca. A profundidade de recursão é um porque não existe
   * aresta no grafo que lhe dê duas.
   *
   * ## Sem marcas, não acontece nada - e nem sequer um evento
   *
   * O §"sem marcas, o comando é um `no-op` seguro" é isto: a bandeira é gasta, o
   * estado não muda, e nenhum evento é emitido. Um evento vazio faria uma cena
   * desenhar um corte carmesim sobre coisa nenhuma.
   */
  private detonateBleeding(attacker: PvpParticipantShipState): void {
    if (this.bleeding.length === 0) {
      return;
    }
    /*
     * Agrupado por alvo, e a chave é o id do casco.
     *
     * Um `Map` é usado para AGRUPAR e nunca para ordenar - a ordem de iteração
     * abaixo vem do roster, precisamente porque a de um `Map` é a de inserção e
     * essa depende de que marca chegou primeiro.
     */
    const byTarget = new Map<PvpParticipantId, typeof this.bleeding>();
    for (const mark of this.bleeding) {
      if (mark.attackerParticipantId !== attacker.participantId) {
        continue;
      }
      const existing = byTarget.get(mark.targetParticipantId);
      if (existing) {
        existing.push(mark);
      } else {
        byTarget.set(mark.targetParticipantId, [mark]);
      }
    }
    if (byTarget.size === 0) {
      return;
    }
    /*
     * As marcas saem ANTES de o dano ser aplicado, e a ordem é uma regra.
     *
     * Um golpe pode matar, e uma morte chama `clearBleedingAround` no meio da
     * passagem. Retirá-las primeiro faz com que a limpeza da morte encontre uma
     * lista de onde elas já saíram, em vez de a encolher enquanto se lê - e faz
     * com que uma detonação que mata o próprio autor (impossível hoje, e não por
     * uma regra escrita aqui) não perca marcas por meio caminho.
     */
    this.bleeding = this.bleeding.filter(
      (mark) => mark.attackerParticipantId !== attacker.participantId,
    );
    const detonated: {
      readonly targetId: PvpParticipantId;
      readonly marks: number;
      readonly applied: bigint;
    }[] = [];
    for (const ship of this.roster.all()) {
      const marks = byTarget.get(ship.participantId);
      if (marks === undefined) {
        continue;
      }

      /*
       * **As duas LINHAGENS são somadas em separado** (gate V28G.O).
       *
       * Um autor pode ter, no mesmo casco, marcas que ele pôs a atacar e marcas
       * que ele ganhou a refletir - o §5 do gate cria essa mistura no momento em
       * que a aplicação de uma marca é intercetada. Elas têm identidades de
       * reflexão DIFERENTES: as originais ainda podem ser intercetadas na
       * detonação, e as refletidas não podem, porque já gastaram a sua única
       * reflexão.
       *
       * Somá-las num número só deixaria uma decidir o destino da outra - uma
       * marca refletida imunizaria dez originais, ou dez originais arrastariam
       * uma refletida para uma segunda reflexão. Por isso a soma é por linhagem,
       * e a rolagem incide só sobre a metade que é elegível.
       */
      const original = marks.filter((mark) => !mark.reflected);
      const mirrored = marks.filter((mark) => mark.reflected);
      const originalMilli = bleedingDetonationMilli(original);
      const mirroredMilli = bleedingDetonationMilli(mirrored);
      /*
       * A interceção da DETONAÇÃO, e só da metade original.
       *
       * O §5: "as marcas já consumidas continuam consumidas; o refletor recebe
       * zero; o dano total e o corte final aparecem no autor original; o
       * refletor recebe autoria pelo dano". A primeira metade já está garantida
       * acima - as marcas saíram da lista ANTES de qualquer dano - e as outras
       * três são o que `resolveReflected` faz.
       */
      const intercepted =
        originalMilli > 0n && this.reflects(ship, attacker, 'bleed_detonation', 'original');
      let applied = 0n;
      if (intercepted) {
        this.resolveReflected(ship, attacker, originalMilli, 'bleed_detonation', null);
        /*
         * A metade refletida cai na mesma, e no alvo: ela não foi intercetada
         * porque não podia sê-lo, e não desaparece por causa da outra metade.
         */
        if (mirroredMilli > 0n) {
          applied = this.applyDamage(ship, mirroredMilli, 'jewel', ship.x, ship.y, attacker, null);
        }
      } else {
        /*
         * UMA instância de dano, igual à SOMA dos milli congelados.
         *
         * Nunca `quantidade x dano atual`: cada marca vale o que valia quando
         * nasceu, e uma nave que melhorou entretanto tem marcas de magnitudes
         * diferentes acumuladas no mesmo alvo.
         *
         * `applyDamage` recusa um alvo já sem vida e devolve zero, o que é
         * exatamente o que se quer: as marcas de um destroço já tinham saído com
         * ele, e um alvo que morra a meio desta passagem não leva um segundo
         * golpe.
         */
        applied = this.applyDamage(
          ship,
          originalMilli + mirroredMilli,
          /* A origem que fecha o grafo. Nunca `cannon`. */
          'jewel',
          ship.x,
          ship.y,
          attacker,
          /* Nenhum tiro trouxe isto: o projétil que a semeou já não existe. */
          null,
        );
      }
      /*
       * O que foi REALMENTE aplicado a ESTE casco, depois do clamp do overkill.
       *
       * Devolvido por `applyDamage` e nunca recalculado: um cliente não tem o
       * perfil, o nível nem as magnitudes congeladas, e a diferença observada na
       * barra move-se também por tiros do mesmo tique.
       *
       * Numa detonação intercetada este número é o que a metade refletida
       * causou, e é zero quando não havia nenhuma - o dano que foi virado é
       * anunciado pelo `reflect`, que é onde o `REFLECT` do §7 vai buscar o seu.
       */
      detonated.push({ targetId: ship.participantId, marks: marks.length, applied });
    }
    /*
     * O evento, e só quando houve mesmo alguma coisa.
     *
     * Um por DETONAÇÃO e não um por alvo, porque a ordem foi uma só. A chave de
     * deduplicação é `(autor, tique)`: um autor detona no máximo uma vez por
     * tique, porque a bandeira é consumida uma vez.
     */
    this.events.push({
      kind: 'bleed',
      effectId: BLEED_DETONATION_EFFECT,
      participantId: attacker.participantId,
      team: attacker.team,
      tick: this.currentTick,
      targets: detonated,
    });
  }

  /**
   * Todas as marcas EM VOLTA de um casco saem juntas: as que ele carregava e as
   * que ele tinha posto.
   *
   * Uma função e não duas, porque os dois caminhos de morte precisam das duas
   * metades e um deles esquecer-se-ia de uma. Uma passagem só sobre a lista.
   */
  private clearBleedingAround(participantId: PvpParticipantId): void {
    if (this.bleeding.length === 0) {
      return;
    }
    this.bleeding = this.bleeding.filter(
      (mark) =>
        mark.targetParticipantId !== participantId && mark.attackerParticipantId !== participantId,
    );
  }

  /** Que acerto direto está forçado agora, gastando o crédito. */
  private spendForcedBleeding(participantId: PvpParticipantId): boolean {
    const credits = this.devBleedingCredits.get(participantId) ?? 0;
    if (credits <= 0) {
      return false;
    }
    this.devBleedingCredits.set(participantId, credits - 1);
    return true;
  }

  /**
   * **DEV/E2E ONLY — arma os próximos `count` acertos diretos de cada cadeira
   * com uma marca de sangramento**, se o casco tiver a joia.
   *
   * Irmã de {@link PvpSimulation.devForcePoison}, com as mesmas garantias e pela
   * mesma razão: a chance real ao topo do contrato é de 13 % por acerto, e
   * esperar por dez marcas numa avaliação manual é uma moeda ao ar.
   *
   * O que ela muda é UMA comparação. A rolagem continua a ser feita e o valor
   * continua a sair do fluxo, portanto ligar isto não desloca uma runa, um proc
   * nem um tique. Não toca em magnitude, autoria, chance real ou instantânea - a
   * marca que sai daqui é construída por `selectBleedingApplication` com o
   * dano-base autoritativo do casco, como qualquer outra.
   *
   * **Não acrescenta a joia.** Um casco sem a Garra Hemorrágica continua a não
   * marcar coisa nenhuma.
   *
   * Chamada apenas pelo objeto da sala, e só quando o ambiente a autoriza. Em
   * produção nada a chama, porque a variável que a autoriza nunca é definida.
   */
  devForceBleeding(count: number): void {
    if (!Number.isSafeInteger(count) || count <= 0) {
      return;
    }
    for (const ship of this.roster.all()) {
      this.devBleedingCredits.set(ship.participantId, count);
    }
  }

  /** Que acerto direto está forçado agora, gastando o crédito. */
  private spendForcedPoison(participantId: PvpParticipantId): boolean {
    const credits = this.devPoisonCredits.get(participantId) ?? 0;
    if (credits <= 0) {
      return false;
    }
    this.devPoisonCredits.set(participantId, credits - 1);
    return true;
  }

  /**
   * **DEV/E2E ONLY — arma os próximos `count` acertos diretos de cada cadeira
   * com um stack de veneno**, se o casco tiver a joia.
   *
   * Irmã de {@link PvpSimulation.devForceVolleyBombs} e de
   * {@link PvpSimulation.devForceVolleyMissiles}, com as mesmas garantias e pela
   * mesma razão: a chance real ao topo do contrato é de 13 % por acerto, e
   * esperar por seis stacks em idades diferentes numa avaliação manual é uma
   * moeda ao ar.
   *
   * O que ela muda é UMA comparação. A rolagem continua a ser feita e o valor
   * continua a sair do fluxo, portanto ligar isto não desloca uma runa, um proc
   * nem um tique. Não toca em magnitude, duração, número de parcelas, autoria,
   * chance real ou instantânea - o stack que sai daqui é construído por
   * `selectPoisonApplication` com o dano-base autoritativo do casco, como
   * qualquer outro.
   *
   * **Não acrescenta a joia.** Um casco sem o Orbe da Víbora continua a não
   * envenenar coisa nenhuma.
   *
   * Chamada apenas pelo objeto da sala, e só quando o ambiente a autoriza. Em
   * produção nada a chama, porque a variável que a autoriza nunca é definida.
   */
  devForcePoison(count: number): void {
    if (!Number.isSafeInteger(count) || count <= 0) {
      return;
    }
    for (const ship of this.roster.all()) {
      this.devPoisonCredits.set(ship.participantId, count);
    }
  }

  /* ---------------------------------------------------------------------- */
  /*  A Égide Espinhosa (gate V28G.O)                                       */
  /* ---------------------------------------------------------------------- */

  /** O fluxo DEFENSIVO desta cadeira, criado à primeira utilização. */
  private reflectRngFor(participantId: PvpParticipantId): SimulationRng {
    const existing = this.reflectRng.get(participantId);
    if (existing) {
      return existing;
    }
    const created = createSimulationRng(
      deriveSimulationSeed(this.seed, PVP_REFLECTION_RNG_LABEL, this.seatIndexOf(participantId)),
    );
    this.reflectRng.set(participantId, created);
    return created;
  }

  /**
   * **A FRONTEIRA AUTORITATIVA: este payload hostil é intercetado?**
   *
   * Chamada nos oito pontos onde um efeito hostil resolveria contra um casco, e
   * em mais nenhum. Cada um deles é um instante em que o payload ainda não
   * causou nada - antes do dano, antes do estado, antes da explosão - porque o
   * §2 do gate exige que um payload refletido não deixe rasto nenhum no
   * refletor.
   *
   * ## A ordem das perguntas é o contrato do §4, e não é arbitrária
   *
   * 1. **a tabela fechada** decide se este par `(linhagem, tipo)` é sequer
   *    intercetável. Um payload já refletido sai aqui, **sem gastar rolagem** -
   *    o §4 diz "resolve normalmente sem nova rolagem", e consumir um valor para
   *    depois o deitar fora faria a sequência do defensor depender de quantos
   *    payloads refletidos passaram por ele, o que é observável;
   * 2. **a autoria** decide se há a quem devolver. Sem autor, auto-dano ou
   *    aliado saem aqui, também sem gastar rolagem: não são instâncias
   *    elegíveis, e uma rolagem sobre uma coisa que não pode refletir é uma
   *    rolagem que muda a sequência por nada;
   * 3. **um casco morto não interceta.** Um destroço não se defende, e devolver
   *    dano em nome dele daria autoria a quem já não está;
   * 4. **UMA rolagem**, do fluxo do DEFENSOR;
   * 5. **o perfil congelado** decide, e é o da SESSÃO. Nada aqui abre inventário
   *    nenhum: `ship.profile.jewels` é o mesmo perfil que a sala congelou quando
   *    a partida começou.
   *
   * ## A afordância troca a COMPARAÇÃO, e nada mais
   *
   * A rolagem já foi feita e o valor já saiu do fluxo, portanto ligá-la não
   * desloca uma runa, um proc, um tique nem outra rolagem - a sequência da sala
   * é idêntica à de uma sala sem ferramenta nenhuma. **Não acrescenta a joia**:
   * um casco sem a Égide Espinhosa continua a não intercetar coisa nenhuma,
   * porque `selectReflection` pergunta ao perfil e não ao crédito.
   */
  private reflects(
    defender: PvpParticipantShipState,
    author: PvpParticipantShipState | null,
    kind: HostilePayloadKind,
    lineage: ReflectionLineage,
  ): boolean {
    if (!reflectionMayIntercept(lineage, kind)) {
      return false;
    }
    /*
     * A autoria, na forma mínima que o domínio conhece.
     *
     * Um casco não é passado inteiro porque `damage/reflection.ts` é folha: ele
     * não sabe o que é um `PvpParticipantShipState`, e não pode saber sem passar
     * a conhecer o duelo. Duas strings são tudo o que a pergunta precisa.
     */
    if (
      !reflectionAuthorIsEligible(
        author === null ? null : { id: author.participantId, team: author.team },
        { id: defender.participantId, team: defender.team },
      )
    ) {
      return false;
    }
    if (defender.healthMilli <= 0n) {
      return false;
    }
    const stream = this.reflectRngFor(defender.participantId);
    const roll = stream.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    const forced = this.spendForcedReflect(defender.participantId, kind);
    return selectReflection(defender.profile.jewels, lineage, kind, forced ? 0 : roll);
  }

  /** Que corte está forçado agora, gastando o crédito. */
  private spendForcedCyclone(participantId: PvpParticipantId): boolean {
    const credits = this.devCycloneCredits.get(participantId) ?? 0;
    if (credits <= 0) {
      return false;
    }
    this.devCycloneCredits.set(participantId, credits - 1);
    return true;
  }

  /**
   * Se ESTE tipo está forçado agora para esta cadeira, gastando o crédito dele.
   *
   * O crédito é por TIPO: um tiro comum intercetado não consome o orçamento do
   * corte, e é isso que deixa uma avaliação manual ver os dois. Ver a nota em
   * {@link PvpSimulation.devReflectCredits}, que descreve o defeito de
   * demonstração que um contador único produzia.
   */
  private spendForcedReflect(participantId: PvpParticipantId, kind: HostilePayloadKind): boolean {
    const byKind = this.devReflectCredits.get(participantId);
    const credits = byKind?.get(kind) ?? 0;
    if (byKind === undefined || credits <= 0) {
      return false;
    }
    byKind.set(kind, credits - 1);
    return true;
  }

  /**
   * **O anúncio de uma reflexão**, num dos seus dois estágios.
   *
   * Um sítio só, para os oito pontos de interceção não escreverem oito objetos
   * ligeiramente diferentes. As posições são as dos CASCOS - o refletor e o
   * destinatário - e nunca a do projétil: o brilho percorre uma silhueta e o
   * feixe liga dois centros, e a bala está na borda do casco no instante da
   * colisão.
   */
  private announceReflection(
    reflector: PvpParticipantShipState,
    recipient: PvpParticipantShipState,
    kind: HostilePayloadKind,
    stage: 'intercepted' | 'landed',
    applied?: bigint,
  ): void {
    this.events.push({
      kind: 'reflect',
      effectId: REFLECTION_EFFECT,
      participantId: reflector.participantId,
      team: reflector.team,
      targetId: recipient.participantId,
      tick: this.currentTick,
      payload: kind,
      stage,
      x: reflector.x,
      y: reflector.y,
      tx: recipient.x,
      ty: recipient.y,
      ...(applied === undefined ? {} : { applied }),
    });
  }

  /**
   * **Uma interceção que resolve JÁ**: o payload vira e o dano cai no autor.
   *
   * Os quatro casos instantâneos - tiro comum, corte, parcela de veneno e
   * detonação - partilham exatamente esta sequência, e partilhá-la é o que faz
   * as quatro terem a mesma semântica em vez de quatro cópias que divergem.
   *
   * ## A origem é `jewel`, e o que isso desliga sozinho
   *
   * Tudo o que o §3 do gate exige. `damageOriginMayTriggerJewel('jewel')` é
   * falso, portanto um payload refletido não sorteia proc, não ativa Roubo de
   * Vida, não envenena, não marca e não aplica uma segunda Reflexão. Nada disto
   * é uma verificação escrita aqui - é a tabela fechada de origens que já
   * existia, e que este gate não precisou de tocar.
   *
   * ## O overkill do REFLETOR não entra
   *
   * O payload é redirecionado **antes** de atingir a vida dele, portanto o que
   * viaja é a magnitude congelada inteira. Um refletor com 3 de vida devolve os
   * 40 do tiro, e não 3: o clamp que existe é o do DESTINATÁRIO, aplicado por
   * `applyDamage` sobre a vida DELE.
   */
  private resolveReflected(
    reflector: PvpParticipantShipState,
    recipient: PvpParticipantShipState,
    damageMilli: bigint,
    kind: HostilePayloadKind,
    projectileId: number | null,
  ): bigint {
    this.announceReflection(reflector, recipient, kind, 'intercepted');
    const applied = this.applyDamage(
      recipient,
      damageMilli,
      /* A origem que fecha o grafo. Nunca `cannon`. */
      'jewel',
      recipient.x,
      recipient.y,
      /* O REFLETOR é o novo autor: a eliminação e a estatística são dele. */
      reflector,
      projectileId,
    );
    if (applied > 0n) {
      this.announceReflection(reflector, recipient, kind, 'landed', applied);
    }
    return applied;
  }

  /**
   * **A BOMBA intercetada: a original desaparece e uma equivalente nasce no
   * refletor** (gate V28G.O).
   *
   * ## O que desaparece, e o que isso quer dizer exatamente
   *
   * Tudo. `detonateBomb` **não é chamada**, portanto não há explosão, não há
   * dano direto, não há dano em área, não há empurrão e não há evento `blast`.
   * O §5 do gate pede as cinco ausências, e a forma de as garantir não é
   * suprimir cinco coisas uma a uma - é não chamar a função que as produz.
   *
   * ## O que nasce, e porque é a MESMA bomba
   *
   * A mesma seleção congelada - `atomicBomb`, com o `blastMilli` e a `entropy`
   * que o atacante original congelou quando a salva saiu - e o mesmo
   * `damageMilli`. Nada é reavaliado contra o perfil do refletor: o §2 é
   * explícito em que magnitude e dano congelado permanecem os originais, e
   * recalcular seria responder "quanto vale a bomba dele" em vez de "quanto
   * valia a bomba que ele mandou".
   *
   * Arte, tamanho, raio, colisão, proximidade, explosão e empurrão são os
   * mesmos porque saem das mesmas constantes: uma bomba refletida é uma bomba, e
   * este método não escreve um único número aprovado.
   *
   * ## A direção, e o que ela é
   *
   * A posição ATUAL do atacante original, no instante da interceção - o §5 pede
   * exatamente isso. Não persegue: uma bomba nunca perseguiu, e dar-lhe
   * perseguição seria mudar a entidade em vez de a redirecionar. A velocidade é
   * a de uma bomba, derivada da mesma redução de 80 % que a salva aplica.
   *
   * ## O teto de projéteis não pode ser ultrapassado
   *
   * Por construção: a original é removida no mesmo passo em que esta entra. O
   * `stepProjectile` que chamou este método devolve `false`, portanto a bomba
   * intercetada não entra nos sobreviventes - e a nova espera em
   * {@link PvpSimulation.pendingShots} até o laço acabar, para não ser
   * percorrida no tique em que nasceu.
   *
   * @returns se a interceção aconteceu. `false` deixa a bomba detonar como
   *   sempre detonou.
   */
  private reflectBomb(
    projectile: PvpParticipantProjectileState,
    defender: PvpParticipantShipState,
    owner: PvpParticipantShipState,
  ): boolean {
    if (
      !this.reflects(
        defender,
        owner,
        'atomic_bomb_proximity',
        projectile.reflected ? 'reflected' : 'original',
      )
    ) {
      return false;
    }
    const selection = projectile.atomicBomb;
    if (selection === null) {
      // Inalcançável: só o ramo da bomba chama isto. Afirmado para que um call
      // site futuro que se engane falhe em vez de refletir um tiro comum.
      throw new RangeError(`projectile ${projectile.id} reflected without a bomb`);
    }
    this.announceReflection(defender, owner, 'atomic_bomb_proximity', 'intercepted');
    /*
     * A direção: do refletor para onde o atacante está AGORA.
     *
     * `normalise` devolve zero quando os dois cascos estão exatamente no mesmo
     * ponto - geometricamente impossível entre dois corpos sólidos, e tratado na
     * mesma - e nesse caso a bomba parte na direção em que o refletor mira, que
     * é a única direção determinística que existe sem inventar uma.
     */
    const toward = normalise(owner.x - defender.x, owner.y - defender.y);
    const heading: IntVector = toward ?? { x: defender.aimX, y: defender.aimY };
    const velocity = scaleDirection(
      heading,
      divTrunc(PVP_PROJECTILE_SPEED_MU_PER_TICK * PVP_BOMB_SPEED_BPS, 10_000),
    );
    const muzzle = scaleDirection(
      heading,
      pvpHullRadiusMu(defender.profile) + PVP_PROJECTILE_RADIUS_MU,
    );
    this.pendingShots.push({
      id: this.nextProjectileId,
      /* O REFLETOR é o novo proprietário: a eliminação e a estatística são dele. */
      ownerParticipantId: defender.participantId,
      ownerTeam: defender.team,
      /* Os dois números congelados do payload original, intactos. */
      damageMilli: projectile.damageMilli,
      /*
       * Sem proc. Um payload refletido não sorteia coisa nenhuma (§3), e a bomba
       * original também não trazia nenhum - ela ocupa a oportunidade ofensiva do
       * projétil que substituiu.
       */
      jewelProc: null,
      atomicBomb: selection,
      x: defender.x + muzzle.x,
      y: defender.y + muzzle.y,
      vx: velocity.x,
      vy: velocity.y,
      life: PVP_PROJECTILE_LIFETIME_TICKS,
      /* A linhagem que fecha o grafo: esta bomba não pode ser refletida outra vez. */
      reflected: true,
      /* Sem explosão de carta e sem perseguição: um tiro comum (gate V28H.L). */
      cardBlast: null,
      homingTargetId: null,
    });
    this.nextProjectileId += 1;
    return true;
  }

  /**
   * **DEV/E2E ONLY — arma as próximas `count` interceções de cada cadeira.**
   *
   * Irmã de {@link PvpSimulation.devForceVolleyBombs}, com as mesmas garantias e
   * pela mesma razão: a chance real ao topo do contrato é de 6 % por instância
   * hostil, e esperar por ela numa avaliação manual é uma moeda ao ar.
   *
   * O que ela muda é UMA comparação. A rolagem continua a ser feita e o valor
   * continua a sair do fluxo, portanto ligar isto não desloca uma runa, um proc
   * nem um tique. Não toca em magnitude, autoria, linhagem, tabela fechada,
   * chance real nem instantânea - e sobretudo **não desliga a recusa da
   * recursão**: um payload já refletido nem sequer chega à comparação, porque a
   * tabela o recusa antes de a rolagem ser feita.
   *
   * **Não acrescenta a joia.** Um casco sem a Égide Espinhosa continua a não
   * intercetar coisa nenhuma.
   *
   * Chamada apenas pelo objeto da sala, e só quando o ambiente a autoriza. Em
   * produção nada a chama, porque a variável que a autoriza nunca é definida.
   */
  devForceReflect(
    count: number,
    /**
     * Que tipos armar. Omitido, arma **todos** - o comportamento de sempre.
     *
     * Uma lista é o que permite escolher o payload seguinte: armar só
     * `cyclone_cut` deixa o tiro comum resolver normalmente, e um tiro que
     * resolve é o que faz nascer o corte que se quer ver virado. Um tipo fora da
     * lista fechada é ignorado em silêncio - é uma ferramenta, e um nome mal
     * escrito num ambiente de avaliação não deve derrubar uma sala.
     */
    kinds: readonly HostilePayloadKind[] = HOSTILE_PAYLOAD_KINDS,
  ): void {
    if (!Number.isSafeInteger(count) || count <= 0) {
      return;
    }
    const wanted = kinds.filter((kind) =>
      (HOSTILE_PAYLOAD_KINDS as readonly string[]).includes(kind),
    );
    if (wanted.length === 0) {
      return;
    }
    for (const ship of this.roster.all()) {
      const byKind = new Map<HostilePayloadKind, number>();
      for (const kind of wanted) {
        byKind.set(kind, count);
      }
      this.devReflectCredits.set(ship.participantId, byKind);
    }
  }

  /**
   * **DEV/E2E ONLY — arma os próximos `count` projéteis de cada cadeira com um
   * corte de Ciclone**, se o casco tiver a joia.
   *
   * A sexta e última afordância de proc, e existe porque a avaliação manual
   * deste gate precisa de ver um corte REDIRECIONADO: sem ela, demonstrar isso
   * seria esperar que 13 % e 6 % calhassem no mesmo projétil.
   *
   * As mesmas garantias das outras cinco: substitui o valor contra o qual a
   * rolagem é comparada pelo INÍCIO do intervalo do Ciclone, que por construção
   * cai dentro dele. A rolagem continua a ser feita e os dois valores continuam
   * a sair do fluxo. **Não acrescenta a joia.**
   */
  devForceCyclone(count: number): void {
    if (!Number.isSafeInteger(count) || count <= 0) {
      return;
    }
    for (const ship of this.roster.all()) {
      this.devCycloneCredits.set(ship.participantId, count);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  **O Diamante Serrilhado** (gate V28G.P)                           */
  /* ------------------------------------------------------------------ */

  /**
   * O fluxo das serras desta cadeira, criado à primeira utilização.
   *
   * **Um fluxo PRÓPRIO, e a razão é a mesma de sempre:** partilhá-lo com a
   * loteria ofensiva, com o veneno ou com o sangramento deslocaria a sequência
   * deles. Um casco com o Ciclone e com o Diamante teria a sequência dos tiros
   * mudada pelo número de contactos que as lâminas fizessem - que é uma
   * quantidade que depende de onde os inimigos andaram.
   *
   * Com um fluxo à parte, uma partida sem esta joia consome exatamente a mesma
   * entropia que consumia antes deste gate, byte a byte. Este mapa fica vazio, e
   * um fluxo que nunca é criado não gasta nada.
   */
  private sawRngFor(participantId: PvpParticipantId): SimulationRng {
    const existing = this.sawRng.get(participantId);
    if (existing) {
      return existing;
    }
    const created = createSimulationRng(
      deriveSimulationSeed(this.seed, PVP_SAW_RNG_LABEL, this.seatIndexOf(participantId)),
    );
    this.sawRng.set(participantId, created);
    return created;
  }

  /**
   * **Um tique das serras: a órbita anda, e o que ela tocar leva.**
   *
   * ## A ordem, e porque ela é esta
   *
   * A fase avança primeiro, para todos, e só depois é que alguém corta. Sem
   * isso, a primeira nave do roster cortaria com a órbita nova e a última com a
   * órbita velha - uma vantagem que sairia da ordem em que a sala foi
   * construída, que é exatamente o que o contrato proíbe.
   *
   * ## Um casco morto não gira e não corta
   *
   * O §"ao morrer a nave proprietária, todas as serras deixam de causar dano
   * imediatamente" é cumprido aqui e em `applyDamage`, e as duas metades são
   * diferentes: aqui um destroço nem sequer é visitado, e lá os relógios de
   * contacto dele - dos dois lados - são apagados no instante da morte.
   */
  private tickSaws(): void {
    for (const ship of this.roster.all()) {
      const level = sawLevelOf(ship.profile.jewels);
      if (level <= 0 || ship.healthMilli <= 0n) {
        /*
         * Sem joia ou sem vida: a órbita não existe, e a fase volta ao princípio.
         *
         * A saída antecipada não é uma otimização - é o que faz uma sala sem
         * esta joia produzir exatamente o estado que produzia antes deste gate,
         * fase e resto incluídos.
         */
        ship.sawPhase = SAW_INITIAL_PHASE;
        continue;
      }
      ship.sawPhase = sawAdvancePhase(ship.sawPhase, PVP_TICK_MS, level);
    }
    for (const ship of this.roster.all()) {
      const level = sawLevelOf(ship.profile.jewels);
      if (level <= 0 || ship.healthMilli <= 0n) {
        continue;
      }
      this.resolveSawContacts(ship, level);
    }
  }

  /**
   * **As lâminas deste casco contra os inimigos dele, neste tique.**
   *
   * ## O varrimento, e a conta que o justifica
   *
   * A órbita é amostrada em {@link PVP_PROJECTILE_SUBSTEPS} posições dentro do
   * tique, exatamente como um projétil é. A razão é a mesma e a conta está
   * fechada: ao nível 30 a órbita avança 25 graus por tique, o que a 40 300 mu
   * de raio é uma corda de 17 442 mu; somando o deslocamento máximo do dono
   * (17 000) e do alvo (17 000), o movimento relativo chega a 51 442 mu contra
   * uma janela de contacto de 69 680 mu de diâmetro. Não haveria atravessamento
   * mesmo sem varrer - a margem seria 1,35 - e varrer põe-na em 1,8, que é a
   * mesma disciplina que os projéteis já seguem.
   *
   * Amostrado a partir da fase ANTERIOR e recalculado do início em cada
   * sub-passo, nunca acumulado: quatro truncagens somadas encurvariam a órbita.
   *
   * ## A ordem: sub-passo, depois lâmina, depois inimigo
   *
   * O tempo por fora, para os contactos serem resolvidos pela ordem em que
   * aconteceram. Lâmina por dentro do tempo e inimigo por dentro da lâmina, os
   * dois em ordem canónica, para duas máquinas resolverem os mesmos empates.
   *
   * ## A fase larga: um inimigo longe nem chega a ser amostrado
   *
   * Um teste de distância por inimigo, antes do laço, contra o alcance máximo da
   * órbita. Sem ele, uma caçada de 3x3 pagaria `4 lâminas x 4 sub-passos x 5
   * inimigos` testes por nave por tique para descobrir que ninguém estava perto.
   */
  private resolveSawContacts(ship: PvpParticipantShipState, level: number): void {
    const enemies = this.roster.enemiesOf(ship.team);
    if (enemies.length === 0) {
      return;
    }
    const count = sawBladeCount(ship.profile.jewels);
    if (count <= 0) {
      return;
    }
    /*
     * A órbita e a lâmina são medidas no casco DESTE dono (gate V28G.R).
     *
     * Uma nave maior leva as suas serras consigo - a mesma proporção, um raio
     * maior - exatamente como leva os seus canhões. O que muda é o casco; a
     * proporção nunca muda.
     */
    const ownerRadius = pvpHullRadiusMu(ship.profile);
    const orbit = sawOrbitRadius(ownerRadius);
    const bladeRadius = sawBladeRadius(ownerRadius);
    /*
     * O contacto soma o raio do ALVO, e por isso é por alvo.
     *
     * Antes era uma constante partilhada, e podia sê-lo enquanto todos os cascos
     * mediam o mesmo. Uma lâmina toca um alvo grande mais cedo do que um pequeno,
     * e uma medida partilhada faria a serra atravessar o desenho de um deles.
     */
    const contactRadiusOf = (enemy: PvpParticipantShipState): number =>
      bladeRadius + pvpHullRadiusMu(enemy.profile);
    /* O mais longe que uma lâmina deste casco pode tocar, medido do centro dele. */
    const near = enemies.filter((enemy) => {
      if (enemy.healthMilli <= 0n) {
        return false;
      }
      const reach = orbit + contactRadiusOf(enemy);
      return distanceSquared(ship.x, ship.y, enemy.x, enemy.y) <= reach * reach;
    });
    if (near.length === 0) {
      return;
    }
    /*
     * O avanço BRUTO deste tique, antes de a volta fechar.
     *
     * Recalculado em vez de guardado: `sawAdvancePhase` já correu e devolveu a
     * fase enrolada, e subtrair duas fases enroladas daria um número negativo na
     * volta em que a órbita passou o zero.
     */
    const advance = PVP_TICK_MS * sawSpeedNumerator(level);
    const previous =
      (((ship.sawPhase - advance) % SAW_ORBIT_PHASE_UNITS) + SAW_ORBIT_PHASE_UNITS) %
      SAW_ORBIT_PHASE_UNITS;
    for (let step = 1; step <= PVP_PROJECTILE_SUBSTEPS; step += 1) {
      const phase =
        (previous + divTrunc(advance * step, PVP_PROJECTILE_SUBSTEPS)) % SAW_ORBIT_PHASE_UNITS;
      for (let index = 0; index < count; index += 1) {
        const direction = sawDirection(sawBladeStep(phase, index, count));
        const bladeX = ship.x + divTrunc(direction.x * orbit, SAW_VECTOR_SCALE);
        const bladeY = ship.y + divTrunc(direction.y * orbit, SAW_VECTOR_SCALE);
        for (const enemy of near) {
          if (enemy.healthMilli <= 0n) {
            continue;
          }
          if (!this.sawPairDue(ship, index, enemy, level)) {
            continue;
          }
          const contactRadius = contactRadiusOf(enemy);
          if (distanceSquared(bladeX, bladeY, enemy.x, enemy.y) > contactRadius * contactRadius) {
            continue;
          }
          this.sawStrike(ship, index, enemy, level, bladeX, bladeY);
        }
      }
    }
  }

  /** O relógio deste par serra→alvo, ou `undefined` se eles nunca se tocaram. */
  private sawPairOf(
    ownerParticipantId: PvpParticipantId,
    bladeIndex: number,
    targetParticipantId: PvpParticipantId,
  ): PvpSawContactState | undefined {
    return this.sawContacts.find(
      (contact) =>
        contact.ownerParticipantId === ownerParticipantId &&
        contact.bladeIndex === bladeIndex &&
        contact.targetParticipantId === targetParticipantId,
    );
  }

  /**
   * **Se ESTE par serra→alvo já pode voltar a doer.**
   *
   * Um par que nunca se tocou está sempre pronto: uma lâmina que alcança um
   * inimigo cobra no instante em que o alcança, e não um intervalo depois.
   */
  private sawPairDue(
    owner: PvpParticipantShipState,
    bladeIndex: number,
    target: PvpParticipantShipState,
    level: number,
  ): boolean {
    const contact = this.sawPairOf(owner.participantId, bladeIndex, target.participantId);
    if (contact === undefined) {
      return true;
    }
    return sawContactDue((this.currentTick - contact.lastContactTick) * PVP_TICK_MS, level);
  }

  /**
   * **Uma instância de dano de UMA lâmina sobre UM casco.**
   *
   * ## O relógio é marcado ANTES de o dano existir
   *
   * Porque o que ele conta é o CONTACTO, e o contacto aconteceu. Marcá-lo depois
   * deixaria uma lâmina que encontrou um casco já sem vida - ou um contacto
   * intercetado pela Égide - livre para voltar a tentar no sub-passo seguinte, o
   * que é uma serra a bater quatro vezes num tique.
   *
   * Pela mesma razão o CARRY é consumido aqui: o orçamento produziu esta
   * instância, e quem a recebeu não muda quanto ela custou a produzir.
   *
   * ## A reflexão, na sua própria fronteira
   *
   * O payload é a instância de dano, e não a lâmina. Um contacto intercetado
   * devolve o dano ao dono das serras e **não transfere serra nenhuma**: a
   * órbita continua onde estava, o refletor continua sem lâminas, e o que mudou
   * de destino foi um número. É o mesmo desenho aprovado da Égide, no casco
   * certo, sem uma linha de arte nova.
   *
   * ## O que um contacto confirmado pode semear, e o que nunca pode
   *
   * Veneno, Sangramento, Ciclone e Roubo de Vida - os quatro que o contrato
   * V28G.P nomeia. O Roubo de Vida entra sozinho, dentro de `applyDamage`, pela
   * tabela fechada; os outros três são sorteados aqui, do fluxo próprio das
   * serras. O Núcleo Atômico e o Arsenal Ígneo não entram, e não é por não
   * haver chamador: `damageOriginMayTrigger` recusa-os pelo par
   * `(origem, efeito)`, portanto escrever o chamador que falta continuaria a não
   * produzir nada.
   */
  private sawStrike(
    owner: PvpParticipantShipState,
    bladeIndex: number,
    target: PvpParticipantShipState,
    level: number,
    bladeX: number,
    bladeY: number,
  ): void {
    const existing = this.sawPairOf(owner.participantId, bladeIndex, target.participantId);
    if (existing === undefined) {
      this.sawContacts.push({
        ownerParticipantId: owner.participantId,
        bladeIndex,
        targetParticipantId: target.participantId,
        lastContactTick: this.currentTick,
      });
    } else {
      existing.lastContactTick = this.currentTick;
    }
    const resolved = sawContactDamageMilli(
      /*
       * O dano-base AUTORITATIVO deste casco, NESTE instante, e mais nada: sem a
       * Chama, sem os Corações, sem a runa de Dano Dobrado e sem outro proc. A
       * mesma leitura que todas as outras joias fazem.
       */
      BigInt(pvpShipBaseDamageMilli(owner.profile)),
      level,
      owner.sawCarryMilli,
    );
    owner.sawCarryMilli = resolved.carry;
    if (resolved.damageMilli <= 0n) {
      return;
    }
    /*
     * A identidade desta instância, tirada do MESMO contador dos projéteis.
     *
     * Um espaço de ids só, e é isso que impede uma colisão. A cena deduplica um
     * proc por este número - `seenProcs`, em `pvp/connection.ts` - portanto um
     * contador próprio que recomeçasse noutro sítio faria um cliente descartar
     * um sifão novo por o julgar um antigo.
     */
    const contactId = this.nextProjectileId;
    this.nextProjectileId += 1;
    if (this.reflects(target, owner, 'saw_contact', 'original')) {
      this.resolveReflected(target, owner, resolved.damageMilli, 'saw_contact', contactId);
      return;
    }
    const applied = this.applyDamage(
      target,
      resolved.damageMilli,
      'saw',
      bladeX,
      bladeY,
      owner,
      contactId,
    );
    if (applied <= 0) {
      return;
    }
    this.sawTriggers(owner, target, contactId);
  }

  /**
   * **Os três sorteios que um contacto confirmado faz**, do fluxo das serras.
   *
   * Pela ordem: o Ciclone, o Veneno e a Garra. A ordem é o contrato do fluxo -
   * mudá-la muda toda a sequência a partir dali - e é a mesma ordem em que um
   * acerto de canhão os resolve, para as duas fontes não terem semânticas
   * diferentes.
   *
   * ## As três rolagens acontecem mesmo sem as joias
   *
   * Como acontecem num acerto de canhão: o valor sai do fluxo e o seletor
   * responde `null` porque o perfil não tem a peça. Fazer a rolagem depender da
   * posse faria a sequência de um casco depender do que ele tem encaixado, e
   * duas re-simulações do mesmo duelo com perfis lidos por ordens diferentes
   * divergiriam.
   *
   * ## Sem ferramentas de desenvolvimento
   *
   * `devForcePoison`, `devForceBleeding` e `devForceCyclone` são sobre o TIRO, e
   * gastar um crédito aqui roubaria à salva seguinte o efeito que o operador
   * pediu para ver. Uma avaliação manual das serras não precisa deles: ao nível
   * 30 há vinte contactos por segundo, e 13 % de chance chega a um stack em
   * menos de um segundo.
   */
  private sawTriggers(
    owner: PvpParticipantShipState,
    target: PvpParticipantShipState,
    contactId: number,
  ): void {
    const stream = this.sawRngFor(owner.participantId);
    const procRoll = stream.nextIntInclusive(0, PROC_CHANCE_DENOMINATOR - 1);
    const entropy = stream.nextIntInclusive(0, 0xffff_ffff);
    const selection = selectOutgoingProc(
      owner.profile.jewels,
      'saw',
      procRoll,
      entropy,
      BigInt(pvpShipBaseDamageMilli(owner.profile)),
    );
    if (selection !== null && selection.cutMilli > 0n && target.healthMilli > 0n) {
      this.applySawProc(owner, target, selection, contactId);
    }
    this.poisonDirectHit(owner, target, 'saw');
    this.bleedingDirectHit(owner, target, 'saw');
  }

  /**
   * O corte do Ciclone que um contacto de serra originou.
   *
   * A gémea de {@link PvpSimulation.applyJewelProc}, e separada dela porque a
   * outra lê a seleção CONGELADA de um projétil e esta recebe uma seleção que
   * acabou de nascer. Fundi-las obrigaria a inventar um projétil para o contacto
   * ter onde guardar a sua, que é exatamente a mentira que a instantânea teria
   * de levar.
   *
   * A reflexão é a do corte e não a do contacto: o §4 do gate V28G.O manda que
   * um proc com magnitude, alvo e instante próprios ganhe a SUA rolagem, e o
   * corte tem os três. Um contacto intercetado nunca chega aqui - `sawStrike`
   * devolve antes.
   */
  private applySawProc(
    owner: PvpParticipantShipState,
    target: PvpParticipantShipState,
    selection: JewelProcSelection,
    contactId: number,
  ): void {
    if (this.reflects(target, owner, 'cyclone_cut', 'original')) {
      const reflected = this.resolveReflected(
        target,
        owner,
        selection.cutMilli,
        'cyclone_cut',
        contactId,
      );
      if (reflected <= 0) {
        return;
      }
      this.events.push({
        kind: 'proc',
        procId: selection.id,
        participantId: target.participantId,
        team: target.team,
        targetId: owner.participantId,
        projectileId: contactId,
        tick: this.currentTick,
        x: owner.x,
        y: owner.y,
      });
      return;
    }
    const applied = this.applyDamage(
      target,
      selection.cutMilli,
      /* A origem que fecha o grafo: um corte não origina outro. */
      'jewel',
      target.x,
      target.y,
      owner,
      contactId,
    );
    if (applied <= 0) {
      return;
    }
    this.events.push({
      kind: 'proc',
      procId: selection.id,
      participantId: owner.participantId,
      team: owner.team,
      targetId: target.participantId,
      projectileId: contactId,
      tick: this.currentTick,
      x: target.x,
      y: target.y,
    });
  }

  /**
   * Apaga os relógios de contacto de um casco, **dos dois lados**.
   *
   * As lâminas dele deixam de ter memória de quem tocaram, e as lâminas dos
   * outros deixam de ter memória de o terem tocado. As duas metades numa
   * chamada, para não haver um caminho de morte que se lembre de uma e esqueça a
   * outra - a mesma disciplina de `clearBleedingAround`.
   */
  private clearSawContactsAround(participantId: PvpParticipantId): void {
    if (this.sawContacts.length === 0) {
      return;
    }
    this.sawContacts = this.sawContacts.filter(
      (contact) =>
        contact.ownerParticipantId !== participantId &&
        contact.targetParticipantId !== participantId,
    );
  }

  /** O fluxo de SELEÇÃO DE ALVO desta cadeira, criado à primeira utilização. */
  private missileRngFor(participantId: PvpParticipantId): SimulationRng {
    const existing = this.missileRng.get(participantId);
    if (existing) {
      return existing;
    }
    const created = createSimulationRng(
      deriveSimulationSeed(
        this.seed,
        PVP_MISSILE_TARGET_RNG_LABEL,
        this.seatIndexOf(participantId),
      ),
    );
    this.missileRng.set(participantId, created);
    return created;
  }

  /** O fluxo por salva desta cadeira, criado à primeira utilização. */
  private bombRngFor(participantId: PvpParticipantId): SimulationRng {
    const existing = this.bombRng.get(participantId);
    if (existing) {
      return existing;
    }
    const created = createSimulationRng(
      deriveSimulationSeed(this.seed, PVP_VOLLEY_BOMB_RNG_LABEL, this.seatIndexOf(participantId)),
    );
    this.bombRng.set(participantId, created);
    return created;
  }

  /** O índice CANÓNICO da cadeira. Nunca a posição no roster. */
  private seatIndexOf(participantId: PvpParticipantId): number {
    const index = pvpCanonicalSeats(this.ruleset).findIndex(
      (seat) => seat.participantId === participantId,
    );
    if (index < 0) {
      throw new RangeError(`unknown participant for the proc stream: ${participantId}`);
    }
    return index;
  }

  /**
   * **Os dois projéteis dos canhões temporários** (gate V28H.J).
   *
   * ## Porque são dois, e não uma salva maior
   *
   * Porque são dois canhões. O contrato dos Super Canhões diz "cada ataque
   * dispara exactamente um projétil por canhão", e no duelo a salva do casco é
   * um projétil - portanto uma salva com os canhões montados são três tiros: o
   * do casco, e um de cada canhão.
   *
   * ## Porque não há bomba nem míssil aqui
   *
   * Porque a rolagem POR SALVA já correu, uma vez, para a salva do casco - e é a
   * unidade da Bomba e do Míssil. Sortear outra vez por causa de dois canhões
   * daria a quem os tem o triplo das bombas, que é uma decisão de equilíbrio que
   * ninguém tomou. O que corre aqui é a rolagem POR PROJÉTIL, que é a unidade do
   * Ciclone, e ela corre uma vez por cada um dos dois.
   *
   * ## Onde eles nascem
   *
   * Nas bocas dos dois canhões, que estão de lado em relação ao casco: o mesmo
   * afastamento lateral que a caça desenha, convertido para as milli-unidades da
   * arena do duelo. O tiro sai na direcção da mira, como o do casco.
   */
  /**
   * **O Turbilhão de Lâminas, por um tique** (gate V28H.L).
   *
   * ## Porque não é uma entidade
   *
   * Porque a lâmina não tem vida, não é alvo, não colide com nada e não
   * sobrevive ao dono. Ela é uma PROPRIEDADE do casco enquanto a carta está
   * activa - exactamente como as serras da joia - e uma entidade seria um
   * objecto na instantânea, no fio e no renderer para dizer o que um
   * temporizador já diz.
   *
   * ## O intervalo é da CARTA, e é por isso que ele vive no acumulador dela
   *
   * `attackAccumulatorMs` é o relógio próprio desta habilidade, e já
   * atravessava a instantânea por causa dos Super Canhões. Um casco encostado
   * leva um golpe a cada 400 ms, e não um por tique: sem o acumulador, o
   * Turbilhão cobrava oito vezes por segundo.
   *
   * ## Apanha TODOS os que estão ao alcance, e não o mais próximo
   *
   * "Atinge inimigos próximos", no plural, e é o que separa esta carta das
   * quatro que escolhem um alvo. Um casco inalvejável não é apanhado - a lâmina
   * é uma coisa que aterra - e um invisível é, porque ele está mesmo ali.
   *
   * A origem é `card`: o dano especial de uma carta não activa joias, e é a
   * tabela fechada que o garante.
   */
  private tickBladeStorm(ship: PvpParticipantShipState): void {
    const card = ship.cards.find(
      (entry) => entry.definitionId === 'card_blade_storm' && entry.activeRemainingMs > 0,
    );
    if (card === undefined || ship.healthMilli <= 0n) {
      return;
    }
    const balance = cardBalance('card_blade_storm');
    if (balance === null) {
      return;
    }
    card.attackAccumulatorMs += PVP_TICK_MS;
    if (card.attackAccumulatorMs < balance.hitIntervalMs) {
      return;
    }
    card.attackAccumulatorMs -= balance.hitIntervalMs;
    const reach = Math.trunc((pvpHullRadiusMu(ship.profile) * balance.reachBps) / 10_000);
    const damageMilli =
      (pvpShotDamageMilli(
        ship.profile,
        ship.effects.doubleDamageTicks > 0 ? PVP_RUNE_DAMAGE_BPS : 10_000,
      ) *
        BigInt(balance.damageBps)) /
      10_000n;
    if (damageMilli <= 0n) {
      return;
    }
    for (const enemy of this.roster.enemiesOf(ship.team)) {
      if (enemy.healthMilli <= 0n || !cardIsHittable(enemy.cardEffects)) {
        continue;
      }
      const hitRadius = reach + pvpHullRadiusMu(enemy.profile);
      if (distanceSquared(ship.x, ship.y, enemy.x, enemy.y) > hitRadius * hitRadius) {
        continue;
      }
      this.applyDamage(enemy, damageMilli, 'card', enemy.x, enemy.y, ship, null);
    }
  }

  /**
   * **A carga acabou: a habilidade sai** (gate V28H.L).
   *
   * Duas cartas passam por aqui, e as duas fazem coisas diferentes com o mesmo
   * instante:
   *
   * - a **Esfera Ígnea** lança UM projétil com a sua própria explosão, e a
   *   habilidade acaba aí - a duração dela é zero, portanto `activeRemainingMs`
   *   já era só a carga;
   * - a **Lança Solar** não lança nada: o feixe é ela própria, e o que este
   *   instante faz é deixá-la passar de "a carregar" para "a arder". Não há
   *   trabalho a fazer, e o `switch` diz isso em vez de o deixar implícito.
   *
   * O Catolé também carrega? **Não.** Ele é teleguiado e sai no instante da
   * ordem - o seu alvo é escolhido em `planGuidedBlast`, e um Catolé com carga
   * seria uma bomba que anuncia para onde vai antes de existir.
   */
  private releaseChargedCard(ship: PvpParticipantShipState, card: CardAbilityState): void {
    const balance = cardBalance(card.definitionId);
    if (balance === null || ship.healthMilli <= 0n) {
      return;
    }
    if (balance.effect === 'charged_projectile') {
      /* Com a FORÇA que a carga alcançou, e não sempre cheia (gate V28H.U). */
      this.launchCardBlast(ship, balance, null, card.chargeStrengthBps);
      /*
       * **E o arrefecimento começa AQUI** (gate V28I.C), no instante em que a
       * esfera sai - e não no da ordem, quinze segundos antes. Este é o caminho
       * por onde passam as duas maneiras de ela sair: a tecla solta e o fim
       * natural da carga.
       */
      cardBeginCooldown(card);
      return;
    }
    if (balance.effect === 'charged_beam') {
      /*
       * **A primeira parcela cai no instante em que o feixe acende.**
       *
       * O acumulador arranca CHEIO, e é um fencepost com consequência: sem
       * isto, a primeira parcela caía um intervalo depois de acender e a última
       * não caía de todo - o feixe acaba exactamente no instante em que ela
       * venceria, e `ageCards` corre antes das habilidades. Um feixe de 1,2 s a
       * 200 ms entregava cinco parcelas em vez de seis.
       *
       * Acender e bater ao mesmo tempo é também o que se vê: a carga é o aviso,
       * e o fim dela é o golpe.
       */
      const card = ship.cards.find((entry) => entry.definitionId === 'card_solar_lance');
      if (card !== undefined) {
        card.attackAccumulatorMs = balance.hitIntervalMs - PVP_TICK_MS;
      }
    }
  }

  /**
   * **Um projétil com explosão de CARTA**, teleguiado ou não.
   *
   * A Esfera Ígnea e o Catolé saem por aqui, e a única diferença entre os dois
   * é o `target`: um voa a direito, o outro persegue. O dano e o raio são da
   * carta - lidos da tabela, escalados pelo dano-base de quem lançou - e o
   * projétil nasce com a explosão dentro dele, congelada, exactamente como uma
   * Bomba Atômica nasce com a sua magnitude.
   *
   * A origem do dano é `card`, portanto nada disto activa uma joia.
   */
  private launchCardBlast(
    ship: PvpParticipantShipState,
    balance: CardBalance,
    target: PvpParticipantShipState | null,
    /**
     * A força da carga, em bps (gate V28H.U).
     *
     * A Esfera Ígnea é carregada enquanto a tecla está premida, e o que ela
     * produz cresce com o tempo aguentado: **a bola e o dano**. O Catolé não
     * carrega e passa cheio, que é o que `CARD_CHARGE_FULL_BPS` significa.
     */
    strengthBps: number = CARD_CHARGE_FULL_BPS,
  ): void {
    if (this.shots.length >= PVP_MAX_PROJECTILES) {
      return;
    }
    const damageMilli =
      (pvpShotDamageMilli(
        ship.profile,
        ship.effects.doubleDamageTicks > 0 ? PVP_RUNE_DAMAGE_BPS : 10_000,
      ) *
        BigInt(balance.damageBps)) /
      10_000n;
    /*
     * O raio do Catolé é uma DECISÃO APROVADA - "6x o raio da Bomba Atômica" -
     * e por isso é lido da constante que a multiplica, e não dos basis points.
     * Os bps da tabela dizem a mesma coisa para ela ser legível de uma vez; a
     * prova compara os dois, e é ela que impede a tabela de mentir.
     */
    const radiusMu =
      balance.effect === 'guided_blast'
        ? PVP_CATOLE_BLAST_RADIUS_MU
        : /*
           * E o RAIO da esfera cresce com a carga (gates V28H.U e V28H.V).
           *
           * "Quanto maior o tempo pressionado, maiores ficam a bola e o dano" -
           * e a bola desenhada sai deste número, portanto o que se vê é o que
           * apanha.
           *
           * `cardSphereRadiusFor` e já não a do feixe: com nove segundos de
           * carga, o chão de 40% da espessura dava uma bola quase cheia num
           * toque, e o crescimento - que é o que esta carta é - não se lia.
           */
          Math.trunc(
            cardSphereRadiusFor(
              /* O alcance DESTA arena (gate V28H.Z): o da caça é dez vezes menor. */
              (pvpHullRadiusMu(ship.profile) * PVP_FIREBALL_REACH_BPS) / 10_000,
              strengthBps,
            ),
          );
    /*
     * **Mais lento do que um tiro**, e é geometria e não gosto.
     *
     * Uma perseguição fecha quando o raio da curva é menor do que o raio a que
     * ela apanha o alvo. O raio da curva é a velocidade a dividir pelo ângulo
     * que ela corrige por tique; à velocidade de um tiro comum, o Catolé
     * orbitava o adversário para sempre. Abrandá-lo fecha a curva - e uma bomba
     * colossal lenta é também o que a descrição dela sugere.
     */
    /*
     * E a Esfera vai 30% mais depressa do que o Catolé (gate V28H.W): ela sai a
     * direito, portanto não tem curva de perseguição para a velocidade abrir.
     */
    const speedBps =
      balance.effect === 'guided_blast' ? PVP_CARD_PROJECTILE_SPEED_BPS : PVP_FIREBALL_SPEED_BPS;
    const velocity = scaleDirection(
      { x: ship.aimX, y: ship.aimY },
      Math.trunc((PVP_PROJECTILE_SPEED_MU_PER_TICK * speedBps) / 10_000),
    );
    this.shots.push({
      id: this.nextProjectileId,
      ownerParticipantId: ship.participantId,
      ownerTeam: ship.team,
      /*
       * Zero de dano DIRECTO: uma carta destas não bate no casco em que toca,
       * ela rebenta. Todo o dano dela é o da explosão, e é assim que o número
       * que o jogador lê é um só.
       */
      damageMilli: 0n,
      jewelProc: null,
      atomicBomb: null,
      cardBlast: {
        kind: balance.effect === 'guided_blast' ? 'catole' : 'fireball',
        radiusMu,
        damageMilli,
      },
      homingTargetId: target === null ? null : target.participantId,
      x: ship.x,
      y: ship.y,
      vx: velocity.x,
      vy: velocity.y,
      /*
       * **Um tempo de vida PRÓPRIO, e maior** (gate V28H.L).
       *
       * Um tiro comum morre depressa porque é barato e há muitos. Uma Esfera
       * Ígnea custa 22 s e um Catolé custa 60 s, e a arena passou a ter 24 000
       * unidades de largura - com o tempo de um tiro comum, o Catolé expirava a
       * meio caminho e a palavra "teleguiada" não queria dizer nada.
       */
      life: PVP_CARD_PROJECTILE_LIFETIME_TICKS,
      reflected: false,
    });
    this.nextProjectileId += 1;
  }

  /**
   * **A Lança Solar, por um tique** (ADR 0065, gate V28H.L).
   *
   * ## O feixe é um EVENTO com duração, e cobra em parcelas
   *
   * É a terceira das cinco restrições da ADR, e a que a mantém honesta: o feixe
   * não causa dano contínuo. Ele cobra de `hitIntervalMs` em `hitIntervalMs`,
   * e **cada parcela imprime o seu próprio número** - exactamente como um tiro
   * imprime o seu. O §"cada golpe lógico imprime o seu número" fica cumprido;
   * o que muda é a forma do que causa o golpe.
   *
   * ## A geometria: um segmento, e a distância de cada casco a ele
   *
   * Do casco de quem lança, ao longo da mira, com o comprimento da carta. Um
   * inimigo é atingido quando o CENTRO dele fica a menos de meia espessura mais
   * o seu próprio raio do segmento - a mesma aritmética borda-a-borda que um
   * tiro usa, e tudo em inteiros.
   *
   * Um casco inalvejável não é atingido: o feixe é uma coisa que aterra.
   */
  private tickSolarLance(ship: PvpParticipantShipState): void {
    const card = ship.cards.find(
      (entry) => entry.definitionId === 'card_solar_lance' && cardAbilityIsFiring(entry),
    );
    if (card === undefined || ship.healthMilli <= 0n) {
      return;
    }
    const balance = cardBalance('card_solar_lance');
    if (balance === null) {
      return;
    }
    card.attackAccumulatorMs += PVP_TICK_MS;
    if (card.attackAccumulatorMs < balance.hitIntervalMs) {
      return;
    }
    card.attackAccumulatorMs -= balance.hitIntervalMs;
    const aim = normalise(ship.aimX, ship.aimY);
    if (aim === null) {
      return;
    }
    const hullRadius = pvpHullRadiusMu(ship.profile);
    const length = Math.trunc((hullRadius * PVP_SOLAR_LANCE_REACH_BPS) / 10_000);
    /*
     * **A espessura e o dano saem da CARGA** (gate V28H.N).
     *
     * Contrato aprovado: "espessura, intensidade e dano aumentam
     * progressivamente". As mesmas duas funções que a cena usa para desenhar
     * decidem aqui quem é apanhado - duas expressões seriam um feixe pintado
     * mais gordo do que aquilo por que ele cobra.
     */
    const halfWidth = Math.trunc(
      cardBeamWidthFor((hullRadius * PVP_SOLAR_LANCE_WIDTH_BPS) / 10_000, card.chargeStrengthBps),
    );
    const damageMilli =
      (pvpShotDamageMilli(
        ship.profile,
        ship.effects.doubleDamageTicks > 0 ? PVP_RUNE_DAMAGE_BPS : 10_000,
      ) *
        BigInt(cardScaleByCharge(balance.damageBps, card.chargeStrengthBps))) /
      10_000n;
    if (damageMilli <= 0n) {
      return;
    }
    /*
     * **A BOCA, e não o centro do casco** (gate V28H.N).
     *
     * Contrato aprovado: "o feixe nasce na frente da nave, nunca atrás ou
     * atravessando o casco". Medido do centro, o segmento começava DENTRO da
     * nave e apanhava quem estivesse encostado atrás dela - com `along` preso
     * a zero, um adversário às costas caía no princípio do feixe.
     */
    const muzzleX = ship.x + divTrunc(aim.x * hullRadius, VECTOR_SCALE);
    const muzzleY = ship.y + divTrunc(aim.y * hullRadius, VECTOR_SCALE);
    for (const enemy of this.roster.enemiesOf(ship.team)) {
      if (enemy.healthMilli <= 0n || !cardIsHittable(enemy.cardEffects)) {
        continue;
      }
      /*
       * A projecção do casco sobre o feixe, presa ao segmento: um inimigo atrás
       * de quem dispara está a zero, e um para lá da ponta está no fim.
       */
      const dx = enemy.x - muzzleX;
      const dy = enemy.y - muzzleY;
      const alongScaled = (dx * aim.x + dy * aim.y) / VECTOR_SCALE;
      const along = Math.max(0, Math.min(length, Math.trunc(alongScaled)));
      const nearestX = muzzleX + divTrunc(aim.x * along, VECTOR_SCALE);
      const nearestY = muzzleY + divTrunc(aim.y * along, VECTOR_SCALE);
      const reach = halfWidth + pvpHullRadiusMu(enemy.profile);
      if (distanceSquared(nearestX, nearestY, enemy.x, enemy.y) > reach * reach) {
        continue;
      }
      this.applyDamage(enemy, damageMilli, 'card', enemy.x, enemy.y, ship, null);
    }
  }

  private fireSuperCannons(ship: PvpParticipantShipState): void {
    if (!shipHasSuperCannons(ship)) {
      return;
    }
    const damageMilli = pvpShotDamageMilli(
      ship.profile,
      ship.effects.doubleDamageTicks > 0 ? PVP_RUNE_DAMAGE_BPS : 10_000,
    );
    const velocity = scaleDirection(
      { x: ship.aimX, y: ship.aimY },
      PVP_PROJECTILE_SPEED_MU_PER_TICK,
    );
    for (const muzzle of pvpSuperCannonMuzzles(ship)) {
      if (this.shots.length >= PVP_MAX_PROJECTILES) {
        return;
      }
      this.shots.push({
        id: this.nextProjectileId,
        ownerParticipantId: ship.participantId,
        ownerTeam: ship.team,
        damageMilli,
        /* Uma rolagem por PROJÉTIL, com a origem que a tabela fechada conhece. */
        jewelProc: this.rollOutgoingProc(ship, 'temporary_cannon'),
        atomicBomb: null,
        x: muzzle.x,
        y: muzzle.y,
        vx: velocity.x,
        vy: velocity.y,
        life: PVP_PROJECTILE_LIFETIME_TICKS,
        /* Original, como o do casco: um canhão não reflete nada. */
        reflected: false,
        /* Sem explosão de carta e sem perseguição: um tiro comum (gate V28H.L). */
        cardBlast: null,
        homingTargetId: null,
      });
      this.nextProjectileId += 1;
    }
  }

  private fire(ship: PvpParticipantShipState): void {
    /*
     * Decremented only while positive, exactly as the integer countdown was.
     *
     * Not clamped at zero, and that is the accumulator: a cadence that is not a
     * whole number of ticks lands in `(-1000, 0]`, and the volley below ADDS
     * the interval to that residue instead of assigning over it. With the
     * default 4 000 it lands on zero every time, so a shipped duel fires on the
     * identical ticks - proved in `duel-invariance.test.ts`.
     */
    if (ship.cooldownMilliTicks > 0) {
      ship.cooldownMilliTicks -= PVP_MILLI_TICKS_PER_TICK;
    }
    if (!ship.firing || !ship.connected || ship.healthMilli <= 0n || ship.cooldownMilliTicks > 0) {
      return;
    }
    /*
     * **Congelado não dispara** (gate V28H.L).
     *
     * Depois do decremento e não antes: o Congelamento impede a SALVA, e não o
     * relógio. Um casco que descongelasse com a recarga por andar teria a carta
     * a devolver-lhe tempo, e o Congelamento não é uma carta de recarga.
     */
    if (cardIsFrozen(ship.cardEffects)) {
      return;
    }
    if (this.shots.length >= PVP_MAX_PROJECTILES) {
      // A ceiling reached is a defect, not a rule. Drop the shot rather than
      // grow without bound; the cadence test proves it is never reached.
      return;
    }
    /*
     * **A rolagem da SALVA, e ela acontece exatamente aqui.**
     *
     * Antes da velocidade porque é ela que decide qual é a velocidade: uma bomba
     * viaja a 80 % do que este projétil viajaria, e "o que ele viajaria" é o
     * número que a linha seguinte calcularia de qualquer maneira.
     *
     * Consumida com joia ou sem ela. Um duelo sem Núcleo Atômico consome os
     * mesmos dois valores do mesmo fluxo, e é isso que faz a sequência de um
     * casco não depender do que ele traz encaixado.
     */
    const { bomb, missile } = this.rollVolleyProcs(ship);
    /*
     * **A bomba OCUPA a oportunidade ofensiva deste projétil.**
     *
     * A loteria por projétil corre na mesma - tem de correr, ou a sequência de
     * procs do casco mudava por causa de uma joia que ele nem tem - mas o que
     * ela escolheu é DEITADO FORA quando este projétil se torna a bomba. É o §2
     * do gate: "a bomba ocupa apenas a oportunidade ofensiva daquele projétil".
     *
     * Um projétil que fosse bomba E trouxesse um corte seria uma oportunidade
     * gasta duas vezes, e a magnitude de cada uma foi calculada contra a chance
     * de ela ser escolhida sozinha.
     */
    const rolled = this.rollOutgoingProc(ship);
    const proc = bomb === null ? rolled : null;
    const velocity = scaleDirection(
      { x: ship.aimX, y: ship.aimY },
      /*
       * 80 % quando é bomba, e a redução aplica-se à VELOCIDADE e não ao vetor
       * já escalado: `divTrunc` uma vez, sobre o número inteiro que a arena usa,
       * em vez de duas truncaturas encadeadas que arredondariam a direção.
       */
      bomb === null
        ? PVP_PROJECTILE_SPEED_MU_PER_TICK
        : divTrunc(PVP_PROJECTILE_SPEED_MU_PER_TICK * PVP_BOMB_SPEED_BPS, 10_000),
    );
    // Born at the hull's edge so it never starts inside its own ship.
    const muzzle = scaleDirection(
      { x: ship.aimX, y: ship.aimY },
      /*
       * A boca é a mesma. O §3 exige que a bomba nasça no MESMO ponto do
       * projétil que substituiu, portanto o corpo maior não a afasta do casco -
       * ela sai de onde o tiro teria saído, e é isso que faz a substituição
       * parecer uma substituição.
       */
      pvpHullRadiusMu(ship.profile) + PVP_PROJECTILE_RADIUS_MU,
    );
    const projectile: PvpParticipantProjectileState = {
      id: this.nextProjectileId,
      ownerParticipantId: ship.participantId,
      ownerTeam: ship.team,
      /*
       * Decidido AQUI, e nunca relido no impacto.
       *
       * Toda a fronteira temporal do gate V28F.B é esta linha: o tiro nasce com
       * o dano que o perfil tinha neste instante - corações incluídos - e
       * carrega-o até bater. Um projétil que perguntasse ao perfil no impacto
       * mudaria de ideias em voo quando uma runa expirasse ou um coração fosse
       * incorporado a meio da partida.
       */
      damageMilli: pvpShotDamageMilli(
        ship.profile,
        ship.effects.doubleDamageTicks > 0 ? PVP_RUNE_DAMAGE_BPS : 10_000,
      ),
      /*
       * A LOTERIA, e ela acontece exatamente aqui.
       *
       * Uma rolagem por disparo, do fluxo autoritativo DESTE participante, no
       * instante em que o projétil nasce. Nunca no impacto: um sorteio no
       * impacto teria de reler o perfil, e o perfil pode ter mudado entre o
       * disparo e a batida.
       *
       * Dois valores consumidos por disparo, sempre - a rolagem e a entropia -
       * mesmo quando não há joia nenhuma. Consumir condicionalmente faria a
       * sequência de um casco depender do seu equipamento, e dois duelos com a
       * mesma semente deixariam de ser o mesmo duelo.
       */
      jewelProc: proc,
      /**
       * A bomba, quando esta salva a sorteou.
       *
       * Uma bomba e nunca duas: a rolagem é uma só, e num duelo a salva é um
       * projétil. Não nasce um projétil ADICIONAL - este é o projétil que a
       * salva já ia disparar, transformado.
       */
      atomicBomb: bomb,
      x: ship.x + muzzle.x,
      y: ship.y + muzzle.y,
      vx: velocity.x,
      vy: velocity.y,
      life: PVP_PROJECTILE_LIFETIME_TICKS,
      /* Um tiro de canhão nasce sempre original: uma salva não reflete nada. */
      reflected: false,
      /* Sem explosão de carta e sem perseguição: um tiro comum (gate V28H.L). */
      cardBlast: null,
      homingTargetId: null,
    };
    this.nextProjectileId += 1;
    this.shots.push(projectile);
    /*
     * **Os dois canhões temporários disparam na MESMA salva** (gate V28H.J).
     *
     * Armamento adicional, e é literalmente isso: eles não têm cadência própria
     * no duelo - acompanham a do casco, que é a mesma decisão da caça com outro
     * nome (lá o acumulador deles corre à velocidade efectiva da nave; aqui a
     * salva é uma só e eles entram nela).
     *
     * Cada um leva o dano do DONO, já com a runa de dano dobrado se ela estiver
     * a correr, e cada um sorteia a SUA oportunidade de joia - uma rolagem por
     * projétil, que é a unidade que o contrato das joias fixou. A origem é
     * `temporary_cannon`, e é ela que abre a porta: o que estes tiros gerarem
     * nasce com origem `jewel`, que não origina nada.
     */
    this.fireSuperCannons(ship);
    /*
     * **E a rolagem do Coração Vital, do SEU fluxo, uma vez por salva.**
     *
     * Depois de o projétil existir, porque é ele que dá ao anúncio a identidade
     * autoritativa que o deduplica - e antes de qualquer recarga, porque a
     * unidade é a salva que acabou de sair.
     *
     * A cura não depende de o tiro acertar, nem sequer de ele chegar a lado
     * nenhum: o §"o proc não depende de acertar um inimigo" é cumprido por a
     * chamada estar aqui, no disparo, e não numa fronteira de impacto.
     */
    this.procVitalHeal(ship, projectile.id);
    /*
     * ADDED, not assigned: the residue below zero is the fraction of a tick
     * this volley was late, and adding carries it into the next one. Assigning
     * would throw it away and round every non-whole cadence down to the tick.
     *
     * O intervalo é resolvido AQUI, na recarga, e não guardado no perfil: o
     * perfil continua a dizer qual é a cadência do casco, e as joias compõem-se
     * por cima dele - a mesma forma que a runa de movimento já usa. Sem joia de
     * cadência a função devolve o próprio campo do perfil, portanto uma partida
     * de hoje recarrega exatamente os 4 000 de sempre (V28G.D).
     */
    ship.cooldownMilliTicks += this.fireIntervalOf(ship);
    /*
     * **E atacar encerra o Manto Espectral** (gate V28H.L), que é a decisão
     * APROVADA sobre essa carta e a única maneira de a perder. Aqui e não no
     * impacto: o que a descrição diz é "atacar", e um tiro que erra foi um
     * ataque na mesma.
     */
    /* E atirar encerra o Manto, pela mesma porta que uma carta ofensiva usa. */
    this.breakCloak(ship);
    this.events.push({
      kind: 'fired',
      participantId: ship.participantId,
      team: ship.team,
      x: projectile.x,
      y: projectile.y,
    });
    /*
     * **O MÍSSIL, e ele nasce ADICIONALMENTE** (gate V28G.L).
     *
     * Depois do projétil e depois do evento, porque é isso que ele é: a salva
     * saiu inteira - com o seu dano, o seu proc e a sua arte intactos - e o
     * míssil aparece ao lado dela. Nenhum tiro comum é substituído, nenhum muda
     * de velocidade, e o `fired` que a cena desenha é o mesmo `fired` de sempre.
     *
     * A recarga já foi somada acima, e de propósito: o míssil não custa cadência
     * e não a devolve. Ele é um extra, e um extra não mexe no relógio da arma.
     */
    if (missile !== null) {
      this.launchMissile(ship, missile);
    }
  }

  /* ------------------------------------------------------------------------ */
  /*  O Arsenal Ígneo (gate V28G.L)                                           */
  /* ------------------------------------------------------------------------ */

  /**
   * **Põe um míssil no ar.** Escolhe o alvo, aponta-o e regista-o.
   *
   * ## O alvo é escolhido AQUI, e uniformemente
   *
   * Ao nascer, e não no primeiro tique: um míssil sem alvo durante um tique
   * voaria um passo numa direção que ninguém decidiu. A escolha é uniforme entre
   * os inimigos VIVOS - o único adversário num 1x1, qualquer um dos três num
   * 3x3 - e sai do fluxo de alvos desta cadeira, portanto é determinística e
   * reproduzível a partir da instantânea.
   *
   * Um casco sem nenhum inimigo vivo não lança míssil nenhum. A alternativa
   * seria criá-lo para o explodir no mesmo tique, o que só produziria uma
   * explosão que ninguém pediu numa partida que já acabou.
   *
   * ## A direção inicial é a MIRA
   *
   * Não a direção do alvo. O míssil sai da boca da arma como um tiro sai, e é a
   * perseguição que o curva a partir daí - até 12 graus por tique. Nascer já
   * apontado ao alvo seria uma viragem instantânea, que é a primeira coisa que o
   * contrato proíbe.
   */
  private launchMissile(ship: PvpParticipantShipState, selection: MissileSelection): void {
    const target = this.chooseMissileTarget(ship);
    if (target === null) {
      return;
    }
    const muzzle = scaleDirection(
      { x: ship.aimX, y: ship.aimY },
      /* A mesma boca do tiro: o míssil sai de onde a salva saiu. */
      pvpHullRadiusMu(ship.profile) + PVP_PROJECTILE_RADIUS_MU,
    );
    this.missiles.push({
      id: this.nextProjectileId,
      ownerParticipantId: ship.participantId,
      ownerTeam: ship.team,
      damageMilli: selection.damageMilli,
      entropy: selection.entropy,
      targetParticipantId: target.participantId,
      x: ship.x + muzzle.x,
      y: ship.y + muzzle.y,
      dirX: ship.aimX,
      dirY: ship.aimY,
      life: PVP_MISSILE_LIFETIME_TICKS,
      /* Um míssil de salva nasce sempre original: uma salva não reflete nada. */
      reflected: false,
    });
    this.nextProjectileId += 1;
    /*
     * **O teto, e a política que o contrato manda: os NOVOS sobrevivem.**
     *
     * Alcançá-lo é um defeito e não uma regra - `PVP_MAX_MISSILES` é o pior caso
     * legítimo derivado da cadência legal mais rápida - por isso a defesa larga
     * o mais ANTIGO, que é o que está mais perto de expirar sozinho. Recusar o
     * novo, como um projétil é recusado, prenderia a sala precisamente aos
     * mísseis que estavam de saída.
     *
     * O mais antigo sai em SILÊNCIO, sem explosão e sem dano: não aconteceu nada
     * no mundo do jogo, e desenhar fogo por causa de um limite interno seria
     * mostrar ao jogador um facto que não existe.
     */
    while (this.missiles.length > PVP_MAX_MISSILES) {
      this.missiles.shift();
    }
  }

  /**
   * **O MÍSSIL intercetado: o original some e um equivalente nasce no refletor**
   * (gate V28G.O).
   *
   * ## O que desaparece
   *
   * Tudo. `explodeMissile` **não é chamada**, portanto não há explosão, não há
   * dano e não há evento `blast` sobre o refletor. O §5 pede as três ausências,
   * e a forma de as garantir é não chamar a função que as produz.
   *
   * ## O que nasce, e porque é o MESMO míssil
   *
   * O mesmo `damageMilli` congelado e a mesma `entropy` do original: o §2 é
   * explícito em que magnitude e dano congelado permanecem os originais.
   * Recalcular contra o perfil do refletor responderia "quanto vale o míssil
   * dele" em vez de "quanto valia o míssil que ele mandou".
   *
   * Arte, chama, tamanho, velocidade, giro e os nove segundos são os mesmos
   * porque saem das mesmas constantes - `PVP_MISSILE_LIFETIME_TICKS` inteiro, e
   * não o que restava ao original: o §5 diz "os nove segundos aprovados", que é
   * uma vida nova e não um resto herdado. Este método não escreve um único
   * número aprovado.
   *
   * ## O alvo inicial é o atacante original, e depois a mecânica normal
   *
   * `targetParticipantId` nasce apontado a quem o mandou, como o §5 exige. A
   * partir daí ele é um míssil do refletor como qualquer outro: persegue com o
   * mesmo limite de viragem, retargeta pela mesma regra entre os inimigos do
   * NOVO proprietário, e as paredes continuam a bloqueá-lo.
   *
   * ## A direção inicial é a MIRA do refletor, e não o alvo
   *
   * A mesma regra de {@link PvpSimulation.launchMissile}, e pela mesma razão:
   * nascer já apontado ao alvo seria uma viragem instantânea, que é a primeira
   * coisa que o contrato do míssil proíbe. A perseguição curva-o a partir daí.
   *
   * @returns se a interceção aconteceu. `false` deixa o míssil explodir como
   *   sempre explodiu.
   */
  private reflectMissile(missile: PvpMissileState, defender: PvpParticipantShipState): boolean {
    const owner = this.roster.require(missile.ownerParticipantId);
    if (
      !this.reflects(
        defender,
        owner,
        'missile_impact',
        missile.reflected ? 'reflected' : 'original',
      )
    ) {
      return false;
    }
    this.announceReflection(defender, owner, 'missile_impact', 'intercepted');
    const muzzle = scaleDirection(
      { x: defender.aimX, y: defender.aimY },
      /* A mesma boca de sempre: ele parte de onde um tiro do refletor partiria. */
      pvpHullRadiusMu(defender.profile) + PVP_PROJECTILE_RADIUS_MU,
    );
    this.pendingMissiles.push({
      id: this.nextProjectileId,
      /* O REFLETOR é o novo proprietário: a eliminação e a estatística são dele. */
      ownerParticipantId: defender.participantId,
      ownerTeam: defender.team,
      /* Os dois números congelados do payload original, intactos. */
      damageMilli: missile.damageMilli,
      entropy: missile.entropy,
      /* O atacante original é o alvo inicial, como o §5 manda. */
      targetParticipantId: owner.participantId,
      x: defender.x + muzzle.x,
      y: defender.y + muzzle.y,
      dirX: defender.aimX,
      dirY: defender.aimY,
      life: PVP_MISSILE_LIFETIME_TICKS,
      /* A linhagem que fecha o grafo: este míssil não pode ser refletido outra vez. */
      reflected: true,
    });
    this.nextProjectileId += 1;
    return true;
  }

  /**
   * Um inimigo vivo, escolhido uniformemente do fluxo desta cadeira.
   *
   * `enemiesOf` responde à pergunta "quem é hostil" uma vez, e é a mesma função
   * que os projéteis usam - portanto nunca um aliado, nunca o dono, e nunca uma
   * entidade que não é um casco desta sala. A lista chega em ordem CANÓNICA, que
   * é o que faz o índice sorteado significar sempre a mesma cadeira.
   */
  private chooseMissileTarget(ship: PvpParticipantShipState): PvpParticipantShipState | null {
    /*
     * **E ninguém que não se possa ESCOLHER** (gate V28H.L). Um míssil escolhe
     * um alvo, portanto o Bastião e o Manto tiram-no da lista: o primeiro por
     * ser inalvejável, o segundo porque não se escolhe o que não se vê.
     */
    const candidates = this.roster
      .enemiesOf(ship.team)
      .filter((enemy) => enemy.healthMilli > 0n && cardIsSelectable(enemy.cardEffects));
    if (candidates.length === 0) {
      return null;
    }
    const stream = this.missileRngFor(ship.participantId);
    const index = chooseMissileTargetIndex(
      candidates.length,
      stream.nextIntInclusive(0, candidates.length - 1),
    );
    return index === null ? null : (candidates[index] ?? null);
  }

  /**
   * **The only place a ship's health goes down.**
   *
   * ## Why it had to be extracted before anything else
   *
   * It used to live inline, in the middle of `stepProjectile`'s substep loop -
   * clamp, subtract, push a `hit`, check for zero, clear the effects, push a
   * `death`. That is fine while a projectile is the only thing that can hurt
   * anybody. The hunt learned the other lesson years ago and wrote it down in
   * `applyHit`: *"ONE path for the ship and the pet, on purpose. A second path
   * for the pet is exactly how a mob would end up paying twice."*
   *
   * The PvP is about to grow saws, missiles, bombs, bleeding, poison and
   * reflection. Six more places that subtract from `health` would be six
   * places that could forget the clamp, forget the death event, or forget to
   * clear a dead hull's teleport charge - and the one that forgets is the one
   * nobody is looking at.
   *
   * ## What it guarantees, once
   *
   * - **the clamp**: health never goes below zero, and the event reports what
   *   was ACTUALLY applied - a shot that finishes a ship on 10 health reports
   *   10, not 40;
   * - **no resurrection**: a hull already at zero absorbs nothing and emits
   *   nothing, so two shots landing on the same substep cannot announce two
   *   deaths;
   * - **death is announced once**, by the blow that caused it;
   * - **a wreck keeps no timer and no charge** - coming back with a banked
   *   teleport would make dying a way to store one.
   *
   * ## The origin
   *
   * Typed, and named by every call site - there is no default, because a
   * default is exactly how a new call site inherits a classification nobody
   * chose for it. `PvpDamageOrigin` excludes `pet` at the TYPE: a duel has no
   * pets, so the value cannot be passed rather than being passed and refused.
   *
   * It decides one thing today - whether the blow may set off a jewel - and the
   * answer comes from the closed table in `damage/origin.ts`. The list is empty
   * because no jewel is registered, but the gate is real and runs on every
   * blow, so the first jewel is a REGISTRATION rather than a new branch here.
   * That is what makes "a jewel never sets off another jewel" a missing edge in
   * the graph instead of a depth counter somebody has to maintain.
   *
   * @returns the damage actually applied, which is zero for a blow that landed
   *   on a hull that was already down.
   */
  /**
   * O efeito do proc que este projétil trazia, se trazia algum.
   *
   * ## O que é lido, e o que deliberadamente não é
   *
   * ## Nada é decidido aqui
   *
   * A magnitude foi congelada no nascimento, dentro da seleção. Este método
   * aplica-a e não reavalia coisa nenhuma: não lê o perfil, não conhece uma
   * joia pelo nome e não sabe o que é um `levelSum`. Num duelo isso é hoje
   * indistinguível de a ler no impacto - `freezeParticipantJewels` recusa
   * depois do tique zero, portanto o perfil não pode mudar a meio - e é escrito
   * assim na mesma, porque a regra temporal tem de ser a MESMA nos dois modos,
   * e a caça é onde ela se nota.
   *
   * ## Porque a origem é `jewel`
   *
   * Para que o corte não possa originar nada. `damageOriginMayTriggerJewel`
   * devolve falso para `jewel`, e é a tabela fechada que garante que a
   * profundidade de recursão é exatamente um - não um contador que alguém teria
   * de decrementar.
   *
   * ## O evento sai do DANO APLICADO, e não da intenção
   *
   * `applyDamage` devolve o que realmente tirou, e é zero para um casco que já
   * estava em baixo. Anunciar antes de perguntar - que é o que este método
   * fazia - publicava um corte em cima de um alvo que o tiro comum já tinha
   * matado, e uma apresentação desenharia um facto que não aconteceu.
   */
  /**
   * A cura de um golpe confirmado, e a única coisa que sobe a vida de um casco.
   *
   * ## A ordem, e porque é a única possível
   *
   * dano → cura → (morte do alvo). A base é o que o golpe REMOVEU, portanto ela
   * não pode ser calculada antes de ele acontecer. Um alvo que morre do golpe
   * não anula a cura: o dano comum aconteceu mesmo, e é dele que ela sai.
   *
   * ## Um atacante morto não é curado, e nunca ressuscita
   *
   * Alcançável a sério neste modo, ao contrário da caça: um casco cai e os seus
   * tiros continuam no ar, porque nada os remove. A recusa é a guarda de vida a
   * zero abaixo, e ela é estrutural: sem ela, um destroço curar-se-ia.
   *
   * ## Nada aqui é reavaliado
   *
   * O coeficiente vem do perfil CONGELADO da cadeira, que `freezeParticipantJewels`
   * recusa mudar depois do tique zero. Sem consulta, sem I/O, e sem releitura de
   * catálogo: `resolveJewelEffects` lê um `Map` construído ao carregar o módulo.
   */
  private stealLife(
    attacker: PvpParticipantShipState,
    target: PvpParticipantShipState,
    appliedDamageMilli: bigint,
    projectileId: number | null,
  ): void {
    if (projectileId === null) {
      /*
       * Sem chave de deduplicação não há sifão, e a MESMA regra da caça.
       *
       * Inalcançável hoje: o único golpe sem projétil é uma parcela de veneno, e
       * a origem `jewel` nunca chega a esta função - a tabela fechada recusa-a
       * uma linha antes. Escrito assim porque a alternativa seria inventar um
       * id, que é a maneira de um cliente desenhar o mesmo sifão duas vezes.
       */
      return;
    }
    const { lifestealBps } = resolveJewelEffects(attacker.profile.jewels);
    if (lifestealBps <= 0n) {
      /*
       * Sem a joia, nem uma multiplicação. A saída antecipada não é uma
       * otimização: é o que faz uma sala sem joias produzir exatamente o estado
       * que produzia antes deste gate, restos incluídos.
       */
      return;
    }
    /*
     * **Um casco em baixo não é curado, e nunca ressuscita** (gate V28G.I).
     *
     * A guarda vem antes da aritmética e não depois: uma cura calculada sobre um
     * destroço seria uma cura que o resto do atacante guardaria para a ronda
     * seguinte, e um destroço não guarda nada.
     */
    if (attacker.healthMilli <= 0n) {
      return;
    }
    /*
     * A fração SUB-MILLI continua a viver num resto (gate V28G.S.1).
     *
     * `lifestealEarnedMilli` é a mesma aritmética exata de sempre: ela multiplica
     * o dano aplicado pela percentagem e guarda em `carryBps` o que sobra de um
     * MILÉSIMO. Esse resto não desapareceu com a vida em milli - ele é menor do
     * que a unidade da vida, e sem ele mil golpes de um milli a 9 % curariam
     * zero.
     *
     * O que desapareceu foi o SEGUNDO resto, `healCarryMilli`, que só existia
     * para converter milli-cura numa barra inteira.
     *
     * `appliedDamageMilli` já é milli - é o que `applyDamage` removeu, depois do
     * clamp do overkill - portanto não há conversão nenhuma neste ponto.
     */
    const earned = lifestealEarnedMilli(
      appliedDamageMilli,
      lifestealBps,
      attacker.lifestealCarryBps,
    );
    attacker.lifestealCarryBps = earned.nextCarryBps;
    /*
     * E o teto limita, sem overheal e sem escudo. O que sobra do que foi ganho
     * é DESCARTADO e não guardado: guardá-lo faria uma estadia com a vida cheia
     * virar um salto no primeiro instante depois de levar dano.
     */
    const missing = attacker.profile.maxHealthMilli - attacker.healthMilli;
    const healed = earned.healMilli > missing ? missing : earned.healMilli;
    if (healed <= 0n) {
      // Vida cheia, ou nada ganho ainda. A passiva continua ativa e o benefício
      // deste instante é zero: sem overheal, sem escudo, sem ressurreição. E sem
      // evento: não houve facto nenhum a anunciar.
      return;
    }
    this.grantHealth(attacker, healed);
    /*
     * O anúncio, e só depois de a cura ter mesmo acontecido.
     *
     * Diz o efeito, quem foi curado, quem foi drenado, que tiro o causou e onde.
     * Não diz quanto - a vida já viaja a subir nos frames de estado - nem nível,
     * cópias, joia ou qualquer parâmetro de arte.
     *
     * A posição é a do ALVO, que é de onde o fluxo parte: a mesma regra que o
     * corte segue, para uma apresentação partilhada não ter de saber em que modo
     * está. O destino é o atacante, e a cena sabe onde ele está porque o desenha.
     */
    this.events.push({
      kind: 'proc',
      procId: LIFESTEAL_HEAL_EFFECT,
      participantId: attacker.participantId,
      team: attacker.team,
      targetId: target.participantId,
      projectileId,
      tick: this.currentTick,
      /*
       * A vida que o casco REALMENTE ganhou, em milli (gate V28G.S.1).
       *
       * Já é milli: a vida de um casco é milli, portanto o que foi somado a ela
       * é o número que sai daqui, sem multiplicação nenhuma pelo caminho.
       */
      healedMilli: healed,
      x: target.x,
      y: target.y,
    });
  }

  private applyJewelProc(
    attacker: PvpParticipantShipState,
    target: PvpParticipantShipState,
    projectile: PvpParticipantProjectileState,
  ): void {
    const selection = projectile.jewelProc;
    if (selection === null || selection.cutMilli <= 0n) {
      return;
    }
    /*
     * **A REFLEXÃO DO CORTE, na sua PRÓPRIA fronteira** (gate V28G.O).
     *
     * O §4 do gate diz que "procs que já existem como payloads ou entidades
     * próprias são refletidos na sua própria fronteira de resolução", e o corte
     * é exatamente isso: ele tem uma magnitude congelada, um alvo e um instante
     * de resolução seus, e por isso ganha a SUA rolagem em vez de herdar a do
     * tiro que o trouxe.
     *
     * Um tiro intercetado nunca chega aqui - `stepProjectile` devolve antes - e
     * é assim que o §"refletir o dano-base impede que esse proc dependente
     * nasça" fica cumprido pela estrutura.
     *
     * Refletido, o corte troca de dono: o dano e a animação passam a acontecer
     * no atacante original, e o REFLETOR é o autor. Não há proc novo sorteado -
     * a seleção congelada é a mesma, virada.
     */
    if (this.reflects(target, attacker, 'cyclone_cut', 'original')) {
      const reflected = this.resolveReflected(
        target,
        attacker,
        selection.cutMilli,
        'cyclone_cut',
        projectile.id,
      );
      if (reflected <= 0) {
        return;
      }
      /*
       * O evento do corte, virado: quem corta é o refletor e quem é cortado é o
       * atacante. A cena desenha a mesma meia-lua aprovada, no casco certo - o
       * §7 manda reutilizar a apresentação do efeito no novo destinatário, e é
       * isso que trocar os dois ids faz, sem uma linha de arte nova.
       */
      this.events.push({
        kind: 'proc',
        procId: selection.id,
        participantId: target.participantId,
        team: target.team,
        targetId: attacker.participantId,
        projectileId: projectile.id,
        tick: this.currentTick,
        x: attacker.x,
        y: attacker.y,
      });
      return;
    }
    const applied = this.applyDamage(
      target,
      selection.cutMilli,
      'jewel',
      projectile.x,
      projectile.y,
      /*
       * O atacante é passado e a origem `jewel` é que decide: o corte NÃO cura.
       * Passá-lo na mesma é o que faz a assinatura ser uniforme, e é o que
       * deixa a decisão na tabela fechada em vez de num parâmetro em falta.
       */
      attacker,
      projectile.id,
    );
    if (applied <= 0) {
      return;
    }
    /*
     * O evento, e só depois de o golpe ter mesmo acontecido.
     *
     * Diz o quê, de quem, em quem e onde. Não carrega magnitude, nível, cópias
     * nem nada visual: o dano viaja no evento de dano que já existe.
     */
    this.events.push({
      kind: 'proc',
      procId: selection.id,
      participantId: attacker.participantId,
      team: attacker.team,
      targetId: target.participantId,
      projectileId: projectile.id,
      tick: this.currentTick,
      /*
       * A posição do ALVO, e não a do projétil (correção do gate V28G.H).
       *
       * O corte nasce sobre o casco atingido. O projétil está na borda do casco
       * no instante da colisão, portanto um efeito centrado nele fica meio corpo
       * ao lado - e, mais importante, a caça já anunciava o alvo. Duas regras
       * diferentes obrigariam uma apresentação partilhada a saber em que modo
       * está, que é exatamente o que ela não pode saber.
       */
      x: target.x,
      y: target.y,
    });
  }

  /**
   * **A explosão do Núcleo Atômico.** Dano em área, empurrão, e o anúncio.
   *
   * ## As quatro regras do §4, e porque nenhuma delas é um caso especial
   *
   * - **o alvo direto leva os dois**: o dano normal do projétil que a bomba
   *   substituiu, com origem `cannon`, e a seguir o dano adicional da explosão,
   *   com origem `jewel`. Duas chamadas, porque são duas origens - e são duas
   *   origens porque só a primeira é um tiro;
   * - **os vizinhos levam só a explosão**, e não há teto de quantos. Um limite
   *   seria uma decisão de equilíbrio que ninguém tomou;
   * - **contra parede, limite ou fim de vida não há alvo direto**, e a ausência
   *   escreve-se `direct === null` em vez de um alvo inventado;
   * - **aliados nunca**. `enemiesOf` responde à pergunta uma vez, e não há
   *   segunda lista por onde um aliado entre.
   *
   * ## Porque a origem é `jewel`, e o que isso desliga sozinho
   *
   * Tudo. `damageOriginMayTriggerJewel('jewel')` é falso, e é essa tabela que
   * faz a explosão não acionar outro proc, não acionar o Roubo de Vida, não
   * criar Ciclone e não voltar a aplicar-se a si mesma. Nada disto é uma
   * verificação escrita aqui: a profundidade de recursão é um porque não existe
   * aresta no grafo que lhe dê duas.
   *
   * ## A ordem, e porque a lista de vítimas é fechada primeiro
   *
   * Quem está no raio é decidido ANTES de qualquer dano. Um casco não sai do
   * roster ao morrer - ao contrário de um mob - portanto a lista não mudava de
   * qualquer maneira; fechá-la na mesma é o que impede que um empurrão aplicado
   * a meio mova um casco para dentro ou para fora do raio de um vizinho ainda
   * por resolver.
   */
  private detonateBomb(
    projectile: PvpParticipantProjectileState,
    direct: PvpParticipantShipState | null,
  ): void {
    const selection = projectile.atomicBomb;
    if (selection === null) {
      // Inalcançável: só um projétil com bomba chega aqui. Afirmado para que um
      // futuro call site que se engane falhe em vez de detonar um tiro comum.
      throw new RangeError(`projectile ${projectile.id} detonated without a bomb`);
    }
    const owner = this.roster.require(projectile.ownerParticipantId);
    /*
     * O CENTRO: o casco atingido quando existe, senão o ponto onde a bomba está.
     *
     * A mesma regra que o Ciclone segue e pela mesma razão - um efeito centrado
     * na bala fica meio corpo ao lado num alvo grande - e é o mesmo centro que o
     * evento leva, portanto o círculo que o jogador vê é o círculo que decidiu
     * quem levou.
     */
    const x = direct === null ? projectile.x : direct.x;
    const y = direct === null ? projectile.y : direct.y;
    const radiusSquared = PVP_BOMB_BLAST_RADIUS_MU * PVP_BOMB_BLAST_RADIUS_MU;
    const victims = this.roster
      .enemiesOf(projectile.ownerTeam)
      .filter(
        (ship) => ship.healthMilli > 0n && distanceSquared(x, y, ship.x, ship.y) <= radiusSquared,
      );

    if (direct !== null) {
      /*
       * O dano NORMAL, e primeiro. O inimigo que acionou a proximidade recebe o
       * que o projétil substituído teria causado - é dano de canhão, e continua
       * a curar quem o deu se ele trouxer Roubo de Vida.
       *
       * **Exceto numa bomba REFLETIDA** (gate V28G.O). Ela não é o tiro de
       * ninguém: o projétil que ela substituiu era do atacante original, e o
       * refletor não disparou coisa nenhuma. Origem `jewel` é o que lhe nega
       * proc, Roubo de Vida e estado - o §3 do gate inteiro, obtido pela tabela
       * fechada de origens em vez de por três condições escritas aqui.
       */
      const appliedDirect = this.applyDamage(
        direct,
        projectile.damageMilli,
        projectile.reflected ? 'jewel' : 'cannon',
        x,
        y,
        owner,
        projectile.id,
      );
      if (projectile.reflected && appliedDirect > 0n) {
        this.announceReflection(owner, direct, 'atomic_bomb_proximity', 'landed', appliedDirect);
      }
    }

    const hit: PvpParticipantId[] = [];
    for (const victim of victims) {
      const applied = this.applyDamage(
        victim,
        selection.blastMilli,
        /* A origem que fecha o grafo. Nunca `cannon`. */
        'jewel',
        x,
        y,
        owner,
        projectile.id,
      );
      if (applied > 0n) {
        hit.push(victim.participantId);
        /*
         * **`REFLECT` sobre a área de uma bomba refletida** (gate V28G.O).
         *
         * O §7 do gate manda escrever `REFLECT` sobre TODO o dano cuja origem
         * seja uma reflexão, e a área de uma bomba refletida é isso: ela existe
         * porque alguém a intercetou. O splash não é INTERCETÁVEL - a tabela
         * fechada recusa-o, e diz porquê - mas ser refletido e poder refletir
         * são perguntas diferentes.
         */
        if (projectile.reflected) {
          this.announceReflection(owner, victim, 'atomic_blast_splash', 'landed', applied);
        }
      }
      /*
       * O EMPURRÃO, e só sobre quem sobreviveu.
       *
       * A morte encerra o estado (§6), portanto um casco que a explosão acabou
       * de destruir não é empurrado - um destroço a deslizar seria a coisa que
       * essa regra existe para impedir.
       *
       * E um casco JÁ empurrado não recebe o segundo: o dano aplicou-se acima,
       * como o §6 exige, mas o empurrão em curso não é interrompido nem
       * reiniciado. A guarda é `=== null` e não uma comparação de forças: "o
       * mais forte ganha" seria uma regra que ninguém escreveu.
       */
      if (victim.healthMilli > 0n && victim.knockback === null) {
        victim.knockback = pvpKnockbackFrom({
          shipX: victim.x,
          shipY: victim.y,
          blastX: x,
          blastY: y,
          entropy: selection.entropy,
          seatIndex: this.seatIndexOf(victim.participantId),
        });
      }
    }

    /*
     * O anúncio, com o raio MECÂNICO.
     *
     * Emitido sempre que a bomba detona - contra um casco, contra uma pilastra,
     * contra o limite ou por fim de vida - porque o jogador tem de ver a
     * explosão em todos eles. O que varia é `hit`, que é a lista de quem
     * realmente levou dano e não a de quem estava por perto.
     */
    this.events.push({
      kind: 'blast',
      effectId: ATOMIC_BLAST_EFFECT,
      participantId: owner.participantId,
      team: owner.team,
      projectileId: projectile.id,
      tick: this.currentTick,
      x,
      y,
      radius: PVP_BOMB_BLAST_RADIUS_MU,
      hit,
    });
  }

  /**
   * @param attacker quem deu o golpe, para o Roubo de Vida saber a quem devolver
   *   (gate V28G.I). NOMEADO por cada call site e nunca inferido: o alvo não
   *   sabe quem lhe bateu, e derivá-lo do projétil aqui dentro seria a mesma
   *   inferência que a origem tipada já recusa.
   * @param projectileId o tiro que trouxe o golpe, e a chave de deduplicação de
   *   um sifão confirmado. Passado mesmo quando a origem não pode curar, para a
   *   assinatura ser uniforme e a decisão ficar na tabela fechada.
   *
   *   `null` para um golpe que **nenhum tiro trouxe** (gate V28G.M): uma parcela
   *   de veneno cai um segundo depois do acerto, e o projétil que a semeou já
   *   não existe. Inventar-lhe um id seria a maneira de um cliente deduplicar
   *   duas coisas diferentes uma contra a outra.
   */
  private applyDamage(
    target: PvpParticipantShipState,
    damageMilli: bigint,
    origin: PvpDamageOrigin,
    x: number,
    y: number,
    attacker: PvpParticipantShipState,
    projectileId: number | null,
  ): bigint {
    if (target.healthMilli <= 0n || damageMilli <= 0n) {
      return 0n;
    }
    /*
     * **A Marca da Caveira, e ela é do ATACANTE** (gate V28H.L).
     *
     * Primeira de todas, e antes da Égide do destinatário: a Marca reduz o dano
     * que um casco CAUSA, portanto ela encolhe o payload antes de ele chegar a
     * quem o leva. A ordem importa e é esta - se a Marca viesse depois da
     * redução, duas percentagens compostas dariam um número diferente do que
     * qualquer das duas anuncia.
     *
     * Vale para as onze origens hostis pela mesma razão que a Égide vale:
     * esta é a fronteira única do duelo. Um casco marcado bate menos com um
     * tiro, com uma serra, com uma bomba e com um canhão temporário.
     */
    const markedMilli = cardDamageOutMilli(attacker.cardEffects, damageMilli);
    if (markedMilli <= 0n) {
      return 0n;
    }
    damageMilli = markedMilli;
    /*
     * **A redução do DESTINATÁRIO, e é aqui que ela acontece** (gate V28G.Q).
     *
     * ## Porque exatamente neste ponto, e não noutro
     *
     * Porque este é o ponto do §"ordem de resolução" do gate, e a ordem sai da
     * estrutura em vez de uma sequência escrita:
     *
     * 1. o payload hostil bruto já foi formado pelo chamador;
     * 2. a Reflexão já correu - `resolveReflected` chama ESTE método com o
     *    atacante no lugar do alvo, portanto quem chega aqui é sempre o
     *    destinatário final, e a Égide que se aplica é a DELE;
     * 3. a redução, esta linha;
     * 4. o clamp pela vida restante, em `resolveIntegerStrike`, logo abaixo;
     * 5. o `applied` que sai dele;
     * 6. o Roubo de Vida, que multiplica esse `applied` e mais nada.
     *
     * Um refletor recebe zero porque o payload nunca chega a este método por ele;
     * o payload integral segue para o atacante, e é a redução DELE que o encolhe.
     *
     * ## Porque uma linha e não um `if` por origem
     *
     * Porque todas as onze origens hostis do domínio passam por aqui - é a
     * fronteira única do duelo - e `damage/damage-reduction.ts` tem a tabela
     * fechada que o prova. Uma condicional por call site seria onze sítios onde a
     * décima segunda origem se esquece de reduzir.
     *
     * O que NÃO passa por aqui também é a resposta: uma eliminação, um abandono,
     * um tempo esgotado e um encerramento administrativo DEFINEM a vida, não
     * causam dano, e por isso não veem multiplicador nenhum.
     *
     * ## Sem consulta e sem alocação
     *
     * `resolveJewelEffects` lê um `Map` construído ao carregar o módulo e um
     * perfil sem esta joia devolve `NO_JEWEL_EFFECTS` por identidade - portanto
     * um duelo sem ela produz `BPS_ONE`, e `reduceHostileDamageMilli` devolve o
     * mesmo `bigint` que recebeu, sem multiplicar e sem dividir.
     */
    const hostileMilli = reduceHostileDamageMilli(
      damageMilli,
      hostileDamageMultiplierBps(resolveJewelEffects(target.profile.jewels).damageReductionBps),
    );
    if (hostileMilli <= 0n) {
      /*
       * A redução consumiu o golpe inteiro antes de ele chegar à vida.
       *
       * Alcançável só para um payload abaixo de dois milli - nada no duelo de
       * hoje o produz - e nomeado em vez de deixado cair na subtração abaixo,
       * para que "não houve golpe" e "o golpe foi zero" continuem a ser a mesma
       * resposta escrita uma vez.
       */
      return 0n;
    }
    /*
     * **O clamp, e é uma subtração** (gate V28G.S.1).
     *
     * O golpe e a vida vivem os dois em milli, portanto o dano aplicado é o
     * mínimo entre os dois e mais nada: sem conversão, sem truncatura e sem
     * resto para segurar. Um golpe de 10^43 milli e um de 40 400 matam um casco
     * de 30 000 milli da mesma maneira, e nenhum dos dois deixa fração por
     * pagar - porque não há nenhuma a perder.
     *
     * Era aqui que vivia `resolveIntegerStrike`, com `damageCarryMilli` ao lado
     * dele. Os dois existiam para converter milli-dano numa barra INTEIRA, e
     * essa barra deixou de existir.
     *
     * O overkill continua sem ser contado: `applied` é o que a vida tinha, e
     * nunca o que o golpe trazia.
     */
    const applied = hostileMilli > target.healthMilli ? target.healthMilli : hostileMilli;
    target.healthMilli -= applied;
    if (applied <= 0n) {
      /*
       * Um golpe que não chegou a um milésimo. Não emite `hit` - a regra do
       * `CLAUDE.md` é que cada acerto imprime o SEU número, e imprimir zero seria
       * imprimir um número que não é o dano de nada.
       *
       * Alcançável só contra um casco já em zero, que a guarda no topo deste
       * método já recusa. Fica como recusa estrutural.
       */
      return 0n;
    }
    /*
     * O Roubo de Vida, aqui e em mais nenhum sítio (gate V28G.I).
     *
     * Esta é a única linha do duelo onde um golpe já ACONTECEU e o que ele
     * removeu ainda está na mão. `applied` é o dano depois do clamp do overkill
     * - um tiro de 40 num casco com 3 de vida vale 3 aqui - e é essa a base que
     * o contrato manda usar, revogando a D-4.3 do V28D.2.
     *
     * A pergunta é à tabela fechada, e não a uma lista de exclusões:
     * `damageOriginMayTriggerJewel` responde `true` só para `cannon`, portanto o
     * corte do Ciclone - que entra aqui com origem `jewel` - não cura, e uma
     * origem nova sem decisão é um erro de compilação.
     *
     * ANTES do evento e antes da morte do alvo, mas isso não é uma escolha de
     * ordem: a cura sai do que já foi removido, e um alvo que morre do golpe não
     * a anula, porque o dano comum aconteceu mesmo.
     */
    this.events.push({
      kind: 'hit',
      participantId: target.participantId,
      team: target.team,
      x,
      y,
      amount: applied,
    });
    /*
     * O Roubo de Vida, DEPOIS do golpe e do seu número (gate V28G.I).
     *
     * A ordem da fila é a ordem de leitura de uma cena: primeiro o dano, depois
     * o sifão que ele produziu. A ordem MECÂNICA é indiferente - a cura já foi
     * decidida por `applied`, e nada entre as duas linhas lhe toca - portanto a
     * única razão para a escolher é a legibilidade, e é essa.
     */
    if (damageOriginMayTriggerJewel(origin)) {
      this.stealLife(attacker, target, applied, projectileId);
    }
    if (target.healthMilli === 0n) {
      // Um destroço não leva resto nenhum para a ronda seguinte (gate V28G.I).
      target.lifestealCarryBps = 0;
      /* E não continua a ser empurrado: a morte encerra o estado (gate V28G.J). */
      target.knockback = null;
      pvpClearEffects(target.effects);
      /*
       * E o veneno morre com ele (gate V28G.M).
       *
       * O §"se o alvo morrer, todos os stacks daquele alvo são removidos" é uma
       * regra sobre o ALVO e não sobre quem os aplicou: dois atacantes que
       * envenenaram o mesmo casco perdem os dois, ao mesmo tempo, porque o que
       * acabou foi a coisa que os carregava.
       */
      this.clearPoisonOf(target.participantId);
      /*
       * E o sangramento morre com ele, nos DOIS sentidos (gate V28G.N).
       *
       * As marcas que ele carregava saem porque o §"desaparece quando o alvo
       * morre" é sobre o alvo; as que ele tinha posto noutros saem porque o
       * §"desaparece quando o autor morre" é sobre o autor, e um destroço não dá
       * ordens. As duas metades numa chamada, para não haver um caminho de morte
       * que se lembre de uma e esqueça a outra.
       */
      target.detonateBleed = false;
      this.clearBleedingAround(target.participantId);
      /*
       * **E as serras param no mesmo instante** (gate V28G.P).
       *
       * Nos dois sentidos, como as marcas: as lâminas DELE deixam de existir - o
       * §"ao morrer a nave proprietária, todas as serras deixam de causar dano
       * imediatamente" é sobre o dono - e os relógios que outras lâminas
       * guardavam SOBRE ele saem porque o alvo acabou. Aqui e não no tique
       * seguinte: uma serra que continuasse a cortar um destroço seria dano
       * depois da morte.
       *
       * A fase volta ao princípio e o resto é largado, pela mesma regra dos
       * outros: um destroço não leva nada para a ronda seguinte.
       */
      target.sawPhase = SAW_INITIAL_PHASE;
      target.sawCarryMilli = 0n;
      this.clearSawContactsAround(target.participantId);
      this.events.push({
        kind: 'death',
        participantId: target.participantId,
        team: target.team,
        x: target.x,
        y: target.y,
      });
    }
    /*
     * Where the procs will hang.
     *
     * Empty today: `jewelTriggersFor` returns a frozen, shared empty array, so
     * this allocates nothing and iterates zero times. A `jewel` blow would get
     * the same empty array for a DIFFERENT reason - the table forbids it - and
     * that difference is the whole contract.
     */
    for (const effect of jewelTriggersFor(origin)) {
      throw new RangeError(`no jewel effect is registered in this gate: ${effect}`);
    }
    return applied;
  }

  /**
   * **Um tique de voo de cada míssil.** Perseguir, virar, andar, e resolver.
   *
   * Separado de {@link PvpSimulation.advanceProjectiles} porque são entidades
   * diferentes com regras que não se tocam - e mantê-las separadas é o que faz
   * este gate não poder alterar um tiro comum nem uma bomba por acidente.
   */
  private advanceMissiles(): void {
    const survivors: PvpMissileState[] = [];
    for (const missile of this.missiles) {
      if (this.stepMissile(missile)) {
        survivors.push(missile);
      }
    }
    /*
     * **Os mísseis refletidos entram DEPOIS do laço** (gate V28G.O), pela mesma
     * razão exata que as bombas refletidas entram depois do laço dos projéteis:
     * um `push` durante a passagem seria visitado por ela, e o míssil daria um
     * passo no tique em que nasceu.
     *
     * O teto não pode ser ultrapassado: cada um destes substitui um que acabou
     * de sair.
     */
    if (this.pendingMissiles.length > 0) {
      survivors.push(...this.pendingMissiles);
      this.pendingMissiles = [];
    }
    this.missiles = survivors;
  }

  /** Devolve se o míssil continua no ar depois deste tique. */
  private stepMissile(missile: PvpMissileState): boolean {
    missile.life -= 1;
    if (missile.life <= 0) {
      /*
       * **Nove segundos, e acabou.** Explode onde está, e **não fere ninguém**.
       *
       * `null` para o alvo direto é o que torna a detonação inofensiva - a mesma
       * forma que a Bomba usa para o mesmo caso, e a única diferença é que a
       * Bomba ainda tem uma área e este não tem nada.
       */
      this.explodeMissile(missile, null);
      return false;
    }
    /*
     * **O RETARGET, e ele acontece antes de qualquer movimento.**
     *
     * Um alvo que morreu já não é um alvo, e continuar a persegui-lo por um
     * tique poria o míssil a curvar na direção de um destroço. A escolha nova sai
     * do mesmo fluxo de alvos do DONO e é uniforme entre os inimigos vivos,
     * exatamente como a primeira foi.
     *
     * Sem inimigo nenhum vivo, o contrato é explícito: explode visualmente e
     * desaparece. Não fica à espera, não paira e não escolhe uma direção.
     */
    let target =
      missile.targetParticipantId === null
        ? undefined
        : this.roster.get(missile.targetParticipantId);
    if (target === undefined || target.healthMilli <= 0n) {
      const owner = this.roster.require(missile.ownerParticipantId);
      const next = this.chooseMissileTarget(owner);
      if (next === null) {
        this.explodeMissile(missile, null);
        return false;
      }
      missile.targetParticipantId = next.participantId;
      target = next;
    }
    /*
     * **Para onde apontar: o ponto de INTERCEPTAÇÃO** (gate V28G.L.1).
     *
     * Onde o casco VAI ESTAR, e não onde ele está. Perseguir a posição atual é
     * perseguir o passado: o míssil chega sempre atrasado e acaba a arrastar-se
     * atrás do alvo numa curva que nunca fecha.
     */
    const aim = pvpMissileAimPoint(
      { x: missile.x, y: missile.y },
      { x: target.x, y: target.y },
      this.shipVelocity(target),
      PVP_MISSILE_SPEED_MU_PER_TICK,
      missile.life,
    );
    /*
     * **A viragem, e a regra que mata a órbita.**
     *
     * Vira até 12 graus para o ponto de mira — a não ser que ele esteja DENTRO
     * do círculo de viragem, caso em que virar é gastar giro numa curva que a
     * geometria não deixa fechar. Aí segue em frente: a distância cresce, e
     * quando ela passa `2R` a perseguição volta a poder fechar sozinha.
     */
    /*
     * A caixa de captura mede o casco do ALVO que ele persegue (gate V28G.R).
     *
     * O primeiro termo é o raio real desse casco, e não a constante da arena: um
     * alvo maior é alcançado mais cedo, e o círculo de viragem que decide se
     * vale a pena virar é medido contra o mesmo número que a colisão usa.
     */
    const hitRadius = pvpHullRadiusMu(target.profile) + PVP_MISSILE_HIT_RADIUS_MU;
    const steered = pvpMissileSteer(
      { x: missile.dirX, y: missile.dirY },
      { x: missile.x, y: missile.y },
      aim,
      hitRadius,
    );
    missile.dirX = steered.x;
    missile.dirY = steered.y;

    const origin = { x: missile.x, y: missile.y };
    const step = scaleDirection(
      { x: missile.dirX, y: missile.dirY },
      PVP_MISSILE_SPEED_MU_PER_TICK,
    );

    /*
     * **A colisão VARRIDA sobre o segmento inteiro do tique.**
     *
     * Quatro amostras chegavam por pouco — 26 350 unidades por sub-passo contra
     * 61 525 de captura — e deixaram de chegar assim que a correção da órbita pôs
     * o míssil a atacar de frente e à velocidade máxima. Um segmento não tem
     * buracos, e o instante do contacto é uma fração exata do passo.
     *
     * Quem ele pode atingir: a outra equipa, e mais ninguém. **Qualquer** inimigo
     * hostil, e não só o que ele persegue — um aliado que se atravesse não é
     * tocado, porque `enemiesOf` nunca o devolve.
     */
    let firstAt: PvpSweepHit | null = null;
    let struck: PvpParticipantShipState | null = null;
    for (const enemy of this.roster.enemiesOf(missile.ownerTeam)) {
      if (enemy.healthMilli <= 0n) {
        continue;
      }
      /*
       * Por inimigo varrido, e não o do alvo perseguido: um casco que se
       * atravessa é apanhado pelo SEU tamanho, não pelo de outro.
       */
      const enemyHitRadius = pvpHullRadiusMu(enemy.profile) + PVP_MISSILE_HIT_RADIUS_MU;
      const at = pvpSweepCircle(origin, step, { x: enemy.x, y: enemy.y }, enemyHitRadius);
      if (at === null) {
        continue;
      }
      /*
       * O MAIS CEDO, e um empate fica com o primeiro em ordem canónica: o roster
       * entrega-a assim, e um `<` estrito preserva-o.
       */
      if (firstAt === null || pvpSweepEarlier(at, firstAt)) {
        firstAt = at;
        struck = enemy;
      }
    }
    /* A PAREDE, com a espessura do CORPO e não da caixa de captura de um casco. */
    for (const obstacle of PVP_OBSTACLES) {
      const at = pvpSweepBox(origin, step, obstacle, PVP_MISSILE_BODY_RADIUS_MU);
      if (at !== null && (firstAt === null || pvpSweepEarlier(at, firstAt))) {
        firstAt = at;
        struck = null;
      }
    }
    /* E o LIMITE do mundo, pela mesma regra e no mesmo instante comparável. */
    const leaving = pvpSweepBounds(origin, step, PVP_ARENA_WIDTH_MU, PVP_ARENA_HEIGHT_MU);
    if (leaving !== null && (firstAt === null || pvpSweepEarlier(leaving, firstAt))) {
      firstAt = leaving;
      struck = null;
    }

    if (firstAt !== null) {
      /*
       * Ele pára ONDE tocou, e não no fim do passo: o centro da explosão é o
       * ponto de contacto, e um míssil que detonasse cem mil unidades adiante
       * mostraria fogo onde não aconteceu nada.
       */
      const at = pvpSweepPoint(origin, step, firstAt);
      missile.x = at.x;
      missile.y = at.y;
      /*
       * **A REFLEXÃO DO MÍSSIL, decidida quando ele ALCANÇARIA o alvo** (gate
       * V28G.O).
       *
       * Nesta linha e não depois de `explodeMissile`, porque o §5 do gate manda
       * decidir "quando o míssil alcançaria o alvo, antes da explosão/dano": o
       * original tem de desaparecer **sem explosão, dano ou efeito** no
       * refletor, e uma explosão suprimida depois de acontecer já emitiu o seu
       * evento.
       *
       * Um míssil que vai bater numa pilastra, no limite ou que esgota os nove
       * segundos não passa por aqui - nesses casos `struck` é `null`, e a
       * ausência de alvo é a ausência de refletor. As paredes continuam a
       * bloquear exatamente como bloqueavam.
       */
      if (struck !== null && this.reflectMissile(missile, struck)) {
        return false;
      }
      this.explodeMissile(missile, struck);
      return false;
    }

    missile.x = origin.x + step.x;
    missile.y = origin.y + step.y;
    return true;
  }

  /**
   * O deslocamento que um casco vai fazer neste tique, para o míssil o prever.
   *
   * Derivado dos MESMOS campos que `moveShip` usa - a direção normalizada da
   * intenção e a velocidade efetiva do casco - em vez de guardado. Um segundo
   * sítio a lembrar-se da velocidade de uma nave seria um sítio para divergir do
   * primeiro, e a previsão passaria a apontar para onde ninguém vai.
   */
  private shipVelocity(ship: PvpParticipantShipState): IntVector {
    if (ship.healthMilli <= 0n || (ship.moveX === 0 && ship.moveY === 0)) {
      return { x: 0, y: 0 };
    }
    return scaleDirection(
      movementDirection(ship.moveX, ship.moveY),
      pvpTickTravelMu(this.moveSpeedOf(ship)),
    );
  }

  /**
   * **A explosão do míssil.** Um golpe, ou nenhum, e o anúncio.
   *
   * ## As regras, e nenhuma delas é um caso especial
   *
   * - **só o casco atingido leva**, e leva uma vez. Não há área, não há vizinhos
   *   e não há segunda passagem - `direct` é o único alvo que existe;
   * - **contra parede, limite ou fim de vida não há alvo nenhum**, e a ausência
   *   escreve-se `direct === null` em vez de um alvo inventado. Nenhuma dessas
   *   três explosões causa dano;
   * - **não há empurrão**. Nem aqui nem em lado nenhum: o orçamento inteiro
   *   desta joia é pago em dano, e é por isso que a magnitude é 90 % em vez dos
   *   112,5 % da Bomba;
   * - **aliados nunca**, porque `enemiesOf` já respondeu à pergunta antes de
   *   este método ser chamado.
   *
   * ## A origem é `jewel`, e o que isso desliga sozinho
   *
   * Tudo. `damageOriginMayTriggerJewel('jewel')` é falso, e é essa tabela que faz
   * o golpe do míssil não acionar outro proc, não acionar o Roubo de Vida, não
   * criar Ciclone e não lançar um segundo míssil. Nada disto é uma verificação
   * escrita aqui: a profundidade de recursão é um porque não existe aresta no
   * grafo que lhe dê duas.
   *
   * ## O raio anunciado é ZERO, e isso é a verdade
   *
   * O evento de explosão carrega o raio MECÂNICO - o que decidiu quem levou - e
   * o do míssil não existe. Zero diz "não houve área", e é o que impede uma cena
   * de desenhar um círculo que pareça uma zona de dano.
   */
  private explodeMissile(missile: PvpMissileState, direct: PvpParticipantShipState | null): void {
    const owner = this.roster.require(missile.ownerParticipantId);
    const hit: PvpParticipantId[] = [];
    if (direct !== null) {
      const applied = this.applyDamage(
        direct,
        missile.damageMilli,
        /* A origem que fecha o grafo. Nunca `cannon`. */
        'jewel',
        missile.x,
        missile.y,
        owner,
        missile.id,
      );
      if (applied > 0) {
        hit.push(direct.participantId);
        /*
         * **`REFLECT` sobre o golpe de um míssil refletido** (gate V28G.O).
         *
         * O §7 manda que o número apareça quando o novo payload **finalmente
         * causar dano**, e não no relançamento - portanto é aqui, e não em
         * `reflectMissile`. Um míssil refletido que vá bater numa pilastra nunca
         * chega a esta linha, e nunca imprime número nenhum: o brilho que o
         * refletor já viu é que dizia que ele defendeu.
         */
        if (missile.reflected) {
          this.announceReflection(owner, direct, 'missile_impact', 'landed', applied);
        }
      }
    }
    this.events.push({
      kind: 'blast',
      effectId: MISSILE_EFFECT,
      participantId: owner.participantId,
      team: owner.team,
      projectileId: missile.id,
      tick: this.currentTick,
      x: missile.x,
      y: missile.y,
      /* Sem área: o raio mecânico é zero, e a cena desenha fogo compacto. */
      radius: 0,
      hit,
    });
  }

  private advanceProjectiles(): void {
    const survivors: PvpParticipantProjectileState[] = [];
    for (const projectile of this.shots) {
      if (this.stepProjectile(projectile)) {
        survivors.push(projectile);
      }
    }
    /*
     * **As bombas refletidas entram DEPOIS do laço** (gate V28G.O).
     *
     * Um `push` durante a passagem seria visitado por ela - um `for...of` sobre
     * um array vê o que lhe foi acrescentado - e a bomba refletida daria um
     * passo no tique em que nasceu, antes de o refletor sequer a ter mostrado.
     *
     * O teto não pode ser ultrapassado: cada uma destas substitui uma que
     * acabou de sair, portanto a lista não cresce.
     */
    if (this.pendingShots.length > 0) {
      survivors.push(...this.pendingShots);
      this.pendingShots = [];
    }
    this.shots = survivors;
  }

  /**
   * **A explosão de uma CARTA**, se este projétil carregava uma.
   *
   * Um raio e um dano próprios, e origem `card` - portanto nada disto activa
   * uma joia, que é o contrato do catálogo escrito na tabela fechada.
   *
   * **Apanha quem estiver no raio, inalvejável ou não.** É a fraqueza declarada
   * do Bastião: uma explosão não ESCOLHE ninguém, ela cobre uma área, e a carta
   * que impede ser escolhido não tem por onde a impedir.
   *
   * O evento é um `blast`, o mesmo que a Bomba Atômica publica, porque é o
   * mesmo facto para quem desenha: uma coisa rebentou aqui e apanhou estes.
   */
  /**
   * **O dardo de gelo chegou: a sala decide quanto tempo, e se trava o ataque.**
   *
   * As duas tiradas saem do id do projéctil (gate V28H.W) - determinista, igual
   * na re-simulação, e diferente de tiro para tiro. Os dois `salt` separam-nas:
   * sem eles, a duração e o travamento seriam a mesma resposta lida duas vezes.
   *
   * O travamento viaja na MAGNITUDE da ranhura do congelamento, cheia ou zero -
   * ver `cardFreezeBlocksAttack`. Nenhuma forma nova em lado nenhum.
   */
  private applyBoltImpact(
    projectileId: number,
    kind: 'ice_sphere' | 'ice_star' | 'shock_bolt',
    victim: PvpParticipantShipState,
  ): void {
    if (kind === 'shock_bolt') {
      /*
       * **A energia elétrica chega** (gate V28H.X): cadência a menos, e nada
       * sorteado. A descrição do Curto-Circuito não tem "entre X e Y" nenhum -
       * são quarenta por cento e seis segundos, sempre - portanto ele não passa
       * por `rolls.ts`. Um sorteio inventado aqui seria uma regra que ninguém
       * escreveu.
       */
      const shock = cardBalance('card_attack_break');
      if (shock !== null) {
        /*
         * Pela MESMA função que a via instantânea usa para escolher a ranhura -
         * `debuffSlotOf` - e não pelo nome do campo escrito à mão: a carta diz
         * qual é o seu debuff na tabela, e um segundo sítio a decidir isso seria
         * um sítio para discordar do primeiro.
         */
        applyCardTimer(this.debuffSlotOf(victim, shock), shock.durationMs, shock.magnitudeBps);
      }
      return;
    }
    if (kind === 'ice_star') {
      const balance = cardBalance('card_slow');
      applyCardTimer(
        victim.cardEffects.slow,
        pvpRollDurationMs(projectileId, 1, balance?.durationMs ?? 3_000, PVP_SLOW_DURATION_STEPS),
        balance?.magnitudeBps ?? 5_000,
      );
      return;
    }
    const balance = cardBalance('card_freeze');
    const blocksAttack = pvpRollHits(projectileId, 2, PVP_FREEZE_ATTACK_BLOCK_BPS);
    applyCardTimer(
      victim.cardEffects.frozen,
      pvpRollDurationMs(projectileId, 3, balance?.durationMs ?? 2_000, PVP_FREEZE_DURATION_STEPS),
      /* Cheia é paralisia total; zero é só o movimento, que é sempre travado. */
      blocksAttack ? (balance?.magnitudeBps ?? 10_000) : 0,
    );
  }

  private detonateCardBlast(
    projectile: PvpParticipantProjectileState,
    direct: PvpParticipantShipState | null,
  ): void {
    /*
     * `?? null` e não `=== null`: um projétil pode chegar aqui sem o campo -
     * um documento anterior à versão 20, ou uma fixture que injecta o mínimo -
     * e `undefined` significa exactamente o mesmo que `null`. Distingui-los
     * seria uma diferença sem consequência a produzir uma excepção.
     */
    const blast = projectile.cardBlast ?? null;
    if (blast === null) {
      return;
    }
    const owner = this.roster.get(projectile.ownerParticipantId);
    if (owner === undefined) {
      return;
    }
    const x = direct === null ? projectile.x : direct.x;
    const y = direct === null ? projectile.y : direct.y;
    const hit: PvpParticipantId[] = [];
    for (const victim of this.roster.enemiesOf(projectile.ownerTeam)) {
      if (victim.healthMilli <= 0n) {
        continue;
      }
      const reach = blast.radiusMu + pvpHullRadiusMu(victim.profile);
      if (distanceSquared(x, y, victim.x, victim.y) > reach * reach) {
        continue;
      }
      const applied = this.applyDamage(victim, blast.damageMilli, 'card', x, y, owner, null);
      if (applied > 0n) {
        hit.push(victim.participantId);
      }
      /*
       * **E o Catolé EMPURRA** (gate V28H.U), três vezes mais do que a Bomba.
       *
       * Decisão aprovada, e a física é a MESMA: a mesma queda com a distância, a
       * mesma direcção desempatada, o mesmo reparto pelos tiques. O que muda são
       * dois números que a função recebe - duplicá-la seria duplicar a física.
       *
       * Um casco já empurrado não recebe o segundo, e a guarda é `=== null` e
       * não uma comparação de forças: "o mais forte ganha" seria uma regra que
       * ninguém escreveu. É a mesma que a Bomba já segue.
       *
       * A Esfera não empurra: a descrição dela fala de uma esfera explosiva, e
       * acrescentar-lhe um empurrão seria inventar.
       */
      if (blast.kind === 'catole' && victim.healthMilli > 0n && victim.knockback === null) {
        victim.knockback = pvpKnockbackFrom({
          shipX: victim.x,
          shipY: victim.y,
          blastX: x,
          blastY: y,
          /*
           * A entropia do PROJÉTIL e não uma tirada nova: ela desempata a
           * direcção de quem está exactamente em cima da explosão, e tem de ser
           * a mesma nos dois lados que re-simulam.
           */
          entropy: projectile.id,
          seatIndex: this.seatIndexOf(victim.participantId),
          blastRadiusMu: blast.radiusMu,
          strengthMu: PVP_CATOLE_KNOCKBACK_MU,
        });
      }
    }
    this.events.push({
      kind: 'blast',
      /*
       * **A arte de CADA uma** (gate V28H.U), e já não a da Bomba Atômica.
       *
       * Elas partilhavam-na, e o resultado era o Catolé - que tem seis vezes o
       * raio da Bomba - a ler-se igual a uma esfera de fogo. A cena escolhe pelo
       * id e nunca pelo tamanho: adivinhar pela medida seria decidir a arte a
       * partir de um número de equilíbrio.
       */
      effectId: blast.kind === 'catole' ? CATOLE_BLAST_EFFECT : FIREBALL_BLAST_EFFECT,
      participantId: owner.participantId,
      team: owner.team,
      projectileId: projectile.id,
      tick: this.currentTick,
      x,
      y,
      radius: blast.radiusMu,
      hit,
    });
  }

  /**
   * Curva um projétil teleguiado na direcção do seu alvo, um tique.
   *
   * Nada acontece a quem não persegue ninguém - que é quase todo o projétil - e
   * nada acontece se o alvo já morreu: ele segue a direito para onde ia, e
   * rebenta lá. Não escolhe outro, pela razão escrita em `homingTargetId`.
   *
   * A correcção é uma fracção da velocidade, aplicada à velocidade e
   * renormalizada: tudo inteiro, sem ângulo, sem seno e sem vírgula.
   */
  private steerHoming(projectile: PvpParticipantProjectileState): void {
    const targetId = projectile.homingTargetId ?? null;
    if (targetId === null) {
      return;
    }
    const target = this.roster.get(targetId);
    if (target === undefined || target.healthMilli <= 0n) {
      return;
    }
    const wanted = normalise(target.x - projectile.x, target.y - projectile.y);
    if (wanted === null) {
      return;
    }
    const speed = isqrt(projectile.vx * projectile.vx + projectile.vy * projectile.vy);
    if (speed === 0) {
      return;
    }
    /*
     * A mistura é feita entre DIREÇÕES unitárias, e não entre velocidades.
     *
     * Misturar as velocidades directamente estourava o inteiro seguro: uma
     * componente de 15 500 vezes `VECTOR_SCALE` dá 10^9, e `normalise` eleva ao
     * quadrado - 10^18, muito acima de `MAX_SAFE_INTEGER`. Com unitários cada
     * termo cabe em `VECTOR_SCALE`, o quadrado fica em 10^9, e a aritmética é a
     * mesma curva.
     */
    const current = normalise(projectile.vx, projectile.vy);
    if (current === null) {
      return;
    }
    const keep = VECTOR_SCALE - PVP_CARD_HOMING_BPS;
    const nudgedX =
      divTrunc(current.x * keep, VECTOR_SCALE) +
      divTrunc(wanted.x * PVP_CARD_HOMING_BPS, VECTOR_SCALE);
    const nudgedY =
      divTrunc(current.y * keep, VECTOR_SCALE) +
      divTrunc(wanted.y * PVP_CARD_HOMING_BPS, VECTOR_SCALE);
    const heading = normalise(nudgedX, nudgedY);
    if (heading === null) {
      return;
    }
    const next = scaleDirection(heading, speed);
    (projectile as { vx: number; vy: number }).vx = next.x;
    (projectile as { vx: number; vy: number }).vy = next.y;
  }

  /** Returns whether the projectile is still in flight after this tick. */
  private stepProjectile(projectile: PvpParticipantProjectileState): boolean {
    projectile.life -= 1;
    if (projectile.life <= 0) {
      /*
       * Expired. A duel shot never re-acquires and never lingers.
       *
       * Uma BOMBA não expira em silêncio: o §3 diz que ela detona quando termina
       * o seu tempo de vida, e detonar sem alvo direto é exatamente o caso que o
       * §4 descreve - "não existe dano direto: somente o dano adicional em
       * área". Onde ela estiver, e mais nada.
       */
      if (projectile.atomicBomb !== null) {
        this.detonateBomb(projectile, null);
      }
      /* E uma explosão de CARTA também não expira em silêncio (V28H.L). */
      this.detonateCardBlast(projectile, null);
      return false;
    }
    /*
     * **A perseguição do Catolé, antes de ele andar** (gate V28H.L).
     *
     * Uma correcção de rumo por tique, limitada, e sobre a velocidade que ele já
     * tinha - portanto ele curva, e não vira. O alvo foi escolhido no
     * lançamento e nunca é renegociado: um projétil que trocasse de alvo a meio
     * seria um projétil que ninguém consegue evitar.
     */
    this.steerHoming(projectile);
    const originX = projectile.x;
    const originY = projectile.y;
    /*
     * Who this shot may hit: the other team, and nobody else.
     *
     * `otherSeat(projectile.seat)` used to answer this, and it could only ever
     * have answered it for two ships. The owner is excluded because it is on the
     * owning team, and so is every ally - one list, computed once by the roster,
     * says both.
     */
    const targets = this.roster.enemiesOf(projectile.ownerTeam);
    const bomb = projectile.atomicBomb !== null;
    /*
     * O raio a que este projétil apanha um casco.
     *
     * Um tiro comum toca-o: `casco + bala`. Uma bomba **aproxima-se** dele, e o
     * raio é `casco + proximidade` - 65 000 mu, ainda dentro dos 78 000 do raio
     * final da explosão, que é a ordem que o §5 exige. É a mesma aritmética de
     * borda-a-borda dos dois lados; o que muda é a espessura do lado da bomba.
     */
    /*
     * Por ALVO, e não uma constante partilhada (gate V28G.R).
     *
     * O primeiro termo é o casco de quem LEVA, e cada casco tem o seu. Um alvo
     * maior é atingido mais cedo - é a contrapartida geométrica da vida a mais -
     * e uma medida partilhada faria um tiro atravessar o desenho de um deles.
     */
    /*
     * Uma explosão de CARTA aproxima-se como uma bomba (gate V28H.L).
     *
     * Ela não bate: rebenta. Com o raio fino de um tiro comum, um Catolé
     * teleguiado orbitava o alvo sem nunca lhe tocar - o raio da curva dele é
     * maior do que a espessura de uma bala - e ficava a andar às voltas até o
     * tempo de vida acabar. Medido, e é o defeito que esta linha corrige.
     */
    /*
     * **E a ESFERA apanha com o CORPO dela, que cresce com a carga** (V28H.Z).
     *
     * Ela tinha um raio fixo - a proximidade da Bomba mais metade, decidida no
     * gate anterior - e isso funcionava enquanto a bola era pequena. Depois de a
     * carga cheia passar a setenta cascos de raio, o desenho e a mecânica
     * deixaram de dizer a mesma coisa: a bola cobria uma nave inteira e
     * atravessava-a, porque o que rebentava era o centro dela a chegar a oitenta
     * e cinco mil.
     *
     * Agora é o mesmo número que a cena desenha - {@link cardSphereBodyRadius}
     * sobre o raio da explosão que ela vai abrir - e por isso a borda que se vê
     * é a borda que apanha, em qualquer carga.
     *
     * O CHÃO fica: uma esfera de carga mínima é uma bola pequena, e sem ele ela
     * passaria a apanhar menos do que apanhava antes desta mudança.
     */
    const sphere = projectile.cardBlast?.kind === 'fireball' ? projectile.cardBlast : null;
    /*
     * A parte SÓLIDA do corpo, e não o corpo inteiro (gate V28I.A): os últimos
     * vinte por cento da bola desenhada são orla luminosa, e ferir com eles
     * fazia-a rebentar antes de encostar.
     */
    const sphereBody =
      sphere === null
        ? 0
        : Math.max(PVP_FIREBALL_PROXIMITY_RADIUS_MU, cardSphereHitRadius(sphere.radiusMu));
    const projectileReach =
      sphere !== null
        ? sphereBody
        : bomb || projectile.cardBlast != null
          ? PVP_BOMB_PROXIMITY_RADIUS_MU
          : PVP_PROJECTILE_RADIUS_MU;
    /*
     * Uma bomba é um corpo maior, e toca uma pilastra mais cedo por isso - e uma
     * esfera carregada é o maior corpo do jogo, pela mesma razão e pelo mesmo
     * número. Uma bola que atravessasse uma pilastra que ela cobre seria a
     * mesma mentira, do outro lado.
     */
    const wallRadius =
      sphere !== null ? sphereBody : bomb ? PVP_BOMB_BODY_RADIUS_MU : PVP_PROJECTILE_RADIUS_MU;

    for (let step = 1; step <= PVP_PROJECTILE_SUBSTEPS; step += 1) {
      /*
       * Computed from the ORIGIN each substep rather than accumulated, so the
       * truncation of one substep never feeds the next. Four accumulated
       * truncations would slowly bend a shot's path.
       */
      projectile.x = originX + divTrunc(projectile.vx * step, PVP_PROJECTILE_SUBSTEPS);
      projectile.y = originY + divTrunc(projectile.vy * step, PVP_PROJECTILE_SUBSTEPS);

      if (
        projectile.x < 0 ||
        projectile.y < 0 ||
        projectile.x > PVP_ARENA_WIDTH_MU ||
        projectile.y > PVP_ARENA_HEIGHT_MU
      ) {
        /*
         * O LIMITE DO MAPA. Um tiro comum desaparece; uma bomba detona (§3).
         *
         * No ponto onde ela está, que é o primeiro sub-passo que saiu da caixa -
         * e não recortado para dentro. Recortar seria mover o centro da explosão
         * para um sítio onde a bomba não esteve, e o raio dela ia com ele.
         */
        if (bomb) {
          this.detonateBomb(projectile, null);
        }
        /* E uma explosão de CARTA, pela mesma regra e no mesmo ponto (V28H.L). */
        this.detonateCardBlast(projectile, null);
        return false;
      }
      for (const obstacle of PVP_OBSTACLES) {
        if (
          projectile.x >= obstacle.x - wallRadius &&
          projectile.x <= obstacle.x + obstacle.w + wallRadius &&
          projectile.y >= obstacle.y - wallRadius &&
          projectile.y <= obstacle.y + obstacle.h + wallRadius
        ) {
          /* A PAREDE. A mesma regra do limite, e pela mesma razão. */
          if (bomb) {
            this.detonateBomb(projectile, null);
          }
          this.detonateCardBlast(projectile, null);
          return false;
        }
      }
      /*
       * One shot takes one target.
       *
       * With a single enemy there was nothing to choose. With five there is, and
       * the choice is the CLOSEST hull by the same squared distance the hit test
       * already computes - no new physics, and the answer a player expects when
       * two ships overlap. An exact tie goes to the earlier participant in
       * canonical order, so the outcome cannot depend on how the roster was
       * built. The shot is then spent: it damages one hull, is removed once, and
       * never carries on to a second.
       */
      let hit: PvpParticipantShipState | null = null;
      let hitDistance = 0;
      for (const target of targets) {
        if (target.healthMilli <= 0n) {
          continue;
        }
        /*
         * **O Bastião: nada aterra** (gate V28H.L).
         *
         * Um casco inalvejável é saltado, e o tiro segue - ele não é absorvido,
         * atravessa. Inalvejável e não invulnerável: uma explosão, uma
         * hemorragia e um veneno continuam a cobrar, porque nenhuma delas
         * escolhe um casco - uma cobre uma área, e as outras já têm o seu alvo
         * desde que foram colocadas.
         *
         * O Manto NÃO entra aqui, e é a decisão que separa as duas cartas: ele
         * já dura o dobro, e nove segundos em que nada aterra seriam imunidade.
         * Não se acerta no que não se vê, e essa é a defesa dele.
         *
         * **E uma coisa que REBENTA também não entra**: uma bomba e uma explosão
         * de carta passam pelo Bastião como se ele não estivesse lá. Ele impede
         * ser ESCOLHIDO e impede um tiro de aterrar; uma explosão não escolhe
         * ninguém, cobre uma área, e é a fraqueza declarada dele. Sem esta
         * condição a Esfera Ígnea atravessava o casco e ia rebentar longe - o
         * Bastião passava a esquivar-se de explosões, que é o contrário do que
         * está escrito.
         */
        if (
          (!cardIsHittable(target.cardEffects) && !bomb && projectile.cardBlast === null) ||
          /* Um piloto está dentro do Leviatã: a estrutura leva por ele. */
          this.isPiloting(target.participantId)
        ) {
          continue;
        }
        const distance = distanceSquared(projectile.x, projectile.y, target.x, target.y);
        const hitRadius = pvpHullRadiusMu(target.profile) + projectileReach;
        if (distance > hitRadius * hitRadius) {
          continue;
        }
        if (hit === null || distance < hitDistance) {
          hit = target;
          hitDistance = distance;
        }
      }
      if (hit !== null) {
        /*
         * A PROXIMIDADE, e o alvo direto é o mais próximo.
         *
         * O laço acima já respondeu à pergunta do §3 - "se vários inimigos
         * entrarem simultaneamente no raio de proximidade, o alvo direto é o
         * mais próximo; empate por id canónico" - porque é a mesma pergunta que
         * um tiro comum faz, e `targets` chega em ordem canónica.
         */
        const owner = this.roster.require(projectile.ownerParticipantId);
        if (bomb) {
          /*
           * **A REFLEXÃO DA BOMBA, antes de a proximidade a fazer explodir**
           * (gate V28G.O).
           *
           * Nesta linha e não noutra, porque é aqui que o §5 do gate manda
           * decidir: "a reflexão é decidida antes de a proximidade com a nave
           * causar a explosão". Depois de `detonateBomb` já houve dano, área e
           * empurrão, e o contrato exige que o refletor não receba nenhum dos
           * três.
           *
           * Uma bomba que rebenta contra uma parede, contra o limite ou por fim
           * de vida **não passa por aqui** - esses ramos devolvem acima, com
           * `hit` inexistente - e é assim que o §"parede detonando Bomba não
           * inventa refletor" fica cumprido pela estrutura em vez de por uma
           * condição.
           */
          if (this.reflectBomb(projectile, hit, owner)) {
            return false;
          }
          this.detonateBomb(projectile, hit);
          return false;
        }
        /*
         * **A explosão de CARTA, ao tocar um casco** (gate V28H.L).
         *
         * Antes da reflexão do tiro comum e a devolver logo: uma Esfera Ígnea
         * não tem dano directo nenhum - ela rebenta - portanto não há um golpe
         * que se possa reflectir. Deixá-la cair no ramo de baixo faria a
         * Reflexão devolver zero de dano e consumir a carta na mesma.
         */
        if (projectile.cardBlast !== null) {
          /*
           * **Um dardo não rebenta: ele APLICA o efeito a quem tocou** (V28H.W).
           *
           * Antes da explosão e a devolver logo, pela mesma razão que a Esfera
           * Ígnea está antes da reflexão: ele não tem dano nem área, e deixá-lo
           * cair no ramo de baixo faria uma explosão de raio zero e um efeito
           * que nunca chegava a ninguém - que foi exactamente o que aconteceu ao
           * raio do Curto-Circuito enquanto esta pergunta era uma lista escrita
           * à mão. Agora ela é `pvpCardBodyDetonates`, e é exaustiva.
           */
          const bolt = projectile.cardBlast.kind;
          if (bolt !== 'fireball' && bolt !== 'catole' && !pvpCardBodyDetonates(bolt)) {
            this.applyBoltImpact(projectile.id, bolt, hit);
            return false;
          }
          this.detonateCardBlast(projectile, hit);
          return false;
        }
        /*
         * **A REFLEXÃO DO TIRO COMUM, antes do dano e de tudo o que dele
         * depende** (gate V28G.O).
         *
         * Aqui e não depois de `applyDamage`, porque o §2 exige que o efeito
         * original não cause dano NEM ESTADO ao refletor. Suprimir a resolução
         * inteira - e não só o número - é o que faz o corte, o veneno e a marca
         * não nascerem: as três linhas abaixo dependem de este acerto ter
         * acontecido, e ele não aconteceu.
         *
         * É o §4 do gate escrito como estrutura: "se o efeito original dependia
         * de dano realmente aplicado para gerar outro proc, refletir o dano-base
         * impede que esse proc dependente nasça."
         *
         * O projétil termina no contacto na mesma - `return false` - porque o
         * §5 é explícito: "o projétil original termina no contato normal" e
         * "não crie um projétil de retorno". O que atravessa a arena é um feixe
         * branco sem tempo de viagem, e o dano é aplicado imediatamente.
         */
        if (
          this.reflects(
            hit,
            owner,
            'cannon_shot',
            /* Um tiro de canhão é sempre original: uma reflexão não cria tiros. */
            'original',
          )
        ) {
          this.resolveReflected(hit, owner, projectile.damageMilli, 'cannon_shot', projectile.id);
          return false;
        }
        this.applyDamage(
          hit,
          projectile.damageMilli,
          'cannon',
          projectile.x,
          projectile.y,
          owner,
          projectile.id,
        );
        /*
         * O proc, e SÓ num acerto confirmado.
         *
         * Um tiro que erra consome a oportunidade sem produzir efeito: a
         * seleção foi feita no nascimento e não volta ao saco. É a mesma lógica
         * de uma bala que passa ao lado - a munição gastou-se.
         *
         * A ordem importa: o tiro comum aplica-se primeiro e o corte depois, o
         * que faz o corte encontrar a vida já reduzida. Um alvo que morre do
         * tiro não recebe o corte, e `applyDamage` recusa um alvo sem vida.
         */
        this.applyJewelProc(owner, hit, projectile);
        /*
         * **O VENENO, e só depois de o tiro comum ter acontecido** (V28G.M).
         *
         * Nesta linha e não noutra, porque é aqui que as três condições do §
         * "Gatilho" são todas verdadeiras ao mesmo tempo: houve um ACERTO
         * DIRETO, o projétil é de CANHÃO, e o alvo é o que a colisão escolheu.
         *
         * A BOMBA não chega aqui - o ramo dela devolve acima - e é assim que o
         * §"não pode aplicar: Bomba Atômica" fica cumprido pela estrutura em vez
         * de por uma condição. O míssil também não: ele resolve-se em
         * `explodeMissile`, que nunca passa por este ponto. E o corte do Ciclone
         * entra em `applyDamage` com origem `jewel`, que a tabela fechada já
         * recusa.
         *
         * O alvo pode ter morrido do tiro. `poisonDirectHit` pergunta-o antes de
         * gastar a rolagem, porque um stack num destroço é um stack que ninguém
         * paga.
         */
        this.poisonDirectHit(owner, hit, 'cannon');
        /*
         * **O SANGRAMENTO, na mesma linha e pelas mesmas três razões** (V28G.N).
         *
         * Aqui e não noutro sítio, porque é aqui que as três condições do §
         * "Aplicação das marcas" são todas verdadeiras ao mesmo tempo: houve um
         * ACERTO DIRETO, o projétil é de CANHÃO, e o alvo é o que a colisão
         * escolheu.
         *
         * A BOMBA não chega aqui - o ramo dela devolve acima - e é assim que o
         * §"não aplicam: Bomba Atômica" fica cumprido pela estrutura em vez de
         * por uma condição. O míssil resolve-se em `explodeMissile`, que nunca
         * passa por este ponto; o splash da bomba está fora deste ramo; e o
         * corte do Ciclone, o Roubo de Vida, o veneno e outra marca entram em
         * `applyDamage` com origem `jewel`, que a tabela fechada já recusa.
         *
         * DEPOIS do dano normal do tiro, e depois do veneno: o alvo pode ter
         * morrido, e `bleedingDirectHit` pergunta-o antes de gastar a rolagem.
         *
         * Um sorteio PRÓPRIO ao lado do do veneno, e não um intervalo disjunto:
         * uma marca e um stack podem nascer do mesmo acerto, e é isso que dois
         * fluxos separados significam.
         */
        this.bleedingDirectHit(owner, hit, 'cannon');
        return false;
      }
    }
    return true;
  }

  /**
   * Reads the deaths and the clock.
   *
   * A TEAM is out when every one of its ships is down, which is why losing one
   * of three ends nothing and losing the third ends everything. In a duel a team
   * has one ship, so the first death still ends the match on the tick it happens
   * - the rule did not change, only what it is counting.
   *
   * Emits nothing. Deaths are announced once, by the shot that caused them, so
   * asking for the result again - or recomputing it on a later tick - can never
   * produce a second event.
   */
  private resolveEnd(): void {
    const standing = PVP_TEAMS.filter((team) =>
      this.roster.team(team).some((ship) => ship.healthMilli > 0n),
    );
    if (standing.length === 0) {
      // Everybody fell on the same tick. Nobody won, and saying otherwise would
      // mean picking a team because it was checked first.
      this.finishTeam('mutual_destruction', null);
      return;
    }
    const survivor = standing[0];
    if (standing.length === 1 && survivor !== undefined) {
      this.finishTeam('death', survivor);
      return;
    }
    if (this.currentTick >= PVP_TIME_LIMIT_TICKS) {
      this.finishTeam('time_limit', null);
    }
  }

  /* ------------------------------------------------------------------------ */
  /*  The 1x1 boundary: seat-shaped, and refusing rather than lying           */
  /* ------------------------------------------------------------------------ */

  /**
   * The persisted duel snapshot.
   *
   * @throws PvpLegacyDuelError with `snapshot_not_supported_for_ruleset` for a
   * team match. The format holds two ships; a 3x3 has six, and writing two of
   * them would not be a smaller snapshot but a wrong one - a room resumed from
   * it would come back with four ships missing and no way to know it. The next
   * commit adds the format the six fit in; until it does, the refusal is the
   * honest answer and the reason a 3x3 is unreachable from the product.
   */
  snapshot(): PvpSnapshot {
    pvpRequireDuelRuleset(this.ruleset, 'snapshot_not_supported_for_ruleset');
    const ships = this.roster.all();
    const first = ships[0];
    const second = ships[1];
    if (first === undefined || second === undefined) {
      throw new RangeError('a pvp duel snapshot needs both participants');
    }
    /*
     * O que o formato v2 não sabe escrever é recusado em `pvpShipToDuelShip`,
     * abaixo: um perfil não-padrão - o que inclui qualquer contagem de corações
     * diferente de zero - e um resto de dano por pagar. Uma sala com corações
     * não é reduzida a v2 em silêncio.
     */
    return {
      tick: this.currentTick,
      ships: [pvpShipToDuelShip(first), pvpShipToDuelShip(second)],
      projectiles: this.shots.map((projectile) => ({
        id: projectile.id,
        seat: pvpParticipantIdToDuelSeat(projectile.ownerParticipantId),
        /*
         * De volta a pontos inteiros, e exato porque `snapshot()` recusa acima
         * qualquer sala com corações: sem corações um tiro é um múltiplo exato
         * de mil milli, portanto esta divisão não perde nada.
         */
        damage: Number(projectile.damageMilli / BigInt(DAMAGE_MILLI_PER_UNIT)),
        x: projectile.x,
        y: projectile.y,
        vx: projectile.vx,
        vy: projectile.vy,
        life: projectile.life,
      })),
      nextProjectileId: this.nextProjectileId,
      result: this.outcome === null ? null : pvpTeamResultToDuelResult(this.outcome),
      runes: this.runes.map((rune) => ({ ...rune })),
      rng: this.rng.snapshot(),
    };
  }

  /** How the duel ended, in the shape the wire and `pvp_matches` speak. */
  get result(): PvpResult | null {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    return this.outcome === null ? null : pvpTeamResultToDuelResult(this.outcome);
  }

  /** The live ship in a seat. The same object {@link shipOf} returns. */
  ship(seat: PvpSeat): PvpParticipantShipState {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    return this.roster.require(pvpDuelSeatToParticipantId(seat));
  }

  /** Every shot in flight, addressed by seat. */
  projectileList(): readonly PvpProjectileState[] {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    return this.shots.map((projectile) => ({
      id: projectile.id,
      seat: pvpParticipantIdToDuelSeat(projectile.ownerParticipantId),
      /*
       * Um acessório de forma de assento, para o caminho 1x1 histórico. Exato
       * pela mesma razão que `snapshot()`: sem corações um tiro é um múltiplo
       * exato de mil milli, e `pvpShipToDuelShip` recusa uma sala que os tenha.
       */
      damage: Number(projectile.damageMilli / BigInt(DAMAGE_MILLI_PER_UNIT)),
      x: projectile.x,
      y: projectile.y,
      vx: projectile.vx,
      vy: projectile.vy,
      life: projectile.life,
    }));
  }

  /**
   * The presentation events, addressed by seat.
   *
   * @throws PvpLegacyDuelError if any event belongs to a participant with no
   * seat. It cannot happen in a duel - `a1` and `b1` are the only participants
   * there are - and that is the point: a team match reaching this drain would
   * otherwise have its `a2` events silently addressed to seat `a`.
   */
  drainEvents(): readonly PvpEvent[] {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    /*
     * Um evento sem forma no formato de duelo histórico é DEIXADO CAIR, e não
     * traduzido à força. Hoje isso é só o proc de joia, que nenhum cliente de
     * duelo desenha - inventar-lhe uma forma seria mudar um formato que
     * descreve partidas já jogadas.
     */
    return this.drainParticipantEvents()
      .map(pvpParticipantEventToDuelEvent)
      .filter((event): event is PvpEvent => event !== null);
  }

  applyCommand(seat: PvpSeat, intent: PvpCommandIntent): boolean {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    return this.applyParticipantCommand(pvpDuelSeatToParticipantId(seat), intent);
  }

  setConnected(seat: PvpSeat, connected: boolean): void {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    this.setParticipantConnected(pvpDuelSeatToParticipantId(seat), connected);
  }

  finish(reason: PvpEndReason, winner: PvpSeat | null): PvpResult {
    pvpRequireDuelRuleset(this.ruleset, 'seat_not_supported_for_ruleset');
    return pvpTeamResultToDuelResult(
      this.finishTeam(reason, winner === null ? null : pvpDuelSeatToTeam(winner)),
    );
  }
}

/**
 * A persisted duel ship as a participant.
 *
 * `effects` and `teleportTo` are adopted by REFERENCE, which is what
 * the two-seat constructor did (`{ ...bySeat.a }` is a shallow copy). Keeping it
 * keeps the 1x1 identical, and changing it is a separate decision with its own
 * evidence rather than a side effect of this one.

 */
/**
 * A historic duel ship, with the only profile its format can describe.
 *
 * The v2 shape has no profile field. That is not a gap to paper over: every
 * record in it was written when a ship's numbers were module-level constants,
 * so the default IS what it said, exactly. `snapshot()` refuses to write
 * anything else into it, which is what keeps this decode honest rather than
 * optimistic.
 *
 * The cooldown crosses units here: v2 counts whole TICKS, the simulation counts
 * milli-ticks. With the default interval the conversion is exact in both
 * directions, and `snapshot()` refuses a cooldown that is not a whole tick.
 */
function restoreDuelShip(ship: PvpShipState): PvpParticipantShipState {
  const team = pvpDuelSeatToTeam(ship.seat);
  return {
    /*
     * Um documento do formato antigo não tem cartas, e não é um recuo: ele foi
     * escrito por um build em que elas não existiam no duelo (gate V28H.J).
     * Três espaços vazios é o estado exato dessa partida.
     */
    cards: [],
    cardCommands: [],
    pendingCard: null,
    pendingBoard: null,
    pendingCardRelease: null,
    cardEffects: initialCardEffects(),
    participantId: pvpDuelSeatToParticipantId(ship.seat),
    team,
    slot: 1,
    profile: pvpCopyCombatProfile(PVP_DEFAULT_COMBAT_PROFILE),
    x: ship.x,
    y: ship.y,
    aimX: ship.aimX,
    aimY: ship.aimY,
    /*
     * **Pontos inteiros x 1 000, exatamente** (gate V28G.S.1).
     *
     * O formato legado v2 é anterior ao milli e guarda pontos. A travessia é uma
     * multiplicação exata em `bigint`, e não um palpite: um casco que o
     * documento descreve com 359 de vida tinha 359 000 milli, ao milésimo,
     * porque nenhuma fração podia existir quando ele foi escrito.
     */
    healthMilli: BigInt(ship.health) * PVP_HEALTH_MILLI_PER_UNIT,
    cooldownMilliTicks: ship.cooldown * PVP_MILLI_TICKS_PER_TICK,
    moveX: ship.moveX,
    moveY: ship.moveY,
    firing: ship.firing,
    connected: ship.connected,
    appliedSeq: ship.appliedSeq,
    effects: ship.effects,
    teleportTo: ship.teleportTo,
    /*
     * Falso, e é exato e não um valor por omissão (gate V28G.N): o formato
     * legado v2 nasceu antes desta joia, portanto nenhum casco que ele descreve
     * pode ter uma ordem de detonar em mãos.
     */
    detonateBleed: false,
    /*
     * Zero, e é exato e não um valor por omissão (gate V28G.I): o formato legado
     * v2 nasceu antes das joias, portanto nenhum casco que ele descreve pode ter
     * ganho cura. Um documento antigo não recebe resto retroativo - recebe o
     * resto que ele realmente teve, que é nenhum.
     */
    lifestealCarryBps: 0,
    /*
     * `null` pela mesma razão, no gate V28G.J: nenhuma partida v2 tinha bombas,
     * portanto nenhum casco que este formato descreve podia estar a ser
     * empurrado. Inventar-lhe um empurrão agora seria tirar-lhe o controlo por
     * causa de uma explosão que nunca houve.
     */
    knockback: null,
    /*
     * Zero nos dois, pela mesma razão (gate V28G.P): o formato legado v2 nasceu
     * antes das joias, portanto nenhum casco que ele descreve tinha serras. Uma
     * fase e um resto inventados agora seriam uma órbita a começar a meio numa
     * partida que nunca teve lâmina nenhuma.
     */
    sawPhase: SAW_INITIAL_PHASE,
    sawCarryMilli: 0n,
  };
}

/**
 * A hull's numbers as the FORMAT stores them: four numbers and one decimal
 * string.
 *
 * The crossing is explicit because the two shapes disagree on exactly one
 * field. `PvpCombatProfile.primordialHeartCount` is a `bigint` - a running
 * simulation must not lose a digit of it - and `PvpCombatProfileV3`'s is a
 * decimal string, because the format is plain JSON and JSON has no `bigint`.
 * Spreading one into the other would have compiled the day the field was a
 * number and stopped compiling now, which is the point.
 */
function profileToV3(profile: PvpCombatProfile): PvpCombatProfileV3 {
  return {
    baseDamage: profile.baseDamage,
    maxHealthMilli: encodeHealthMilli(profile.maxHealthMilli),
    speedBps: profile.speedBps,
    fireIntervalMilliTicks: profile.fireIntervalMilliTicks,
    primordialHeartCount: profile.primordialHeartCount.toString(10),
    /*
     * Reconstruído entrada a entrada, e não partilhado.
     *
     * Um documento que apontasse para o array vivo do casco seria um snapshot
     * que muda quando a partida muda - precisamente o que a versão 3 foi
     * escrita para evitar, e o que `snapshot-v3.test.ts` verifica mutando cada
     * objeto restaurado.
     */
    jewels: { entries: profile.jewels.entries.map((entry) => ({ ...entry })) },
  };
}

/** And back, exactly. The decoder already proved the string is a decimal. */
function profileFromV3(profile: PvpCombatProfileV3): PvpCombatProfile {
  return {
    baseDamage: profile.baseDamage,
    maxHealthMilli: BigInt(profile.maxHealthMilli),
    speedBps: profile.speedBps,
    fireIntervalMilliTicks: profile.fireIntervalMilliTicks,
    primordialHeartCount: BigInt(profile.primordialHeartCount),
    jewels: { entries: profile.jewels.entries.map((entry) => ({ ...entry })) },
  };
}

/**
 * One ship as version 3 stores it: plain data, deep-copied, nothing shared.
 *
 * `moveX`/`moveY` ARE the velocity - a duel has no inertia, so where a hull goes
 * next is entirely which keys it is holding plus the scenery - and `health === 0`
 * is death. Neither is stored twice, because two sources of truth for "is this
 * ship alive" is how a wreck comes back shooting.
 */
function shipToV3(ship: PvpParticipantShipState): PvpParticipantV3 {
  return {
    /* Os três espaços e as ordens já aplicadas (versão 18, gate V28H.J). */
    cards: ship.cards.map((card) => ({ ...card })),
    cardCommands: [...ship.cardCommands],
    /* E as oito ranhuras de efeito (versão 19, gate V28H.L), pela ordem canónica. */
    cardEffects: CARD_EFFECT_SLOTS.map((slot) => ({
      ms: ship.cardEffects[slot].remainingMs,
      bps: ship.cardEffects[slot].magnitudeBps,
    })),
    participantId: ship.participantId,
    team: ship.team,
    slot: ship.slot,
    profile: profileToV3(ship.profile),

    lifestealCarryBps: ship.lifestealCarryBps,

    x: ship.x,
    y: ship.y,
    moveX: ship.moveX,
    moveY: ship.moveY,
    healthMilli: encodeHealthMilli(ship.healthMilli),
    aimX: ship.aimX,
    aimY: ship.aimY,
    cooldownMilliTicks: ship.cooldownMilliTicks,
    appliedSeq: ship.appliedSeq,
    connected: ship.connected,
    firing: ship.firing,
    effects: { ...ship.effects },
    teleportTo: ship.teleportTo === null ? null : { x: ship.teleportTo.x, y: ship.teleportTo.y },
    /* A ordem em mãos atravessa, como o teleporte pedido atravessa (V28G.N). */
    detonateBleed: ship.detonateBleed,
    /* Reconstruído e não adotado, como tudo o resto aqui. */
    knockback: ship.knockback === null ? null : { ...ship.knockback },
    /* A órbita e o resto das serras (gate V28G.P). */
    sawPhase: ship.sawPhase,
    sawCarryMilli: ship.sawCarryMilli.toString(10),
  };
}

/**
 * As oito ranhuras, do documento para um estado novo.
 *
 * Posicional: a entrada `i` é a ranhura `CARD_EFFECT_SLOTS[i]`, e o decodificador
 * já recusou qualquer documento que não traga exactamente oito. Um documento
 * antigo traz oito ranhuras vazias, que é o estado exacto da partida que o
 * escreveu.
 */
function cardEffectsFromV3(entries: readonly { ms: number; bps: number }[]): CardEffectState {
  const state = initialCardEffects();
  for (const [index, slot] of CARD_EFFECT_SLOTS.entries()) {
    const entry = entries[index];
    if (entry === undefined) {
      continue;
    }
    state[slot].remainingMs = entry.ms;
    state[slot].magnitudeBps = entry.bps;
  }
  return state;
}

/** A team letter from a decoded snapshot, re-branded through the parser. */
function teamFromV3(letter: string): PvpTeam {
  const team = pvpParseTeam(letter);
  if (team === null) {
    // Unreachable: the decoder already refused anything else. Asserted so a
    // future decoder change cannot introduce an undefined team here silently.
    throw new RangeError(`not a pvp team: ${letter}`);
  }
  return team;
}

/** And one ship back, with every object rebuilt rather than adopted. */
function shipFromV3(participant: PvpParticipantV3): PvpParticipantShipState {
  return {
    /*
     * Os três espaços VOLTAM do documento, e nada é reavaliado (gate V28H.J).
     *
     * O arrefecimento e a duração continuam de onde estavam: uma reconexão a
     * meio dos seis segundos devolve os canhões com o tempo que lhes restava, e
     * uma restauração durante o arrefecimento não o reinicia. As identidades das
     * ordens já aplicadas voltam com eles, senão uma repetição depois de uma
     * reconexão ligaria a carta outra vez.
     */
    cards: participant.cards.map((card) => ({
      slot: card.slot as CardSlot,
      definitionId: card.definitionId,
      instanceId: card.instanceId,
      level: card.level,
      cooldownRemainingMs: card.cooldownRemainingMs,
      activeRemainingMs: card.activeRemainingMs,
      attackAccumulatorMs: card.attackAccumulatorMs,
      /*
       * E a CARGA (gate V28H.L). Um documento da versão 19 não a tem, e isso é
       * zero: as duas cartas que carregam não existiam quando ele foi escrito.
       */
      chargeRemainingMs: card.chargeRemainingMs ?? 0,
      /*
       * E a FORÇA da carga (gate V28H.N). Um documento da versão 21 não a tem,
       * e isso é CHEIA: a carga não era graduada, portanto o feixe que ele
       * descreve ardia com tudo o que tinha.
       */
      chargeStrengthBps: card.chargeStrengthBps ?? CARD_CHARGE_FULL_BPS,
    })),
    cardCommands: [...participant.cardCommands],
    /* Uma ordem em mãos não atravessa: ela vale um tique, e o tique passou. */
    pendingCard: null,
    pendingBoard: null,
    pendingCardRelease: null,
    /*
     * E as oito ranhuras voltam onde estavam (gate V28H.L). Reconstruídas e
     * nunca adoptadas, como tudo o resto aqui: uma simulação a correr não
     * partilha uma referência com o documento de onde saiu.
     */
    cardEffects: cardEffectsFromV3(participant.cardEffects),
    participantId: participant.participantId,
    team: teamFromV3(participant.team),
    slot: participant.slot as PvpTeamSlot,
    /*
     * Rebuilt rather than adopted, like every other object here: a running
     * simulation must share no reference with the record it was restored from.
     */
    profile: profileFromV3(participant.profile),

    lifestealCarryBps: participant.lifestealCarryBps,

    x: participant.x,
    y: participant.y,
    aimX: participant.aimX,
    aimY: participant.aimY,
    healthMilli: BigInt(participant.healthMilli),
    cooldownMilliTicks: participant.cooldownMilliTicks,
    moveX: participant.moveX,
    moveY: participant.moveY,
    firing: participant.firing,
    connected: participant.connected,
    appliedSeq: participant.appliedSeq,
    effects: { ...participant.effects },
    teleportTo:
      participant.teleportTo === null
        ? null
        : { x: participant.teleportTo.x, y: participant.teleportTo.y },
    /*
     * E a ordem de detonar RETOMA (gate V28G.N).
     *
     * Uma sala reconstruída entre a chegada da ordem e o tique que a consome
     * executa-a na mesma. Perdê-la seria engolir uma ação de combate numa
     * reconexão, que é exatamente o que uma instantânea existe para impedir.
     */
    detonateBleed: participant.detonateBleed,
    /*
     * O empurrão RETOMA, e com a duração que lhe restava (gate V28G.J).
     *
     * Não é reiniciado. Uma sala reaberta a meio de um empurrão de sete tiques
     * ao quarto continua nos três que faltam, com a distância que ainda não foi
     * percorrida - que é o que o §6 pede quando fala de F5 e de reconexão.
     */
    knockback: participant.knockback === null ? null : { ...participant.knockback },
    /*
     * A órbita e o resto das serras, de volta ao vivo (gate V28G.P).
     *
     * A fase vem do documento porque é a única fonte que ainda diz onde as
     * lâminas estavam; o resto vem de lá porque é a única que diz o que ficou
     * por pagar. Nenhum dos dois é recalculado do tique nem do perfil.
     */
    sawPhase: participant.sawPhase,
    sawCarryMilli: BigInt(participant.sawCarryMilli),
  };
}

/** A persisted duel result as a team result. A seat winner is its own team. */
function restoreDuelResult(result: PvpResult): PvpTeamResult {
  const abandonment = result.abandonment ?? null;
  return {
    winner: result.winner === null ? null : pvpDuelSeatToTeam(result.winner),
    reason: result.reason,
    ticks: result.ticks,
    /*
     * Absent in every duel snapshot written before the cause existed, and absent
     * is `null`: that record genuinely cannot say which of the two abandonments
     * it was, and inventing one would put a false sentence on a result screen
     * for a match somebody actually played.
     */
    abandonment:
      abandonment === null
        ? null
        : {
            cause: abandonment.cause,
            participantId: pvpDuelSeatToParticipantId(abandonment.seat),
          },
  };
}
